import { MAGNET_AGENT_TOOLS, readMagnetAgentTools } from '../../../../packages/shared/src/metadata/magnetAgentTools.js';
import { mountShopifyFinder, renderFinderInspection } from './magnet-shopify-finder.js';
import { loadingState } from '../core/loadingState.js';
import { welcomeExperienceEditorHTML, collectWelcomeExperience, mountWelcomeExperience, welcomeExperienceProblem } from './magnet-welcome-experience.js';
import { PRODUCT_FINDER_QUOTE_COPY, MAGNET_VISUAL_STYLES, MAGNET_MOTION_STYLES, MAGNET_MESSAGE_STYLES } from '@sse/shared';
import { consentEditorHTML, collectConsent, consentProblem, mountConsentSubscriptions } from './magnet-consent.js';
import { legalNoticeEditorHTML, collectLegalNotice, legalNoticeProblem } from './magnet-legal-notice.js';
import { collapseMagnetNavigation, mountMagnetStudio } from './magnet-studio.js';
import { mountStudioDesign } from './magnet-studio-design.js';
import { mountCanvasEditor } from './magnet-canvas-editor.js';
import { saveStylePreference } from '../core/magnetStylePreference.js';
import { styleGalleryHTML, mountStyleGallery } from './magnet-style-gallery.js';
/**
 * magnets-route.js — client-saas /magnets
 *
 * Self-serve magnet lifecycle. Lists the caller's magnets (an app + its conversation), and —
 * for owner/admin/member of a reseller or self-serve org (PROVISION_MAGNETS) — create / edit
 * basics / enable-disable / delete, and copy the embed snippet. Managed clients + viewers see
 * a read-only list (the server enforces the same gates).
 *   GET    /m5t/v5/magnets        POST /m5t/v5/magnets
 *   PATCH  /m5t/v5/magnets/:guid  DELETE /m5t/v5/magnets/:guid
 */

import AuthController from '../services/AuthController.js';
import PubSub from 'pubsub-js';
import AppBroadcast from '../../../../shared/appBroadcast.js';
import { editorWorkspaceEnabled, editorPath, rememberEditorDraft } from '../core/magnetEditorSession.js';
import { appearanceChanges } from './magnet-draft.js';
import { replaceChatSteps, mountFlowTab, validateFlows, showFlowRoute, currentFlowDefinition, changedFlowRoutes, syncFlowPaths, currentFlowRoute, currentFlowSelection, currentFlowNavigation, selectFlowQuestion, refreshFlowDeliveryContext } from './magnet-flow.js';
import { mountLaunchWorkspace } from './magnet-launch-workspace.js';
import { connectEditorLabels } from './magnet-editor-accessibility.js';
import { mountConversationWorkspace } from './magnet-conversation-workspace.js';
import { openPreviewModal, mountPreviewPane, magnetToPackage } from './magnet-preview.js';
import { INLINE_SIZE_PRESETS, PRESET_THUMBS, matchInlineSizePreset, inlineSizeValues, inlineSizeError } from './inline-size-presets.js';
import { inPortfolio, pickerPageHTML, initPicker } from '../services/portfolio-view.js';
import { openTemplateGallery, takePendingTemplate, peekPendingTemplate, setPendingTemplate } from './magnet-templates.js';
import { capList } from '../core/listExpand.js';
import { guardedFetch } from '../core/navGeneration.js';
import { approveMagnetDomain } from '../core/magnetOverview.js';
import { chooseHiddenDomain } from '../core/magnetDomainDialog.js';
import { isDenial, denialState, failIfNotOk } from '../core/denialSurface.js';
import { MAGNET_APPEARANCE_PRESETS, MAGNET_THEME_COLOR_FIELDS, PRESENTATION_STYLES, LAUNCHER_STYLES, magnetAppearancePreset, matchMagnetAppearancePreset, templateChanges } from '@sse/shared';

let stylesInjected = false;
// detailOpen: per-guid Install-panel expansion. undefined = untouched (the never-seen
// auto-open may claim it), true/false = the person's own choice, which always wins.
const state = { magnets: [], owners: [], canProvision: false, canManage: false, primaryOrgGuid: null, stats: {}, install: {}, connections: {}, pairing: {}, pairTimer: null, detailOpen: {} };

const ESC = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ESC[c]);
const toast = (message, type = 'info') => { try { PubSub.publish(AppBroadcast.UI_TOAST, { message, type }); } catch { /* no-op */ } };
const apiBase = () => AuthController.getConfig?.().BASE_URL || '';
export const embedFor = (guid) => `<script src="${apiBase()}/m5t/v5/coreSnippet?appguid=${guid}" async></` + `script>`;
// A strict Content-Security-Policy (Next.js's recommended nonce + strict-dynamic
// setup, for example) silently blocks this widget with no console error a typical
// user would notice — found installing our own magnet on a customer's CSP'd site
// (thewoodworkingbench.com — connect-src blocked capture with no visible symptom
// beyond "something went wrong" in the widget itself; the actual violation only
// showed up in DevTools). Three separate directives can each independently break
// it: script-src (the tag itself never runs), connect-src (capture/event calls get
// blocked even though the script loaded), img-src (an uploaded logo never renders).
// Surfaced here, not just in an external docs page, since this is the one place
// every customer actually looks when installing.
//
// COLLAPSED, reversing an earlier deliberate open-by-default. That decision was made
// because the incident happened with this note shipped-but-collapsed, so leaving it
// collapsed restores the conditions under which someone missed it. What changed is that
// the note is no longer the only thing pointing at CSP: installGuideHTML() below now
// carries a step-by-step per site type, and its Next.js/React section — the platform a
// strict nonce+strict-dynamic policy actually comes from, and where the real incident
// originated — points here explicitly. That targets the warning at the people who need
// it instead of taxing every customer's install with a panel most of them will never
// need. The general expansion was costing every non-CSP install real scroll on the one
// screen a first-time customer is already least sure about.
export const cspNoteHTML = () => `<details class="mag-csp-note">
  <summary><span class="material-symbols-outlined" aria-hidden="true">shield</span>Strict Content-Security-Policy on your site? Read this</summary>
  <div class="mag-csp-note__body">
    <p>A strict CSP can silently block the widget — it just never appears, no visible error. Allowlist <code>${esc(apiBase())}</code> under all three directives. Copy-paste ready for your CSP header/meta tag:</p>
    <pre class="mag-csp-note__snippet"><code>script-src 'self' ${esc(apiBase())};
connect-src 'self' ${esc(apiBase())};
img-src 'self' ${esc(apiBase())};</code></pre>
    <ul>
      <li><code>script-src</code> — lets the install script run at all</li>
      <li><code>connect-src</code> — lets it send captures and events</li>
      <li><code>img-src</code> — lets an uploaded logo render</li>
    </ul>
    <p>Merge these into your existing policy rather than replacing it — most sites already have other origins allowlisted (analytics, fonts, etc.) that this snippet doesn't know about.</p>
    <p>Using a nonce-based CSP? Add your page's own nonce to the script tag: <code>&lt;script nonce="your-nonce" src="..."&gt;</code></p>
  </div>
</details>`;

/**
 * "Where do I actually put this?" — the step the card never answered.
 *
 * The install area handed over a <script> tag and stopped. That is a complete answer for
 * someone who edits HTML for a living and no answer at all for the customer this product
 * is sold to, who is looking at a box of code with no idea which of their site's screens
 * it belongs on. The snippet is identical for every platform; what differs is entirely
 * WHERE it goes, which is exactly the part we were leaving out.
 *
 * Collapsed by default, like the CSP note — the card should open small. But this one is
 * the first-timer's path, so it sits directly under the snippet and says what it is.
 *
 * WordPress gets a manual route on purpose: the plugin (which pairs a site without any
 * copy-paste) is paused, so pointing at it would be pointing at something nobody can
 * install today. When it ships, this section should lead with it and keep the manual
 * paste as the fallback.
 *
 * Squarespace and Wix are grouped because the customer-facing problem is the same one —
 * neither lets you edit page HTML, both hide a "custom code" injection panel somewhere
 * specific, and naming that exact path is the whole value of the section. Squarespace's
 * plan gate is called out because hitting a paywall mid-install with no explanation is
 * the worst version of this.
 *
 * Next.js/React is a note rather than steps: someone on that stack does not need to be
 * told where their layout file is, they need to be told not to hand-write a raw tag into
 * JSX, and that a strict CSP is theirs to fix. It is also where the real CSP incident
 * came from, so it is the section that points at cspNoteHTML().
 */
export const installGuideHTML = (guid) => {
  const snippet = esc(embedFor(guid));
  return `<details class="mag-guide">
  <summary><span class="material-symbols-outlined" aria-hidden="true">help</span>Where do I paste this? Step-by-step for your site</summary>
  <div class="mag-guide__body">

    <div class="mag-guide__plat">
      <h4><span class="material-symbols-outlined" aria-hidden="true">code</span>A plain HTML site</h4>
      <ol>
        <li>Open the page you want the magnet on — or your shared footer/template file, if you have one, so it appears on every page.</li>
        <li>Paste the snippet just before the closing <code>&lt;/body&gt;</code> tag.</li>
        <li>Save, upload the file to your host, and reload your site.</li>
      </ol>
    </div>

    <div class="mag-guide__plat">
      <h4><span class="material-symbols-outlined" aria-hidden="true">wordpress</span>WordPress</h4>
      <ol>
        <li>In WP Admin, install a header/footer snippet plugin — <em>WPCode</em> and <em>Insert Headers and Footers</em> are the common ones.</li>
        <li>Open its settings and find the <strong>Footer</strong> (or "Body — end") box.</li>
        <li>Paste the snippet there, save, and reload your site.</li>
      </ol>
      <p class="mag-guide__note">Prefer a plugin over Appearance &rarr; Theme File Editor: a snippet pasted into a theme file is wiped by the next theme update, and a snippet plugin survives it.</p>
    </div>

    <div class="mag-guide__plat">
      <h4><span class="material-symbols-outlined" aria-hidden="true">web</span>Squarespace or Wix</h4>
      <ol>
        <li><strong>Squarespace:</strong> Settings &rarr; Advanced &rarr; <strong>Code Injection</strong>, paste into the <strong>Footer</strong> box, then Save. Code Injection needs a Business plan or higher.</li>
        <li><strong>Wix:</strong> Settings &rarr; <strong>Custom Code</strong> (under Advanced) &rarr; <em>+ Add Custom Code</em>. Paste, choose <strong>Body — end</strong>, apply to <strong>All pages</strong>, then Apply.</li>
        <li>Publish the site — on both platforms, custom code only runs on the published site, not in the editor preview.</li>
      </ol>
    </div>

    <div class="mag-guide__plat">
      <h4><span class="material-symbols-outlined" aria-hidden="true">deployed_code</span>Next.js, React or another framework</h4>
      <p class="mag-guide__note">Load it through your framework's script component rather than hand-writing the tag into JSX — React will not execute a raw <code>&lt;script&gt;</code> in markup. In Next.js, add this to your root layout:</p>
      <pre class="mag-guide__snippet"><code>import Script from 'next/script';

&lt;Script
  src="${esc(apiBase())}/m5t/v5/coreSnippet?appguid=${esc(guid)}"
  strategy="afterInteractive"
/&gt;</code></pre>
      <p class="mag-guide__note">If your app sets a Content-Security-Policy (a Next.js middleware with a nonce is the usual source), it will block the widget silently — see the CSP note below before you go hunting.</p>
    </div>

    <p class="mag-guide__note">Wherever it goes, the snippet is the same one shown above:</p>
    <pre class="mag-guide__snippet"><code>${snippet}</code></pre>
  </div>
</details>`;
};

// ── configurator vocab (mirrors the SDK + API contract) ──────────────────────
// KEYS here must stay in sync with @sse/shared's magnetAppearance.js
// (DISPLAY_MODE_VALUES/THEME_MODE_VALUES/TRIGGER_VALUES/ACTION_TYPE_VALUES) —
// enforced by magnet-vocab-consistency.test.js, not by importing it directly.
// Left as independent literals rather than derived from the shared module on
// purpose: MODE_LABEL's "Chat bubble"/"Slide-in panel" (used on the magnet
// grid card) and DISPLAY_OPTS's "Chat"/"Slide" (used in this picker) are two
// intentionally DIFFERENT label sets that happen to share keys — deriving one
// from the other's canonical label would have silently changed real UI copy.
// export'd so the consistency test can check them without duplicating them.
export const MODE_LABEL = { chat: 'Chat bubble', right_slide: 'Slide-in panel', modal: 'Modal', inline: 'Inline' };
const MODE_ICON = { chat: 'chat', right_slide: 'vertical_split', modal: 'web_asset', inline: 'view_agenda' };
export const DISPLAY_OPTS = [
  { value: 'inline', icon: 'view_agenda', label: 'Inline' },
  { value: 'chat', icon: 'chat', label: 'Chat' },
  { value: 'right_slide', icon: 'vertical_split', label: 'Slide' },
  { value: 'modal', icon: 'web_asset', label: 'Modal' },
];
/**
 * mag_trigger can be a per-device object ({ desktop, mobile }) as well as a string.
 *
 * This select only speaks strings, so an object matched no option, fell through to the
 * first one, and SAVING SILENTLY OVERWROTE the per-device setting with 'on_load'. It is
 * surfaced as its own selected option instead, and the save omits `trigger` entirely
 * while that option is chosen, so opening the dialog and pressing Save cannot destroy a
 * configuration this UI cannot yet author. Picking any other option is a deliberate
 * override and does write a plain string.
 */
/**
 * The SDK's mobile breakpoint, shown to the customer so "mobile" is not a mystery.
 * Kept in step with MOBILE_MAX_WIDTH in shared/machfivemagnet.js by the SDK test that
 * pins the CSS to that constant; if it ever moves, this label is the other place to
 * change, and saying the number out loud is what makes that visible.
 */
export const MOBILE_BREAKPOINT_PX = 480;

/** Which option the Auto-open select should start on. */
export function triggerSelection(magnet) {
  if (!magnet) return 'on_load';                       // new magnets stay simple
  if (perDeviceTrigger(magnet)) return '__per_device';
  return magnet.trigger || 'none';
}

/** Prefill for one side of the per-device pair. */
export function perDeviceSide(magnet, side) {
  const t = perDeviceTrigger(magnet);
  if (t) return t[side] || 'none';
  // Coming from a single value, seed BOTH sides with it so switching to Per device is a
  // starting point rather than a blank form that silently means 'none'.
  const single = magnet && typeof magnet.trigger === 'string' ? magnet.trigger : 'on_load';
  return side === 'mobile' ? 'none' : single;
}

/**
 * Read the Auto-open control into what the API expects: a single string, or the
 * device-aware { desktop, mobile } object.
 *
 * Returns undefined if the sub-selects are somehow missing while Per device is chosen —
 * the caller then omits `trigger` entirely, preserving what is stored. Sending a half
 * object would be worse than sending nothing.
 */
/**
 * Show the per-device pair only when it is both CHOSEN and MEANINGFUL.
 *
 * Inline is the "meaningful" half: an inline card is always visible, so no trigger of
 * any kind applies to it. Offering a per-device split there would be a setting that
 * silently does nothing, so the option is removed and the pair hidden while Inline is
 * selected — without touching what is stored, so switching display mode back restores
 * the previous choice rather than quietly rewriting it.
 */
export function syncPerDeviceVisibility(scrim) {
  const sel = scrim.querySelector('#mag-f-trigger');
  const pair = scrim.querySelector('#mag-f-perdev');
  if (!sel || !pair) return;
  const isInline = scrim.querySelector('input[name="mag-display"]:checked')?.value === 'inline';
  const triggerGroup = scrim.querySelector('[data-auto-open-settings]');
  if (triggerGroup) triggerGroup.hidden = isInline;
  const opt = sel.querySelector('option[value="__per_device"]');
  if (opt) opt.hidden = isInline;
  // If Inline is picked while Per device was selected, fall the SELECT back to a single
  // value for display purposes only. The stored value is untouched unless they save.
  if (isInline && sel.value === '__per_device') sel.value = 'none';
  pair.hidden = isInline || sel.value !== '__per_device';
}

export function collectTrigger(scrim) {
  const sel = scrim.querySelector('#mag-f-trigger');
  const value = sel?.value || 'on_load';
  if (value !== '__per_device') return value;
  const desktop = scrim.querySelector('#mag-f-trigger-desktop')?.value;
  const mobile = scrim.querySelector('#mag-f-trigger-mobile')?.value;
  if (!desktop || !mobile) return undefined;
  return { desktop, mobile };
}

export function perDeviceTrigger(magnet) {
  const t = magnet && magnet.trigger;
  return (t && typeof t === 'object' && !Array.isArray(t)) ? t : null;
}
export function perDeviceTriggerLabel(magnet) {
  const t = perDeviceTrigger(magnet);
  if (!t) return '';
  const label = (v) => (TRIGGER_OPTS.find((o) => o.value === (v || 'none')) || {}).label || (v || 'none');
  return `Per device — desktop: ${label(t.desktop)}; mobile: ${label(t.mobile)}`;
}
export const THEME_OPTS = [
  // Auto = the VISITOR'S OS setting. Sync = the HOST SITE'S own theme toggle, which the
  // OS query cannot see; the SDK reads <html> for the common conventions and falls back
  // to Auto's behaviour on a site using none of them, so Sync is never worse than Auto.
  { value: 'auto', icon: 'brightness_auto', label: 'Auto' },
  { value: 'sync', icon: 'sync', label: 'Sync' },
  { value: 'light', icon: 'light_mode', label: 'Light' },
  { value: 'dark', icon: 'dark_mode', label: 'Dark' },
];
// Auto-open trigger tokens the SDK honors ('none' = manual open only).
export const TRIGGER_OPTS = [
  { value: 'on_load', label: 'Immediately on page load' },
  { value: 'on_8_seconds', label: 'After 8 seconds' },
  { value: 'on_scroll_80_percent', label: 'At 80% scroll depth' },
  { value: 'on_exit_intent', label: 'On exit intent (mouse leaves the page)' },
  { value: 'none', label: 'Manual only (visitor clicks the launcher)' },
];
// Hub CTA bar. `kind` drives the row UI + how the action is collected:
//   value  → a contact input (call/email)   book → scheduler, optional label/booking-link
//   flow   → routes to the guided flow (chat)  support → multi-select intake (+ options list)
export const CTA_META = {
  book: { label: 'Book', icon: 'event', kind: 'book' },
  chat: { label: 'Chat', icon: 'chat', kind: 'flow' },
  call: { label: 'Call', icon: 'phone', kind: 'value', placeholder: 'Phone number' },
  email: { label: 'Email', icon: 'mail', kind: 'value', placeholder: 'Email address' },
  support: { label: 'Support', icon: 'support_agent', kind: 'support' },
};
// Deliberately a DIFFERENT order from @sse/shared's ACTION_TYPES (a curated
// presentation order, not the backend's validation-array order) — its value
// SET is cross-checked, its order is not.
export const CTA_ORDER = ['book', 'chat', 'call', 'email', 'support'];

// Calendly event types for the Book CTA picker. Fetched once per page visit
// (init() resets it so a fresh connect in Settings is picked up on return).
const pickerOwner = sel => sel.closest('[data-calendar-owner-guid]')?.dataset.calendarOwnerGuid || AuthController.getStoredActiveOrg();
async function calendarPickerRead(path, owner) {
  const url = new URL(`${apiBase()}/m5t/v5/integrations/${path}`);
  if (owner) url.searchParams.set('owner_guid', owner);
  const res = await guardedFetch(url.href);
  const data = await res.json().catch(() => ({}));
  // Old servers remain usable for the active workspace; never accept an
  // unscoped catalog for a different managed client.
  if (data.ok && ((data.owner_guid && data.owner_guid !== owner) || (!data.owner_guid && owner !== AuthController.getStoredActiveOrg()))) throw new Error('Calendar workspace could not be verified');
  return {res, data};
}
let calEventTypes = null;
async function fetchCalendlyEventTypes(owner) {
  if (calEventTypes?.owner === owner) return calEventTypes;
  let result;
  try {
    const {res, data} = await calendarPickerRead('calendly/event_types', owner);
    if (res.status === 404) result = { ok: false, reason: 'not_connected' };
    else if (!data.ok) result = { ok: false, reason: 'error' };
    else result = { ok: true, list: data.data.event_types || [], scheduling_url: data.data.scheduling_url || '' };
  } catch {
    result = { ok: false, reason: 'error' };
  }
  // Cache successes only - a transient failure must not disable the picker for
  // the rest of the page visit.
  if (result.ok) calEventTypes = {...result, owner};
  return result;
}

// Cal.com event types for the Book CTA picker - same caching contract as
// fetchCalendlyEventTypes above (successes only; init() resets per visit).
let ccEventTypes = null;
async function fetchCalcomEventTypes(owner) {
  if (ccEventTypes?.owner === owner) return ccEventTypes;
  let result;
  try {
    const {res, data} = await calendarPickerRead('calcom/event_types', owner);
    if (res.status === 404) result = { ok: false, reason: 'not_connected' };
    else if (!data.ok) result = { ok: false, reason: 'error' };
    else result = { ok: true, list: data.data.event_types || [] };
  } catch {
    result = { ok: false, reason: 'error' };
  }
  if (result.ok) ccEventTypes = {...result, owner};
  return result;
}

async function populateCcEventSelect(sel) {
  sel.dataset.loading = '1';
  const savedUrl = sel.dataset.saved || '';
  const savedName = sel.dataset.savedName || 'Saved Cal.com event';
  sel.innerHTML = `<option value="${esc(savedUrl)}">${savedUrl ? esc(savedName) : 'Loading event types'}</option>`;
  const et = await fetchCalcomEventTypes(pickerOwner(sel));
  delete sel.dataset.loading;
  if (!et.ok) {
    delete sel.dataset.listed;
    sel.innerHTML = savedUrl
      ? `<option value="${esc(savedUrl)}" selected>${esc(savedName)}</option>`
      : `<option value="">${et.reason === 'not_connected' ? 'Connect Cal.com under Settings, Integrations first' : 'Could not load event types'}</option>`;
    return;
  }
  const opts = et.list.map((e) => ({ url: e.url, label: e.length ? `${e.title} (${e.length} min)` : e.title }));
  if (savedUrl && !opts.some((o) => o.url === savedUrl)) opts.unshift({ url: savedUrl, label: savedName });
  sel.innerHTML = opts.length
    ? opts.map((o) => `<option value="${esc(o.url)}"${o.url === (savedUrl || opts[0].url) ? ' selected' : ''}>${esc(o.label)}</option>`).join('')
    : `<option value="">No event types on this Cal.com account</option>`;
}

// Microsoft Bookings pages for the Book CTA picker - same caching contract.
let mbPages = null;
async function fetchMbPages(owner) {
  if (mbPages?.owner === owner) return mbPages;
  let result;
  try {
    const {res, data} = await calendarPickerRead('ms_bookings/businesses', owner);
    if (res.status === 404) result = { ok: false, reason: 'not_connected' };
    else if (!data.ok) result = { ok: false, reason: 'error' };
    else result = { ok: true, list: (data.data.businesses || []).filter((b) => b.public_url) };
  } catch {
    result = { ok: false, reason: 'error' };
  }
  if (result.ok) mbPages = {...result, owner};
  return result;
}

async function populateMbPageSelect(sel) {
  sel.dataset.loading = '1';
  const savedUrl = sel.dataset.saved || '';
  const savedName = sel.dataset.savedName || 'Saved Bookings page';
  sel.innerHTML = `<option value="${esc(savedUrl)}">${savedUrl ? esc(savedName) : 'Loading booking pages'}</option>`;
  const et = await fetchMbPages(pickerOwner(sel));
  delete sel.dataset.loading;
  if (!et.ok) {
    delete sel.dataset.listed;
    sel.innerHTML = savedUrl
      ? `<option value="${esc(savedUrl)}" selected>${esc(savedName)}</option>`
      : `<option value="">${et.reason === 'not_connected' ? 'Connect Microsoft Bookings under Settings, Integrations first' : 'Could not load booking pages'}</option>`;
    return;
  }
  const savedBiz = sel.dataset.savedBiz || '';
  const opts = et.list.map((b) => ({ url: b.public_url, label: b.name, id: b.id }));
  if (savedUrl && !opts.some((o) => o.url === savedUrl)) opts.unshift({ url: savedUrl, label: savedName, id: savedBiz });
  sel.innerHTML = opts.length
    ? opts.map((o) => `<option value="${esc(o.url)}" data-id="${esc(o.id || '')}"${o.url === (savedUrl || opts[0].url) ? ' selected' : ''}>${esc(o.label)}</option>`).join('')
    : `<option value="">No Bookings pages on this Microsoft account</option>`;
  populateMbServiceSelect(sel);
  sel.addEventListener('change', () => populateMbServiceSelect(sel));
}

/**
 * Services for the selected Bookings page — picking one runs OUR scheduler
 * against the org's real calendar (native booking); "Whole page" keeps the
 * in-magnet page embed instead.
 */
async function populateMbServiceSelect(pageSel) {
  const svcSel = pageSel.closest('.mag-cta')?.querySelector('.mag-cta__mbservice');
  if (!svcSel) return;
  const bizId = pageSel.selectedOptions?.[0]?.dataset?.id || '';
  const savedSvc = bizId === pageSel.dataset.savedBiz ? svcSel.dataset.saved || '' : '';
  const savedSvcName = svcSel.dataset.savedName || 'Saved service';
  const ticket = String(Number(svcSel.dataset.request || 0) + 1); svcSel.dataset.request = ticket;
  const current = () => pageSel.isConnected && svcSel.dataset.request === ticket;
  const wholePage = `<option value="">Whole page (opens in the magnet)</option>`;
  if (!bizId) { delete svcSel.dataset.loading; svcSel.innerHTML = wholePage; return; }
  svcSel.dataset.loading = '1';
  svcSel.innerHTML = `<option value="">Loading services…</option>`;
  try {
    const {data} = await calendarPickerRead(`ms_bookings/services?business_id=${encodeURIComponent(bizId)}`, pickerOwner(pageSel));
    if (!data.ok) throw new Error();
    if (!current() || pageSel.selectedOptions?.[0]?.dataset?.id !== bizId) return;
    const list = [...(data.data.services || [])];
    if (savedSvc && !list.some(service => service.id === savedSvc)) list.unshift({id:savedSvc, name:savedSvcName});
    svcSel.innerHTML = wholePage + list.map((sv) =>
      `<option value="${esc(sv.id)}"${sv.id === savedSvc ? ' selected' : ''}>${esc(sv.name)} ${sv.duration_mins ? `(${sv.duration_mins} min, books in the magnet)` : '(saved service; check availability)'}</option>`).join('');
  } catch {
    if (!current()) return;
    svcSel.innerHTML = savedSvc
      ? wholePage + `<option value="${esc(savedSvc)}" selected>${esc(savedSvcName)}</option>`
      : wholePage;
  } finally { if (current()) delete svcSel.dataset.loading; }
}

async function populateCalEventSelect(sel) {
  sel.dataset.loading = '1';
  const savedUrl = sel.dataset.saved || '';
  const savedName = sel.dataset.savedName || 'Saved Calendly event';
  sel.innerHTML = `<option value="${esc(savedUrl)}">${savedUrl ? esc(savedName) : 'Loading event types'}</option>`;
  const et = await fetchCalendlyEventTypes(pickerOwner(sel));
  delete sel.dataset.loading;
  if (!et.ok) {
    // Keep a saved selection usable even when the listing fails; otherwise say why.
    // Clearing the listed flag lets a source re-toggle retry within this modal.
    delete sel.dataset.listed;
    sel.innerHTML = savedUrl
      ? `<option value="${esc(savedUrl)}" selected>${esc(savedName)}</option>`
      : `<option value="">${et.reason === 'not_connected' ? 'Connect Calendly under Settings, Integrations first' : 'Could not load event types'}</option>`;
    return;
  }
  const opts = [];
  if (et.scheduling_url) opts.push({ url: et.scheduling_url, label: 'Full scheduling page (visitor picks the event)' });
  et.list.forEach((e) => opts.push({ url: e.scheduling_url, label: e.duration ? `${e.name} (${e.duration} min)` : e.name }));
  if (savedUrl && !opts.some((o) => o.url === savedUrl)) opts.unshift({ url: savedUrl, label: savedName });
  sel.innerHTML = opts.length
    ? opts.map((o) => `<option value="${esc(o.url)}"${o.url === (savedUrl || opts[0].url) ? ' selected' : ''}>${esc(o.label)}</option>`).join('')
    : `<option value="">No active event types</option>`;
}

// CallRail tracking numbers for the Call CTA picker. Fetched once per page
// visit (init() resets it so a fresh connect in Settings is picked up).
let crTrackers = null;
async function fetchCallrailTrackers() {
  if (crTrackers) return crTrackers;
  let result;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/integrations/callrail/trackers`);
    const data = await res.json().catch(() => ({}));
    if (res.status === 404) result = { ok: false, reason: 'not_connected' };
    else if (!data.ok) result = { ok: false, reason: 'error' };
    else result = { ok: true, list: data.data.trackers || [] };
  } catch {
    result = { ok: false, reason: 'error' };
  }
  // Cache successes only - a transient failure must not disable the picker for
  // the rest of the page visit.
  if (result.ok) crTrackers = result;
  return result;
}

async function populateCrTrackerSelect(sel) {
  sel.dataset.loading = '1';
  const savedNumber = sel.dataset.saved || '';
  const savedName = sel.dataset.savedName || 'Saved tracking number';
  sel.innerHTML = `<option value="${esc(savedNumber)}">${savedNumber ? esc(savedName) : 'Loading tracking numbers'}</option>`;
  const et = await fetchCallrailTrackers();
  delete sel.dataset.loading;
  if (!et.ok) {
    // Keep a saved selection usable even when the listing fails; otherwise say why.
    // Clearing the listed flag lets a source re-toggle retry within this modal.
    delete sel.dataset.listed;
    sel.innerHTML = savedNumber
      ? `<option value="${esc(savedNumber)}" selected>${esc(savedName)}</option>`
      : `<option value="">${et.reason === 'not_connected' ? 'Connect CallRail under Settings, Integrations first' : 'Could not load tracking numbers'}</option>`;
    return;
  }
  const opts = et.list.map((t) => ({
    number: t.number,
    id: t.id,
    name: t.name,
    label: `${t.name} · ${t.number}${t.type === 'session' ? ' (number pool)' : ''}`,
  }));
  if (savedNumber && !opts.some((o) => o.number === savedNumber)) {
    opts.unshift({ number: savedNumber, id: sel.dataset.savedId || '', name: savedName, label: savedName });
  }
  sel.innerHTML = opts.length
    ? opts.map((o) => `<option value="${esc(o.number)}" data-tracker-id="${esc(o.id)}" data-tracker-name="${esc(o.name)}"${o.number === (savedNumber || opts[0].number) ? ' selected' : ''}>${esc(o.label)}</option>`).join('')
    : `<option value="">No active tracking numbers in this company</option>`;
}

// A radio-chip segmented control (accessible; value read via :checked).
function segHTML(name, current, opts) {
  return `<div class="mag-seg" role="radiogroup" aria-label="${esc(name)}">${opts.map((o) => `
    <label><input type="radio" name="${esc(name)}" value="${esc(o.value)}"${o.value === current ? ' checked' : ''}>
      <span><span class="material-symbols-outlined" aria-hidden="true">${esc(o.icon)}</span>${esc(o.label)}</span></label>`).join('')}</div>`;
}

// One opt-in CTA row (kind-aware): toggle + the right control. Prefilled from the saved action.
function ctaRowHTML(type, action) {
  const meta = CTA_META[type];
  const on = !!action;
  const dis = on ? '' : ' disabled';
  let control = '';
  if (meta.kind === 'value' && type === 'call') {
    // Call gets a number-source picker: a plain phone number, or a CallRail
    // tracking number so calls attribute to their source (and swap per visitor
    // on DNI pages). Email stays a plain input below.
    const isCr = action?.config?.source === 'callrail';
    const savedNumber = isCr ? (action?.value || '') : '';
    const savedName = isCr ? (action?.config?.tracker_name || 'Saved tracking number') : '';
    control = `
      <select class="mag-modal__select mag-cta__callsrc"${dis}>
        <option value=""${isCr ? '' : ' selected'}>Phone number</option>
        <option value="callrail"${isCr ? ' selected' : ''}>CallRail</option>
      </select>
      <input class="mag-modal__input mag-cta__val" type="text" placeholder="${esc(meta.placeholder)}" value="${esc(isCr ? '' : (action?.value || ''))}"${dis}${isCr ? ' hidden' : ''}>
      <select class="mag-modal__select mag-cta__crtracker" data-saved="${esc(savedNumber)}" data-saved-name="${esc(savedName)}" data-saved-id="${esc(isCr ? (action?.config?.tracker_id || '') : '')}"${dis}${isCr ? '' : ' hidden'}>
        ${savedNumber ? `<option value="${esc(savedNumber)}" selected>${esc(savedName)}</option>` : ''}
      </select>`;
  } else if (meta.kind === 'value') {
    control = `<input class="mag-modal__input mag-cta__val" type="text" placeholder="${esc(meta.placeholder)}" value="${esc(action?.value || '')}"${dis}>`;
  } else if (meta.kind === 'book') {
    const isCal = action?.config?.source === 'calendly';
    const isCc = action?.config?.source === 'calcom';
    const isMb = action?.config?.source === 'ms_bookings';
    const bookVal = action?.config?.meeting_label || (isCc || isMb ? '' : action?.value) || '';
    const savedUrl = isCal ? (action?.config?.calendly_url || '') : '';
    const savedName = isCal ? (action?.config?.event_name || 'Saved Calendly event') : '';
    const ccSavedUrl = isCc ? (action?.value || '') : '';
    const ccSavedName = isCc ? (action?.config?.event_name || 'Saved Cal.com event') : '';
    const mbSavedUrl = isMb ? (action?.value || '') : '';
    const mbSavedName = isMb ? (action?.config?.event_name || 'Saved Bookings page') : '';
    control = `
      <select class="mag-modal__select mag-cta__booksrc"${dis}>
        <option value=""${isCal || isCc || isMb ? '' : ' selected'}>Built-in scheduler</option>
        <option value="calendly"${isCal ? ' selected' : ''}>Calendly</option>
        <option value="calcom"${isCc ? ' selected' : ''}>Cal.com</option>
        <option value="ms_bookings"${isMb ? ' selected' : ''}>Microsoft Bookings</option>
      </select>
      <input class="mag-modal__input mag-cta__val" type="text" placeholder="Meeting label (e.g. 15 minute call), or paste a booking link" value="${esc(bookVal)}"${dis}${isCal || isCc || isMb ? ' hidden' : ''}>
      <select class="mag-modal__select mag-cta__calevent" data-saved="${esc(savedUrl)}" data-saved-name="${esc(savedName)}"${dis}${isCal ? '' : ' hidden'}>
        ${savedUrl ? `<option value="${esc(savedUrl)}" selected>${esc(savedName)}</option>` : ''}
      </select>
      <select class="mag-modal__select mag-cta__ccevent" data-saved="${esc(ccSavedUrl)}" data-saved-name="${esc(ccSavedName)}"${dis}${isCc ? '' : ' hidden'}>
        ${ccSavedUrl ? `<option value="${esc(ccSavedUrl)}" selected>${esc(ccSavedName)}</option>` : ''}
      </select>
      <select class="mag-modal__select mag-cta__mbevent" data-saved="${esc(mbSavedUrl)}" data-saved-name="${esc(mbSavedName)}" data-saved-biz="${esc(isMb ? (action?.config?.business_id || '') : '')}"${dis}${isMb ? '' : ' hidden'}>
        ${mbSavedUrl ? `<option value="${esc(mbSavedUrl)}" data-id="${esc(isMb ? (action?.config?.business_id || '') : '')}" selected>${esc(mbSavedName)}</option>` : ''}
      </select>
      <select class="mag-modal__select mag-cta__mbservice" data-saved="${esc(isMb ? (action?.config?.service_id || '') : '')}" data-saved-name="${esc(isMb ? (action?.config?.event_name || '') : '')}"${dis}${isMb ? '' : ' hidden'}>
        ${isMb && action?.config?.service_id ? `<option value="${esc(action.config.service_id)}" selected>${esc(action?.config?.event_name || 'Saved service')}</option>` : `<option value="">Whole page (opens in the magnet)</option>`}
      </select>`;
  } else {
    control = `<span class="mag-cta__note">${meta.kind === 'support' ? 'Multi-select intake' : 'Opens your conversation flow'}</span>`;
  }
  let sub = '';
  if (meta.kind === 'support') {
    const opts = (action?.config?.options || []).map((o) => (o && o.label) || o).filter(Boolean);
    sub = `<textarea class="mag-modal__input mag-cta__opts" rows="3" placeholder="Support options, one per line (e.g. Sales&#10;Support&#10;Billing)"${dis}>${esc(opts.join('\n'))}</textarea>`;
  }
  return `<div class="mag-cta" data-type="${type}" data-kind="${meta.kind}">
    <div class="mag-cta__main">
      <label class="mag-cta__tog"><input type="checkbox" class="mag-cta__on"${on ? ' checked' : ''}>
        <span class="material-symbols-outlined" aria-hidden="true">${meta.icon}</span>${meta.label}</label>
      ${control}
    </div>
    ${sub}
  </div>`;
}

function ensureStyles() {
  if (stylesInjected) return;
  const style = document.createElement('style');
  style.id = 'm5t-magnets-styles';
  style.textContent = `
    /* List rows, not a card grid (2026-08-20 redesign). .mag-grid / .mag-card class
       names survive as selector anchors — tests and the async install/connection
       patchers key on them — but the grid is now a bordered list panel of rows. */
    .page-shell .mag-grid { --magcols: minmax(220px, 1.7fr) minmax(135px, 1fr) minmax(150px, 1fr) max-content; display: flex; flex-direction: column; border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-lg, 16px); background: var(--color-bg, #fff); overflow: visible; }
    .page-shell .mag-head { display: grid; grid-template-columns: var(--magcols); column-gap: 18px; padding: 10px 18px; border-bottom: 1px solid var(--color-border, #D4DCE8); font-size: 0.68rem; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .mag-card { padding: 16px 18px; border-bottom: 1px solid var(--color-border, #D4DCE8); transition: background 0.15s ease; }
    .page-shell .mag-card.is-open { background: var(--color-surface, #F4F7FB); box-shadow: inset 3px 0 0 var(--color-accent, #0072BA); }
    .page-shell .mag-card:last-child { border-bottom: 0; }
    .page-shell .mag-line { display: grid; grid-template-columns: var(--magcols); column-gap: 18px; align-items: center; cursor: pointer; }
    .page-shell .mag-card:hover:not(.is-open) { background: var(--color-surface, #F4F7FB); }
    .page-shell .mag-cell { min-width: 0; }
    .page-shell .mag-cell--act { display: flex; align-items: center; gap: 6px; justify-content: flex-end; }
    .page-shell .mag-cellsub { font-size: 0.78rem; color: var(--color-text-subtle, #6C7C9A); margin-top: 2px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .page-shell .mag-status { display: flex; align-items: center; gap: 7px; font-size: 0.9rem; font-weight: 700; color: var(--color-text-muted, #4A5A78); }
    .page-shell .mag-status[data-live="live"] { color: var(--color-success, #009473); }
    .page-shell .mag-status[data-live="seen"] { color: #B45309; }
    .page-shell .mag-statline { font-size: 0.9rem; font-variant-numeric: tabular-nums; color: var(--color-text, #0B1220); }
    .page-shell .mag-statline b { font-weight: 700; }
    .page-shell .mag-statline--empty { color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .mag-btn--icon { padding: 0; width: 34px; height: 34px; justify-content: center; border-radius: 9px; }
    .page-shell .mag-dot { flex: 0 0 auto; width: 10px; height: 10px; border-radius: 50%; background: var(--color-border, #D4DCE8); }
    .page-shell .mag-dot[data-live="live"] { background: var(--color-success, #009473); }
    .page-shell .mag-dot[data-live="seen"] { background: #B45309; }
    .page-shell .mag-dot[data-live="quiet"] { background: transparent; box-shadow: inset 0 0 0 2px var(--color-border, #D4DCE8); }
    .page-shell .mag-dot[data-live="off"] { background: var(--color-text-subtle, #6C7C9A); }
    .page-shell .mag-dot[data-live="draft"] { background: transparent; box-shadow: inset 0 0 0 2px var(--color-text-subtle, #6C7C9A); opacity: 0.65; }
    .page-shell .mag-id__line { display: flex; align-items: center; gap: 8px; min-width: 0; }
    .page-shell .mag-sub { margin-top: 2px; }
    .page-shell .mag-name { font-weight: 700; font-size: 1.02rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .page-shell .mag-sub { font-size: 0.82rem; color: var(--color-text-subtle, #6C7C9A); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .page-shell .mag-more-wrap { position: relative; }
    .page-shell .mag-menu { position: absolute; right: 0; top: calc(100% + 4px); z-index: 30; min-width: 158px; background: var(--color-bg, #fff); border: 1px solid var(--color-border, #D4DCE8); border-radius: 10px; box-shadow: 0 10px 28px rgba(11, 18, 32, 0.14); padding: 4px; display: flex; flex-direction: column; }
    .page-shell .mag-menu[hidden] { display: none; }
    .page-shell .mag-menu__item { appearance: none; border: 0; background: none; cursor: pointer; font: inherit; font-size: 0.84rem; font-weight: 600; text-align: left; display: flex; align-items: center; gap: 8px; padding: 7px 10px; border-radius: 7px; color: var(--color-text, #0B1220); }
    .page-shell .mag-menu__item:hover { background: var(--color-surface, #F4F7FB); }
    .page-shell .mag-menu__item.danger { color: var(--color-danger, #DD4124); }
    .page-shell .mag-menu__item .material-symbols-outlined { font-size: 16px; }
    .page-shell .mag-detail { margin: 13px 0 3px 22px; padding: 14px 16px; border: 1px solid var(--color-border, #D4DCE8); background: var(--color-bg, #fff); border-radius: 12px; display: flex; flex-direction: column; gap: 11px; }
    .page-shell .mag-detail:not([hidden]) { animation: mag-reveal 0.18s ease; }
    @keyframes mag-reveal { from { opacity: 0; transform: translateY(-5px); } }
    @media (prefers-reduced-motion: reduce) { .page-shell .mag-detail:not([hidden]) { animation: none; } .page-shell .mag-card { transition: none; } }
    .page-shell .mag-detail__hd { display: flex; align-items: center; gap: 9px; border-bottom: 1px solid var(--color-border, #D4DCE8); padding-bottom: 10px; }
    .page-shell .mag-detail__k { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.07em; text-transform: uppercase; color: var(--color-accent, #0072BA); background: var(--color-accent-soft, rgba(0,114,186,0.10)); padding: 3px 8px; border-radius: 6px; }
    .page-shell .mag-detail__n { font-size: 0.92rem; font-weight: 700; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .page-shell .mag-detail__x { appearance: none; border: 0; background: none; cursor: pointer; margin-left: auto; width: 30px; height: 30px; border-radius: 8px; display: grid; place-items: center; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .mag-detail__x:hover { background: var(--color-surface, #F4F7FB); color: var(--color-text, #0B1220); }
    .page-shell .mag-detail__x .material-symbols-outlined { font-size: 19px; }
    .page-shell .mag-detail[hidden] { display: none; }
    .page-shell .mag-detail .mag-embed input { background: var(--color-surface, #F4F7FB); }
    @media (max-width: 760px) {
      .page-shell .mag-head { display: none; }
      .page-shell .mag-line { grid-template-columns: 1fr max-content; row-gap: 7px; }
      .page-shell .mag-cell--status, .page-shell .mag-cell--stats { grid-column: 1 / -1; display: flex; align-items: baseline; gap: 8px; }
      .page-shell .mag-cell--status .mag-cellsub, .page-shell .mag-cell--stats .mag-cellsub { margin-top: 0; }
      .page-shell .mag-detail { margin-left: 0; }
    }
    .page-shell .mag-owner { font-size: 0.74rem; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .mag-badge { flex: 0 0 auto; font-size: 0.68rem; font-weight: 700; letter-spacing: 0.05em; text-transform: uppercase; padding: 3px 8px; border-radius: 999px; }
    .page-shell .mag-badge[data-s="ENABLED"] { background: #DCFCE7; color: #047857; }
    .page-shell .mag-badge[data-s="DISABLED"], .page-shell .mag-badge[data-s="DRAFT"] { background: var(--color-surface-2, #EAF0F7); color: var(--color-text-muted, #4A5A78); }
    .page-shell .mag-ab-chip { display: inline-flex; align-items: center; gap: 4px; font-size: 0.66rem; font-weight: 700; letter-spacing: 0.04em; padding: 2px 8px; border-radius: 999px; background: #EDE9FE; color: #6D28D9; }
    .page-shell .mag-ab-chip .material-symbols-outlined { font-size: 13px; }
    .page-shell .mag-alert-chip { display: inline-flex; align-items: center; gap: 4px; font-size: 0.66rem; font-weight: 700; letter-spacing: 0.04em; padding: 2px 8px; border-radius: 999px; background: var(--color-success-soft, #E3F4EF); color: var(--color-success, #009473); text-decoration: none; }
    .page-shell .mag-alert-chip:hover { filter: brightness(0.95); }
    .page-shell .mag-alert-chip .material-symbols-outlined { font-size: 13px; }
    .page-shell .mag-inline-note { display: flex; gap: 6px; align-items: flex-start; font-size: 0.8rem; color: var(--color-text-subtle, #6C7C9A); margin-top: -4px; }
    .page-shell .mag-inline-note .material-symbols-outlined { font-size: 15px; flex: 0 0 auto; margin-top: 1px; }
    .page-shell .mag-inline-note code, .mag-modal__body code { font-family: ui-monospace, Menlo, monospace; font-size: 0.82em; background: var(--color-surface, #F4F7FB); padding: 1px 5px; border-radius: 5px; }
    .page-shell .mag-embed { display: flex; gap: 6px; align-items: center; }
    .page-shell .mag-embed input { flex: 1 1 auto; min-width: 0; font: inherit; font-size: 0.78rem; font-family: ui-monospace, Menlo, monospace; padding: 7px 9px; border: 1px solid var(--color-border, #D4DCE8); border-radius: 8px; background: var(--color-surface, #F4F7FB); color: var(--color-text-muted, #4A5A78); }
    .page-shell .mag-guide { font-size: 0.8rem; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .mag-guide summary { display: flex; align-items: center; gap: 6px; cursor: pointer; font-weight: 600; color: var(--color-primary-600, #0072BA); list-style: none; }
    .page-shell .mag-guide summary::-webkit-details-marker { display: none; }
    .page-shell .mag-guide summary .material-symbols-outlined { font-size: 15px; flex: 0 0 auto; }
    .page-shell .mag-guide__body { margin: 6px 0 0 21px; padding: 8px 10px; border-left: 2px solid var(--color-border, #D4DCE8); }
    .page-shell .mag-guide__plat + .mag-guide__plat { margin-top: 10px; padding-top: 9px; border-top: 1px solid var(--color-border, #D4DCE8); }
    .page-shell .mag-guide__plat h4 { display: flex; align-items: center; gap: 6px; margin: 0 0 4px; font-size: 0.74rem; font-weight: 600; color: var(--color-text, #0B1220); }
    .page-shell .mag-guide__plat h4 .material-symbols-outlined { font-size: 15px; color: var(--color-primary-600, #0072BA); }
    .page-shell .mag-guide__body ol { margin: 0; padding-left: 18px; }
    .page-shell .mag-guide__body li { margin-bottom: 3px; line-height: 1.45; }
    .page-shell .mag-guide__note { margin: 5px 0 0; line-height: 1.45; }
    .page-shell .mag-guide__snippet { margin: 5px 0 0; padding: 7px 9px; border-radius: 6px; background: var(--color-surface, #F4F7FB); border: 1px solid var(--color-border, #D4DCE8); overflow-x: auto; }
    .page-shell .mag-guide__snippet code { font-family: ui-monospace, Menlo, monospace; font-size: 0.9em; white-space: pre; background: none; padding: 0; }
    .page-shell .mag-guide code { font-family: ui-monospace, Menlo, monospace; font-size: 0.82em; background: var(--color-surface, #F4F7FB); padding: 1px 5px; border-radius: 5px; }
    .page-shell .mag-csp-note { font-size: 0.8rem; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .mag-csp-note summary { display: flex; align-items: center; gap: 6px; cursor: pointer; font-weight: 600; color: var(--color-warning, #915E00); list-style: none; }
    .page-shell .mag-csp-note summary::-webkit-details-marker { display: none; }
    .page-shell .mag-csp-note summary .material-symbols-outlined { font-size: 15px; flex: 0 0 auto; }
    .page-shell .mag-csp-note__body { margin: 6px 0 0 21px; padding: 8px 10px; border-left: 2px solid var(--color-border, #D4DCE8); }
    .page-shell .mag-csp-note__body p { margin: 0 0 6px; }
    .page-shell .mag-csp-note__body ul { margin: 0 0 6px; padding-left: 18px; }
    .page-shell .mag-csp-note__body li { margin-bottom: 2px; }
    .page-shell .mag-csp-note code { font-family: ui-monospace, Menlo, monospace; font-size: 0.82em; background: var(--color-surface, #F4F7FB); padding: 1px 5px; border-radius: 5px; }
    /* install status — tokens only, and a state attribute rather than three class
       names, so the three states cannot drift apart visually. */
    .page-shell .mag-install { margin: 8px 0 0; padding: 10px 12px; border-radius: 8px; background: var(--color-surface, #F4F7FB); border: 1px solid var(--color-border, #D4DCE8); font-size: 0.84rem; }
    .page-shell .mag-install__hd { display: flex; align-items: center; gap: 6px; font-weight: 600; color: var(--color-text, #0B1220); }
    .page-shell .mag-install__hd .material-symbols-outlined { font-size: 17px; }
    .page-shell .mag-install[data-state="live"] .mag-install__hd { color: var(--color-success, #009473); }
    .page-shell .mag-install[data-state="seen"] .mag-install__hd { color: var(--color-warning-text, #92400E); }
    .page-shell .mag-install[data-state="quiet"] .mag-install__hd { color: var(--color-text-muted, #4A5A78); }
    .page-shell .mag-install__hint { margin: 5px 0 0; color: var(--color-text-subtle, #6C7C9A); line-height: 1.45; }
    .page-shell .mag-install__seen { margin-top: 7px; }
    .page-shell .mag-install__seenhd { color: var(--color-text-muted, #4A5A78); margin-bottom: 4px; }
    .page-shell .mag-install__row { display: flex; align-items: center; gap: 8px; padding: 3px 0; }
    .page-shell .mag-install__dom { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: ui-monospace, Menlo, monospace; }
    .page-shell .mag-install__hits { flex: 0 0 auto; color: var(--color-text-subtle, #6C7C9A); }
    .page-shell .mag-install__dismissed { margin-top: 7px; }
    .page-shell .mag-install__dismissed > summary { cursor: pointer; color: var(--color-text-muted, #4A5A78); font-size: 0.76rem; }
    .page-shell .mag-install__dismissed > summary:hover { color: var(--color-text, #0B1220); }
    .page-shell .mag-install__dismissed .mag-install__dom { color: var(--color-text-subtle, #6C7C9A); }
    /* connected sites — same surface, border and type scale as the install block above,
       and the same data-state-over-class-names approach, so the two read as one section
       rather than two widgets that happen to sit together. Tokens only. */
    .page-shell .mag-conn { margin: 8px 0 0; padding: 10px 12px; border-radius: 8px; background: var(--color-surface, #F4F7FB); border: 1px solid var(--color-border, #D4DCE8); font-size: 0.84rem; }
    .page-shell .mag-conn__hd { display: flex; align-items: center; gap: 6px; font-weight: 600; color: var(--color-text, #0B1220); }
    .page-shell .mag-conn__hd .material-symbols-outlined { font-size: 17px; }
    .page-shell .mag-conn__row { display: flex; align-items: center; gap: 8px; padding: 5px 0; }
    .page-shell .mag-conn__row + .mag-conn__row { border-top: 1px solid var(--color-border, #D4DCE8); }
    .page-shell .mag-conn__icon { font-size: 17px; flex: 0 0 auto; color: var(--color-text-muted, #4A5A78); }
    .page-shell .mag-conn__row[data-state="live"] .mag-conn__icon { color: var(--color-success, #009473); }
    .page-shell .mag-conn__row[data-state="pending"] .mag-conn__icon { color: var(--color-warning-text, #92400E); }
    .page-shell .mag-conn__row[data-state="off"] { opacity: 0.62; }
    .page-shell .mag-conn__site { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; }
    .page-shell .mag-conn__host { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-family: ui-monospace, Menlo, monospace; }
    .page-shell .mag-conn__sub { color: var(--color-text-subtle, #6C7C9A); font-size: 0.8rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
    .page-shell .mag-conn__status { flex: 0 0 auto; color: var(--color-text-muted, #4A5A78); }
    .page-shell .mag-conn__past { margin-top: 4px; }
    .page-shell .mag-conn__past > summary { cursor: pointer; color: var(--color-text-muted, #4A5A78); font-size: 0.82rem; padding: 3px 0; }
    .page-shell .mag-conn__past > summary:hover { color: var(--color-text, #0B1220); }
    .page-shell .mag-conn__code { margin-top: 8px; padding: 9px 10px; border-radius: 8px; border: 1px dashed var(--color-border, #D4DCE8); background: var(--color-bg, #FFFFFF); }
    .page-shell .mag-conn__code[data-state="done"] .mag-conn__hd { color: var(--color-success, #009473); }
    /* The code has to survive a narrow card without a horizontal scrollbar — it is 48
       characters and someone has to read it off the screen to paste it. */
    .page-shell .mag-conn__codebox { display: flex; align-items: center; gap: 8px; margin-top: 6px; }
    .page-shell .mag-conn__codebox code { flex: 1 1 auto; min-width: 0; font-family: ui-monospace, Menlo, monospace; font-size: 0.82rem; letter-spacing: 0.02em; word-break: break-all; background: var(--color-surface, #F4F7FB); border-radius: 6px; padding: 6px 8px; }
    .page-shell .mag-conn__countdown { margin: 6px 0 0; color: var(--color-text-muted, #4A5A78); }
    .page-shell .mag-csp-note__snippet { margin: 0 0 8px; padding: 10px 12px; background: var(--color-surface, #F4F7FB); border: 1px solid var(--color-border, #D4DCE8); border-radius: 8px; overflow-x: auto; }
    .page-shell .mag-csp-note__snippet code { display: block; background: transparent; padding: 0; white-space: pre; line-height: 1.6; }
    .page-shell .mag-btn { appearance: none; border: 1px solid var(--color-border, #D4DCE8); background: transparent; cursor: pointer; font: inherit; font-size: 0.84rem; font-weight: 600; padding: 7px 12px; border-radius: 8px; display: inline-flex; align-items: center; gap: 5px; color: var(--color-text, #0B1220); }
    .page-shell .mag-btn:hover { background: var(--color-surface, #F4F7FB); }
    .page-shell .mag-btn.danger { color: var(--color-danger, #DD4124); }
    .page-shell .mag-btn .material-symbols-outlined { font-size: 17px; }
    .page-shell .mag-btn--primary { border-color: var(--color-accent, #0072BA); color: var(--color-accent, #0072BA); font-weight: 700; }
    .page-shell .mag-btn--primary:hover { background: var(--color-accent-soft, rgba(0,114,186,0.10)); }
    .page-shell .mag-btn.is-open { background: var(--color-accent-soft, rgba(0,114,186,0.12)); border-color: var(--color-accent, #0072BA); color: var(--color-accent, #0072BA); }
    .mag-funnel-modal .mag-modal__body { max-height: 70vh; overflow-y: auto; }
    .mag-funnel { display: flex; flex-direction: column; gap: 12px; }
    .mag-funnel__row { display: flex; flex-direction: column; gap: 4px; }
    .mag-funnel__meta { display: flex; justify-content: space-between; font-size: 0.8rem; }
    .mag-funnel__label { font-weight: 600; color: var(--color-text-muted, #4A5A78); }
    .mag-funnel__val { font-weight: 700; color: var(--color-text, #0B1220); }
    .mag-funnel__pct { font-size: 0.72rem; font-weight: 500; color: var(--color-text-subtle, #6C7C9A); }
    .mag-funnel__track { height: 12px; border-radius: 999px; background: var(--color-surface-2, #EAF0F7); overflow: hidden; }
    .mag-funnel__bar { height: 100%; border-radius: 999px; background: var(--color-primary-600, #0072BA); transition: width 0.4s ease; }
    .mag-funnel__bar--drop { background: #7BC4C4; }

    .mag-modal-scrim { position: fixed; inset: 0; background: rgba(11,18,32,0.5); z-index: 2000; opacity: 0; transition: opacity 0.18s ease; }
    .mag-modal-scrim.open { opacity: 1; }
    .mag-modal { position: fixed; left: 50%; top: 50%; transform: translate(-50%, -48%); z-index: 2001; display: flex; flex-direction: column; width: min(1680px, 95vw); height: min(920px, 92vh); background: var(--color-bg, #fff); color: var(--color-text, #0B1220); border-radius: var(--radius-lg, 16px); box-shadow: 0 20px 60px rgba(11,18,32,0.3); opacity: 0; transition: opacity 0.18s ease, transform 0.18s ease; overflow: hidden; }
    .mag-modal.open { opacity: 1; transform: translate(-50%, -50%); }
    .mag-modal__head { flex: 0 0 auto; display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid var(--color-border, #D4DCE8); }
    .mag-modal__title { font-family: var(--font-display, inherit); font-size: 1.05rem; font-weight: 600; }
    .mag-modal__close { appearance: none; border: 0; background: transparent; cursor: pointer; font-size: 1.5rem; line-height: 1; color: var(--color-text-muted, #4A5A78); width: 32px; height: 32px; border-radius: 8px; }
    .mag-modal__close:hover { background: var(--color-surface, #F4F7FB); }
    /* two-pane: section nav (sub-menu) + one panel at a time, so no giant single scroll */
    .mag-modal__main { flex: 1 1 auto; display: flex; min-height: 0; }
    .mag-modal__nav { flex: 0 0 auto; width: 188px; padding: 14px 10px; border-right: 1px solid var(--color-border, #D4DCE8); background: var(--color-surface, #F7F9FC); display: flex; flex-direction: column; gap: 3px; overflow-y: auto; }
    .mag-modal__navitem { appearance: none; border: 0; background: transparent; cursor: pointer; font: inherit; font-size: 0.9rem; font-weight: 600; text-align: left; display: flex; align-items: center; gap: 10px; padding: 10px 12px; border-radius: 9px; color: var(--color-text-muted, #4A5A78); }
    .mag-modal__navitem .material-symbols-outlined { font-size: 19px; }
    .mag-modal__navitem:hover { background: var(--color-bg, #fff); color: var(--color-text, #0B1220); }
    .mag-modal__navitem.is-active { background: var(--color-primary-600, #0072BA); color: #fff; }
    .mag-modal__body { flex: 1 1 auto; overflow-y: auto; padding: 20px 24px; min-width: 0; }
    /* live preview column — wide screens only; narrow keeps the footer Preview button */
    .mag-modal__preview { flex: 0 0 clamp(420px, 34vw, 720px); border-left: 1px solid var(--color-border, #D4DCE8); background: var(--color-surface, #F7F9FC); padding: 18px; overflow: hidden; }
    @media (max-width: 1199px) { .mag-modal__preview { display: none; } }
    .mag-tab[hidden] { display: none; }
    .mag-tab > .mag-modal__sec:first-child { margin-top: 0; padding-top: 0; border-top: 0; }
    .mag-modal__label { display: block; font-size: 0.8rem; font-weight: 600; margin: 14px 0 6px; }
    .mag-modal__label:first-child { margin-top: 0; }
    .mag-modal__input, .mag-modal__select { width: 100%; box-sizing: border-box; font: inherit; font-size: 0.95rem; padding: 10px 12px; border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-md, 10px); background: var(--color-bg, #fff); color: var(--color-text, #0B1220); }
    .mag-modal__input:focus, .mag-modal__select:focus { outline: none; border-color: var(--color-primary-600, #0072BA); box-shadow: 0 0 0 3px var(--color-accent-soft, rgba(0,114,186,0.15)); }
    .mag-modal__row { display: flex; gap: 12px; align-items: flex-end; }
    .mag-modal__row > div { flex: 1; }
    .mag-modal__color { width: 46px; height: 44px; padding: 3px; border: 1px solid var(--color-border, #D4DCE8); border-radius: 10px; background: var(--color-bg, #fff); cursor: pointer; }
    .mag-modal__hint { font-size: 0.74rem; color: var(--color-text-subtle, #6C7C9A); margin: 5px 0 0; }
    .mag-modal__foot { flex: 0 0 auto; display: flex; align-items: center; gap: 14px; padding: 13px 20px; border-top: 1px solid var(--color-border, #D4DCE8); background: var(--color-bg, #fff); }
    .mag-modal__error { flex: 1 1 auto; color: var(--color-danger, #DD4124); font-size: 0.82rem; margin: 0; min-height: 1em; }
    .mag-modal__dirty { display: flex; align-items: center; gap: 5px; font-size: 0.78rem; font-weight: 600; color: var(--color-warning, #915E00); }
    .mag-modal__dirty[hidden] { display: none; }
    .mag-modal__dirty .material-symbols-outlined { font-size: 15px; }
    .mag-modal__foot-btns { flex: 0 0 auto; display: flex; gap: 10px; }
    .mag-modal__btn { appearance: none; border: 0; cursor: pointer; font: inherit; font-size: 0.9rem; font-weight: 600; padding: 9px 16px; border-radius: var(--radius-md, 10px); }
    .mag-modal__btn--ghost { background: transparent; color: var(--color-text-muted, #4A5A78); }
    .mag-modal__btn--ghost:hover { background: var(--color-surface, #F4F7FB); }
    .mag-modal__btn--preview { --preview-color: #6d28d9; --preview-hover: #5b21b6; background: var(--preview-color); color: #fff; }
    .mag-modal__btn--preview:hover { background: var(--preview-hover); }
    .mag-modal__btn--preview:focus-visible { outline: 3px solid var(--preview-color); outline-offset: 3px; }
    .mag-modal__btn--primary { background: var(--color-primary-600, #0072BA); color: #fff; }
    .mag-modal__btn--primary[disabled] { opacity: 0.6; cursor: default; }
    @media (max-width: 640px) {
      .mag-modal { width: 96vw; height: 92vh; }
      .mag-modal__main { flex-direction: column; }
      .mag-modal__nav { width: auto; flex-direction: row; overflow-x: auto; border-right: 0; border-bottom: 1px solid var(--color-border, #D4DCE8); }
      .mag-modal__navitem { flex: 0 0 auto; }
    }

    /* configurator: sections + segmented controls + CTA rows */
    .mag-modal__sec { margin-top: 20px; padding-top: 16px; border-top: 1px solid var(--color-border, #D4DCE8); }
    .mag-modal__sec-t { font-size: 0.7rem; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase; color: var(--color-text-subtle, #6C7C9A); margin: 0 0 10px; }
    .mag-seg { display: grid; grid-auto-flow: column; grid-auto-columns: 1fr; gap: 6px; }
    .mag-seg label { position: relative; cursor: pointer; margin: 0; }
    .mag-seg input { position: absolute; opacity: 0; width: 0; height: 0; pointer-events: none; }
    .mag-seg span { display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 9px 4px; border: 1px solid var(--color-border, #D4DCE8); border-radius: var(--radius-md, 10px); font-size: 0.76rem; font-weight: 600; color: var(--color-text-muted, #4A5A78); text-align: center; transition: border-color .12s, background .12s, color .12s; }
    .mag-seg span .material-symbols-outlined { font-size: 20px; }
    .mag-seg label:hover span { border-color: var(--color-primary-600, #0072BA); }
    .mag-seg input:checked + span { border-color: var(--color-accent, #0072BA); background: var(--color-accent-soft, rgba(0,114,186,0.12)); color: var(--color-accent, #0072BA); }
    .mag-seg input:focus-visible + span { box-shadow: 0 0 0 3px var(--color-accent-soft, rgba(0,114,186,0.15)); }
    .mag-cta { display: flex; flex-direction: column; gap: 8px; padding: 7px 0; }
    .mag-cta__main { display: flex; align-items: center; gap: 10px; }
    .mag-cta__tog { flex: 0 0 auto; display: flex; align-items: center; gap: 7px; width: 84px; font-size: 0.86rem; font-weight: 600; cursor: pointer; margin: 0; }
    .mag-cta__tog .material-symbols-outlined { font-size: 18px; color: var(--color-text-subtle, #6C7C9A); }
    .mag-cta__val { flex: 1 1 auto; min-width: 0; }
    .mag-cta__booksrc, .mag-cta__callsrc { flex: 0 0 auto; width: auto; }
    .mag-cta__calevent, .mag-cta__crtracker { flex: 1 1 auto; min-width: 0; }
    .mag-cta__note { flex: 1 1 auto; font-size: 0.78rem; color: var(--color-text-subtle, #6C7C9A); font-style: italic; }
    .mag-cta__opts { width: 100%; box-sizing: border-box; font: inherit; font-size: 0.86rem; resize: vertical; }
    .mag-cta__val:disabled, .mag-cta__opts:disabled { opacity: 0.5; }
    .mag-tog-row { display: flex; align-items: center; gap: 10px; }
    .mag-tog-row label { font-size: 0.9rem; font-weight: 600; cursor: pointer; margin: 0; }
    .mag-surface-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(84px, 1fr)); gap: 10px; margin-top: 8px; }
    .mag-surface-grid[hidden] { display: none; }
    .mag-surface-grid label { display: block; }
    .mag-style-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(130px, 1fr)); gap: var(--space-3, 12px); margin-top: var(--space-2, 8px); }
    .mag-style-card { display: flex; flex-direction: column; gap: var(--space-2, 8px); text-align: left; padding: var(--space-3, 12px); border: 1px solid var(--color-border); border-radius: var(--radius-md); background: var(--color-bg); color: var(--color-text); font: inherit; cursor: pointer; }
    .mag-style-card[aria-pressed="true"] { border-color: var(--color-primary-600); background: var(--color-accent-soft); }
    .mag-style-card:focus-visible { outline: 2px solid var(--color-primary-600); outline-offset: 2px; }
    .mag-style-card small { color: var(--color-text-muted); line-height: 1.4; }
    .mag-style-sample { display: block; padding: var(--space-2, 8px) var(--space-3, 12px); border-radius: var(--radius-sm); background: var(--mag-style-surface); color: var(--mag-style-text); font-family: var(--mag-style-font); font-size: 1.25rem; }
    #mag-f-inline-size[hidden] { display: none; }
    .mag-size-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(104px, 1fr)); gap: 10px; margin-top: 8px; }
    .mag-size-card { position: relative; display: flex; flex-direction: column; align-items: center; gap: 6px; border: 1.5px solid var(--color-border, #D4DCE8); border-radius: var(--radius-md, 10px); padding: 10px 8px 9px; cursor: pointer; text-align: center; }
    .mag-size-card:hover { border-color: var(--color-primary-600, #0072BA); }
    .mag-size-card input { position: absolute; opacity: 0; pointer-events: none; }
    .mag-size-card.is-on { border-color: var(--color-primary-600, #0072BA); background: var(--color-accent-soft, rgba(0,114,186,0.06)); }
    .mag-size-thumb { width: 56px; height: 44px; display: grid; place-items: center; }
    .mag-size-thumb i { display: block; border: 1.5px solid var(--color-text-subtle, #6C7C9A); border-radius: 4px; background: var(--color-surface, #F4F7FB); }
    .mag-size-card.is-on .mag-size-thumb i { border-color: var(--color-primary-600, #0072BA); background: var(--color-bg, #fff); }
    .mag-size-name { font-size: 0.82rem; font-weight: 700; }
    .mag-size-hint { font-size: 0.68rem; color: var(--color-text-subtle, #6C7C9A); line-height: 1.3; }
    #mag-f-inline-custom[hidden] { display: none; }
    .mag-size-err { color: var(--color-danger-text, #B3261E); font-size: 0.78rem; margin: 6px 0 0; }
    .mag-size-err:empty { display: none; }
    .mag-logo-drop { display: flex; align-items: center; gap: 12px; border: 1.5px dashed var(--color-border, #D4DCE8); border-radius: var(--radius-md, 10px); padding: 12px; cursor: pointer; transition: border-color .12s, background .12s; }
    .mag-logo-drop:hover, .mag-logo-drop.is-dragover { border-color: var(--color-primary-600, #0072BA); background: var(--color-accent-soft, rgba(0,114,186,0.06)); }
    .mag-logo-drop__preview { flex: 0 0 auto; width: 48px; height: 48px; border-radius: 6px; overflow: hidden; display: grid; place-items: center; background: var(--color-surface, #F4F7FB); color: var(--color-text-subtle, #6C7C9A); }
    .mag-logo-drop__preview img { width: 100%; height: 100%; object-fit: contain; }
    .mag-logo-drop__body { flex: 1 1 auto; min-width: 0; }
    .mag-logo-drop__t { margin: 0; font-size: 0.86rem; }
    .mag-logo-drop__browse { appearance: none; border: none; background: none; padding: 0; font: inherit; font-weight: 700; color: var(--color-accent, #0072BA); cursor: pointer; text-decoration: underline; }
    .mag-logo-drop__remove { flex: 0 0 auto; appearance: none; border: 1px solid var(--color-border, #D4DCE8); background: var(--color-bg, #fff); cursor: pointer; width: 30px; height: 30px; border-radius: 8px; display: grid; place-items: center; color: var(--color-text-subtle, #6C7C9A); }
    .mag-logo-drop__remove:hover { color: var(--color-danger, #DD4124); border-color: var(--color-danger, #DD4124); }
    .mag-logo-drop__remove .material-symbols-outlined { font-size: 17px; }
    .mag-logo-drop__remove[hidden] { display: none; }
  `;
  document.head.appendChild(style);
  stylesInjected = true;
}

function emptyState(icon, msg) {
  return `<div class="m5t-empty"><span class="material-symbols-outlined" aria-hidden="true">${icon}</span><p>${msg}</p></div>`;
}

// Trailing-30-day funnel strip for a card (from GET /magnets/stats). No data yet → quiet line.
function magnetStatsHTML(guid) {
  const s = state.stats[guid];
  if (!s || (!s.opens && !s.leads)) {
    return `<div class="mag-statline mag-statline--empty">&mdash;</div><div class="mag-cellsub">No activity yet</div>`;
  }
  const fmt = (n) => Number(n || 0).toLocaleString();
  return `<div class="mag-statline" title="Trailing 30 days"><b>${fmt(s.opens)}</b> opens &middot; <b>${fmt(s.leads)}</b> leads</div>
    ${s.completion_rate != null ? `<div class="mag-cellsub">${s.completion_rate}% complete the conversation</div>` : ''}`;
}

// ── install status ───────────────────────────────────────────────────────────
// Three states, and the third is deliberately honest rather than confident.
//
// When a strict script-src blocks us, the script is never REQUESTED — no beacon, no
// snippet hit, nothing. So silence cannot distinguish "hasn't pasted it yet" from
// "pasted it and their CSP is eating it". Calling that "not installed" is the one
// wording we must avoid: it sends someone to re-paste a snippet that is already there.
// Both possibilities get named instead.
//
// Populated per card by loadInstallActivity() after the grid renders; until then the
// block simply isn't drawn, so the card never flashes a wrong state.
function installBlockHTML(m) {
  const a = state.install[m.guid];
  if (!a) return '';
  const unknown = a.unrecognized || [];
  const live = (a.traffic?.opens || 0) > 0;
  // THIS magnet, not the session. A managed_client owner was shown live Allow and
  // Dismiss buttons that 403'd on every click — and the 403 then signed them out.
  const canEdit = m.can_manage === true;

  const seenList = unknown.length ? `
    <div class="mag-install__seen">
      <div class="mag-install__seenhd">Seen requesting this magnet, not on the allow-list:</div>
      ${unknown.map((u) => `
        <div class="mag-install__row">
          <span class="mag-install__dom" title="${esc(u.domain)}">${esc(u.domain)}</span>
          <span class="mag-install__hits">${Number(u.hits || 0).toLocaleString()}&times;</span>
          ${canEdit ? `<button class="mag-btn" data-action="allow-domain" data-guid="${esc(m.guid)}" data-domain="${esc(u.domain)}">Allow</button>
          <button class="mag-btn" data-action="dismiss-domain" data-guid="${esc(m.guid)}" data-domain="${esc(u.domain)}" title="Stop showing this domain">Dismiss</button>` : ''}
        </div>`).join('')}
      <p class="mag-install__hint">Domains must be approved before this magnet will load on them. Only approve a domain you recognise — approving the wrong one lets that site use your magnet. Dismiss hides a domain from this list for good; it does not approve it.</p>
    </div>` : '';

  // Requests that arrived with no referrer at all. Worth showing because they are
  // SERVED: the domain check needs a domain to check, so a request without one falls
  // through. Phrased for the person reading it — "we can't tell which site" — rather
  // than as a security note, since the actionable version for them is usually "your
  // site sets Referrer-Policy: no-referrer", not "you are being attacked".
  const nr = Number(a.no_referer?.hits || 0);
  const noRefererNote = nr ? `
    <p class="mag-install__hint">
      ${nr.toLocaleString()} request${nr === 1 ? '' : 's'} in the last ${Number(a.window_days || 30)} days arrived with no
      referrer, so we couldn't tell which site they came from. They were served. This is
      usually a site sending <code>Referrer-Policy: no-referrer</code>.
    </p>` : '';

  // Dismissed domains live behind a summary: "permanently suppressed" has to stay
  // reachable, or it becomes state a customer cannot see or undo.
  const dismissed = a.dismissed || [];
  const dismissedList = dismissed.length ? `
    <details class="mag-install__dismissed">
      <summary>Dismissed (${dismissed.length})</summary>
      ${dismissed.map((d) => `
        <div class="mag-install__row">
          <span class="mag-install__dom" title="${esc(d)}">${esc(d)}</span>
          ${canEdit ? `<button class="mag-btn" data-action="restore-domain" data-guid="${esc(m.guid)}" data-domain="${esc(d)}">Restore</button>` : ''}
        </div>`).join('')}
      <p class="mag-install__hint">Dismissing also covers subdomains — hiding <code>vercel.app</code> hides every preview URL under it.</p>
    </details>` : '';

  if (live) {
    const when = a.traffic.last_open_at ? new Date(a.traffic.last_open_at).toLocaleString() : '';
    return `<div class="mag-install" data-state="live">
      <div class="mag-install__hd"><span class="material-symbols-outlined" aria-hidden="true">check_circle</span>
        Live${when ? ` — last visitor ${esc(when)}` : ''}</div>
      ${seenList}
      ${noRefererNote}
      ${dismissedList}
    </div>`;
  }
  if (unknown.length) {
    return `<div class="mag-install" data-state="seen">
      <div class="mag-install__hd"><span class="material-symbols-outlined" aria-hidden="true">travel_explore</span>
        Installed somewhere we don't recognise</div>
      ${seenList}
      ${noRefererNote}
      ${dismissedList}
    </div>`;
  }
  return `<div class="mag-install" data-state="quiet">
    <div class="mag-install__hd"><span class="material-symbols-outlined" aria-hidden="true">help</span>Not seen yet</div>
    <p class="mag-install__hint">No visitor has loaded this magnet in the last ${Number(a.window_days || 30)} days. Either the snippet isn't on the site yet, or it is and the site's Content-Security-Policy is blocking it — see the note above.</p>
    ${noRefererNote}
    ${dismissedList}
  </div>`;
}

// One request per card, fired AFTER the grid is on screen and each patched in on its
// own. Deliberately not folded into the list endpoint: this reads a different table
// on a different cadence, and a slow or failed lookup must never hold up (or break)
// the magnet list itself.
async function loadInstallActivity() {
  await Promise.all(state.magnets.map(async (m) => {
    try {
      const res = await guardedFetch(`${apiBase()}/m5t/v5/magnets/${encodeURIComponent(m.guid)}/install-activity`);
      const data = await res.json().catch(() => ({}));
      if (!data.ok) return;
      state.install[m.guid] = data;
      applyInstallToRow(m);
    } catch (err) {
      // Silent: an install-status lookup that fails leaves the card exactly as it was.
      console.warn('[magnets] install activity failed:', m.guid, err);
    }
  }));
}

// ── Connected sites (the WordPress plugin handshake, dashboard side) ─────────────
//
// A magnet can be installed two ways: paste the embed snippet (the box below this
// block), or connect a site with the plugin, which pairs and then registers its own
// domain. This block is that second path's whole surface in the app.
//
// THE CODE IS SHOWN ONCE AND CANNOT BE SHOWN AGAIN — the API stores only its SHA-256,
// so no endpoint could re-display it even if this asked. That makes "generate another"
// the ONLY recovery when someone loses one, so it is said in the panel while the code
// is still live, rather than left for a customer to work out afterwards.
//
// site_name comes from the plugin's blogname — i.e. from whoever held the pairing code
// — so it is untrusted text. Everything here goes through esc(), and none of it is
// trusted for a decision.
const CONN_POLL_MS = 5000;
const CONN_STATUS = {
  ACTIVE: { label: 'Connected', icon: 'check_circle', tone: 'live' },
  PENDING: { label: 'Waiting to connect', icon: 'hourglass_empty', tone: 'pending' },
  REVOKED: { label: 'Disconnected', icon: 'link_off', tone: 'off' },
  // A PENDING code whose window has closed. Nothing sweeps these rows, so without a
  // state of their own they sat in the list claiming to be "Waiting to connect"
  // indefinitely — see connPast().
  EXPIRED: { label: 'Expired', icon: 'timer_off', tone: 'off' },
};

/** Is this row finished — history rather than something the customer is waiting on? */
export function connIsPast(c) {
  if (!c) return false;
  if (c.status === 'REVOKED') return true;
  // codeLive is computed by the API from the same rule the exchange enforces. Older
  // responses predate the field; treat its absence as "still live" so a stale client
  // never hides a code that actually works.
  return c.status === 'PENDING' && c.codeLive === false;
}

function connRowHTML(m, c) {
  const expiredCode = c.status === 'PENDING' && c.codeLive === false;
  const s = (expiredCode ? CONN_STATUS.EXPIRED : CONN_STATUS[c.status]) || { label: c.status, icon: 'help', tone: 'off' };
  const when = c.lastSeen ? new Date(c.lastSeen).toLocaleString() : '';
  // A row with no host means no site ever identified itself against that code. Whether
  // that is worth waiting on depends entirely on the code still being live — saying
  // "Waiting for a site to use this code" next to a Disconnected or Expired badge is
  // what made these rows read as broken.
  const title = c.host
    ? esc(c.host)
    : (expiredCode || c.status === 'REVOKED' ? 'Code was never used' : 'Waiting for a site to use this code');
  const sub = [c.siteName ? esc(c.siteName) : '', when ? `last seen ${esc(when)}` : '']
    .filter(Boolean).join(' — ');
  return `
    <div class="mag-conn__row" data-conn="${esc(c.guid)}" data-state="${s.tone}">
      <span class="mag-conn__icon material-symbols-outlined" aria-hidden="true">${s.icon}</span>
      <span class="mag-conn__site">
        <span class="mag-conn__host">${title}</span>
        ${sub ? `<span class="mag-conn__sub">${sub}</span>` : ''}
      </span>
      <span class="mag-conn__status">${esc(s.label)}</span>
      ${m.can_manage !== true ? '' : (connIsPast(c)
    // A finished row is history the customer may simply not want any more. Clearing is
    // separate from Disconnect on purpose: Disconnect ENDS access, Clear tidies up after
    // it, and only rows that are already finished can be cleared.
    ? `<button class="mag-btn" data-action="clear-connection" data-guid="${esc(m.guid)}" data-conn="${esc(c.guid)}"
                   data-label="${esc(c.host || 'this unused code')}"
                   title="Remove this finished record from the list">
             Clear
           </button>`
    : `<button class="mag-btn" data-action="revoke-connection" data-guid="${esc(m.guid)}" data-conn="${esc(c.guid)}"
                   title="${c.status === 'PENDING' ? 'Cancel this pairing code' : 'Disconnect this site and revoke its key'}">
             ${c.status === 'PENDING' ? 'Cancel' : 'Disconnect'}
           </button>`)}
    </div>`;
}

function connCodeHTML(m) {
  const panel = state.pairing[m.guid];
  if (!panel) return '';
  if (panel.error) {
    return `<div class="mag-conn__code" data-state="error">
      <p class="mag-install__hint">${esc(panel.error)}</p>
      <button class="mag-btn" data-action="pair-start" data-guid="${esc(m.guid)}">Try again</button>
    </div>`;
  }
  if (panel.connected) {
    return `<div class="mag-conn__code" data-state="done">
      <div class="mag-conn__hd"><span class="material-symbols-outlined" aria-hidden="true">check_circle</span>
        Connected${panel.connectedHost ? ` — ${esc(panel.connectedHost)}` : ''}</div>
      <p class="mag-install__hint">The site is paired and its domain has been added to this magnet.</p>
    </div>`;
  }
  const left = Math.max(0, Math.ceil((panel.expiresAt - Date.now()) / 1000));
  if (left <= 0) {
    // Expired. The code is unrecoverable by design, so the only way forward is a new
    // one — offered as the primary action rather than as an explanation.
    return `<div class="mag-conn__code" data-state="expired">
      <p class="mag-install__hint">That code expired. Codes are shown once and can't be retrieved, so generate a new one.</p>
      <button class="mag-btn mag-btn--primary" data-action="pair-start" data-guid="${esc(m.guid)}">Generate a new code</button>
    </div>`;
  }
  const mins = String(Math.floor(left / 60));
  const secs = String(left % 60).padStart(2, '0');
  // Keep this panel free of build-status commentary. The pairing mechanism (mint →
  // exchange) is real and works; narrating the WordPress plugin's readiness to customers
  // only invites confusion for no benefit. Copy here should describe what the code IS and
  // what happens next — never where any part of the product sits in its build.
  return `<div class="mag-conn__code" data-state="live">
    <div class="mag-conn__hd">Your pairing code</div>
    <div class="mag-conn__codebox">
      <code data-x="code">${esc(panel.code)}</code>
      <button class="mag-btn" data-action="copy-code" data-guid="${esc(m.guid)}" title="Copy pairing code">
        <span class="material-symbols-outlined" aria-hidden="true">content_copy</span>
      </button>
    </div>
    <p class="mag-conn__countdown">Expires in <strong data-x="countdown">${mins}:${secs}</strong>. Waiting for your site to connect&hellip;</p>
    <p class="mag-install__hint">This code is shown once and can't be shown again — if you lose it, generate another. It only works for this magnet.</p>
  </div>`;
}

function connectionsBlockHTML(m) {
  const data = state.connections[m.guid];
  // Until the per-card fetch lands there is nothing to say; the block stays absent
  // rather than flashing an empty state that is about to be replaced.
  if (!data) return '';
  if (data.failed) {
    return `<div class="mag-conn">
      <div class="mag-conn__hd">
        <span class="material-symbols-outlined" aria-hidden="true">lan</span>WordPress connections
      </div>
      <p class="mag-install__hint">Couldn't load WordPress connections just now. Reload to try again — this doesn't affect the magnet itself.</p>
    </div>`;
  }
  const list = data.connections || [];
  const visible = list.filter((c) => c.status !== 'PENDING' || m.can_manage === true);
  const activeCount = list.filter((c) => c.status === 'ACTIVE').length;
  const panel = connCodeHTML(m);

  // Split what is happening from what already happened. Every spent pairing code and
  // every disconnected site stayed in the main list forever, so a magnet the customer
  // had merely retried a few times looked like a wall of failures — the live state it
  // was there to show was buried under its own history.
  //
  // The finished rows are kept (collapsed), not dropped: "did I already try this?" is a
  // real question, and the answer costs one line when it is not being asked.
  const live = visible.filter((c) => !connIsPast(c));
  const past = visible.filter((c) => connIsPast(c));

  const rows = live.length
    ? live.map((c) => connRowHTML(m, c)).join('')
    : `<p class="mag-install__hint">No WordPress connections yet. A manually installed magnet does not need a WordPress connection.</p>`;

  const pastBlock = past.length ? `
    <details class="mag-conn__past">
      <summary>${past.length} earlier ${past.length === 1 ? 'code or disconnected site' : 'codes and disconnected sites'}</summary>
      ${past.map((c) => connRowHTML(m, c)).join('')}
    </details>` : '';

  return `<div class="mag-conn">
    <div class="mag-conn__hd">
      <span class="material-symbols-outlined" aria-hidden="true">lan</span>
      WordPress connections${activeCount ? ` (${activeCount})` : ''}
    </div>
    ${rows}
    ${pastBlock}
    ${panel}
    ${m.can_manage === true && !panel ? `
      <button class="mag-btn" data-action="pair-start" data-guid="${esc(m.guid)}">
        <span class="material-symbols-outlined" aria-hidden="true">add_link</span>Connect a WordPress site
      </button>` : ''}
  </div>`;
}

// Reuse the existing pairing workflow on the focused Website setup surface.
// The host is owned by that route; no portfolio-wide prefetch or background polling.
export function mountMagnetConnections(m, host) {
  ensureStyles();
  stopPairingTimer();
  state.pairing = {};
  state.connections = {};
  state.magnets = [m];
  let active = true;
  host.classList.add('mag-card'); host.dataset.guid = m.guid;
  host.innerHTML = '<div class="mag-conn-slot"><p role="status">Loading WordPress connections…</p></div>';
  const onClick = async e => {
    const button = e.target.closest('[data-action]');
    if (!button || button.disabled) return;
    const actions = { 'pair-start': () => startPairing(m), 'copy-code': () => copyPairingCode(m),
      'revoke-connection': () => revokeConnection(m, button.dataset.conn),
      'clear-connection': () => clearConnection(m, button.dataset.conn, button.dataset.label) };
    if (!actions[button.dataset.action]) return;
    button.disabled = true;
    try { await actions[button.dataset.action](); } finally { if (button.isConnected) button.disabled = false; }
  };
  host.addEventListener('click', onClick);
  fetchConnections(m).then(data => {
    if (!active || !host.isConnected) return;
    state.connections[m.guid] = data; repaintConnections(m);
  }).catch(() => {
    if (!active || !host.isConnected) return;
    state.connections[m.guid] = { failed: true }; repaintConnections(m);
  });
  return () => { active = false; stopPairingTimer(); state.pairing = {}; host.removeEventListener('click', onClick); };
}

export const prepareMagnetManagementStyles = () => ensureStyles();

function repaintConnections(m) {
  const host = document.querySelector(`.mag-card[data-guid="${CSS.escape(m.guid)}"] .mag-conn-slot`);
  if (host) host.innerHTML = connectionsBlockHTML(m);
}

async function fetchConnections(m) {
  const res = await guardedFetch(`${apiBase()}/m5t/v5/magnets/${encodeURIComponent(m.guid)}/connections`);
  const data = await res.json().catch(() => ({}));
  if (!data.ok) throw new Error(data.error || 'Could not load connected sites');
  return data;
}

// Same shape as loadInstallActivity: one request per card, fired after the grid is on
// screen, each patched in on its own so a slow or failed lookup never holds up the list.
async function loadConnections() {
  await Promise.all(state.magnets.map(async (m) => {
    try {
      state.connections[m.guid] = await fetchConnections(m);
    } catch (err) {
      // A failure used to console.warn and leave state.connections[guid] undefined,
      // which made connectionsBlockHTML render NOTHING — so the whole section simply
      // was not there, and a permission problem or an undeployed endpoint looked
      // identical to a feature that does not exist. Record the failure so the card can
      // say so; the section is small, and silence about it is worse than a short line.
      console.warn('[magnets] connections failed:', m.guid, err);
      state.connections[m.guid] = { failed: true };
    }
    repaintConnections(m);
  }));
}

/**
 * Tick any open pairing panel: redraw the countdown, and poll for the site to appear.
 *
 * ONE timer for the whole page, not one per card. A per-card interval left running
 * after a re-render is the classic way a list view ends up with a dozen live timers
 * polling in the background; this one starts when a panel opens and stops itself the
 * moment none is open.
 */
function stopPairingTimer() {
  if (!state.pairTimer) return;
  clearInterval(state.pairTimer);
  state.pairTimer = null;
}

/**
 * Stop THIS module instance's pairing timer from outside.
 *
 * The app only ever has one instance of this module, so the timer is self-managing there:
 * it stops as soon as no panel is open, and re-entering the route reuses the same `state`.
 * Under test that assumption breaks. Every test calls vi.resetModules(), so a test that
 * opened a pairing panel leaves a 1000ms interval running inside an ORPHANED instance —
 * one the next import cannot reach, because the fresh `state` has pairTimer: null and
 * stopPairingTimer() therefore finds nothing to clear.
 *
 * The orphan keeps firing against the SHARED document. cardMounted() matches on guid
 * alone and the fixtures reuse 'app-f1t', so it recognises a LATER test's card and calls
 * repaintConnections() with its own stale state, overwriting the live DOM with
 * connectionsBlockHTML() built from connections the current test never supplied. Where
 * that stale state has none, the block renders empty and the rows vanish mid-assertion.
 *
 * That is the whole of the magnets-connections flake: it passed alone because the file
 * finished inside one second, and failed in full parallel runs because those cross the
 * interval boundary — so whichever test happened to be running had its rows wiped, which
 * is why the failing test name moved around while the shape never did.
 *
 * Exported for tests to call in afterEach, while they still hold the instance that owns
 * the timer. Not part of the route's public surface.
 */
export function __stopPairingTimerForTests() {
  stopPairingTimer();
}

/** Is this magnet's card still on screen? */
function cardMounted(guid) {
  return !!document.querySelector(`.mag-card[data-guid="${CSS.escape(guid)}"] .mag-conn-slot`);
}

function ensurePairingTimer() {
  if (state.pairTimer) return;
  let sinceLastPoll = 0;
  state.pairTimer = setInterval(async () => {
    // Navigated away. No route here exposes a teardown hook, so without this the
    // interval would outlive the page and keep polling forever against cards that no
    // longer exist. Dropping the panel is right rather than merely pausing: the code
    // is unrecoverable by design, so there is nothing to come back to.
    for (const guid of Object.keys(state.pairing)) {
      if (!cardMounted(guid)) delete state.pairing[guid];
    }

    const open = Object.keys(state.pairing);
    if (!open.length) { stopPairingTimer(); return; }

    // Nothing left that needs animating or polling: every open panel has finished, one
    // way or another. The panels stay on screen; only the timer stops.
    const alive = open.some((g) => {
      const p = state.pairing[g];
      return !p.connected && !p.error && p.expiresAt - Date.now() > 0;
    });
    if (!alive) {
      open.forEach((g) => {
        const m = state.magnets.find((x) => x.guid === g);
        if (m) repaintConnections(m);      // one last paint, so "expired" is on screen
      });
      stopPairingTimer();
      return;
    }

    sinceLastPoll += 1000;
    const poll = sinceLastPoll >= CONN_POLL_MS;
    if (poll) sinceLastPoll = 0;

    for (const guid of open) {
      const m = state.magnets.find((x) => x.guid === guid);
      const panel = state.pairing[guid];
      if (!m || !panel) continue;
      const expired = panel.expiresAt - Date.now() <= 0;

      if (poll && !panel.connected && !panel.error && !expired) {
        try {
          const data = await fetchConnections(m);
          state.connections[guid] = data;
          const hit = (data.connections || []).find((c) => c.guid === panel.connectionGuid && c.status === 'ACTIVE');
          if (hit) {
            // Success ends the panel's life: the code is spent, and leaving it on screen
            // would keep displaying a secret that no longer does anything.
            state.pairing[guid] = { connected: true, connectionGuid: panel.connectionGuid, connectedHost: hit.host };
            toast(`${hit.host || 'Your site'} connected`, 'success');
          }
        } catch { /* a failed poll is not worth surfacing; the next tick retries */ }
      }
      repaintConnections(m);
    }
  }, 1000);
}

async function startPairing(m) {
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/magnets/${encodeURIComponent(m.guid)}/pairing-code`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}',
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.ok) {
      state.pairing[m.guid] = { error: data.message || data.error || 'Could not generate a pairing code' };
      repaintConnections(m);
      return;
    }
    state.pairing[m.guid] = {
      code: data.pairingCode,
      connectionGuid: data.connectionGuid,
      expiresAt: new Date(data.expiresAt).getTime(),
    };
    // Refresh the list too, so the new "waiting to connect" row appears with the panel.
    try { state.connections[m.guid] = await fetchConnections(m); } catch { /* panel still renders */ }
    repaintConnections(m);
    ensurePairingTimer();
  } catch {
    state.pairing[m.guid] = { error: 'Could not reach the server. Try again.' };
    repaintConnections(m);
  }
}

async function copyPairingCode(m) {
  const code = state.pairing[m.guid]?.code;
  if (!code) return;
  try { await navigator.clipboard.writeText(code); toast('Pairing code copied', 'success'); }
  catch { toast('Copy failed — select the code and copy it manually', 'danger'); }
}

/**
 * Clear a finished connection record — the tidy-up that Disconnect deliberately is not.
 *
 * Only offered on rows that are already finished (revoked sites, codes that expired
 * unused), because the server refuses to clear anything still live. The confirmation says
 * the record goes for good and that access is unaffected, since the two are easy to
 * conflate when they sit next to each other in the same list.
 */
async function clearConnection(m, connectionGuid, label) {
  if (!window.confirm(
    `Clear the record for ${label || 'this connection'}?\n\n`
    + 'It is removed from this list for good. This only tidies up the history — the site '
    + 'is already disconnected, so nothing about access changes.',
  )) return;
  try {
    const res = await guardedFetch(
      `${apiBase()}/m5t/v5/magnets/${encodeURIComponent(m.guid)}/connections/${encodeURIComponent(connectionGuid)}`,
      { method: 'DELETE' }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.ok) { toast(data.message || data.error || 'Could not clear that record', 'danger'); return; }
    toast('Record cleared', 'success');
    // Refresh separately from the success toast: the clear already succeeded, so a failed
    // refresh must not read as a failed clear.
    try { state.connections[m.guid] = await fetchConnections(m); } catch { /* keep the prior list */ }
    repaintConnections(m);
  } catch {
    toast('Could not reach the server', 'danger');
  }
}

async function revokeConnection(m, connectionGuid) {
  const row = (state.connections[m.guid]?.connections || []).find((c) => c.guid === connectionGuid);
  const isPending = row?.status === 'PENDING';
  const what = isPending ? 'Cancel this pairing code?' : `Disconnect ${row?.host || 'this site'}?`;
  // The domain note matters: revoking a credential and un-publishing a domain are
  // separate decisions server-side, and someone clicking Disconnect will assume
  // otherwise unless told.
  const detail = isPending
    ? 'The code stops working immediately.'
    : 'Its key stops working immediately and the plugin will show as disconnected. The domain stays on the allow-list — remove it separately if you want the magnet off that site.';
  if (!window.confirm(`${what}\n\n${detail}`)) return;
  try {
    const res = await guardedFetch(
      `${apiBase()}/m5t/v5/magnets/${encodeURIComponent(m.guid)}/connections/${encodeURIComponent(connectionGuid)}/revoke`,
      { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }
    );
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.ok) { toast(data.message || data.error || 'Could not disconnect', 'danger'); return; }
    // If the open panel was for THIS connection it is now dead — drop it, rather than
    // leaving a live-looking countdown on a code that no longer works.
    if (state.pairing[m.guid]?.connectionGuid === connectionGuid) delete state.pairing[m.guid];
    toast(isPending ? 'Pairing code cancelled' : 'Site disconnected', 'success');
    try { state.connections[m.guid] = await fetchConnections(m); } catch { /* keep the prior list */ }
    repaintConnections(m);
  } catch {
    toast('Could not reach the server', 'danger');
  }
}

// ── row summary state ────────────────────────────────────────────────────────
// One word per magnet for the dot + health line. Admin status outranks traffic
// (a Draft or Disabled magnet is "not live" no matter what the beacons say);
// for ENABLED magnets the word comes from install activity, and 'unknown' just
// means that lookup hasn't landed yet — the row shows nothing rather than a
// guess, same principle as installBlockHTML.
function installStateFor(m) {
  if (m.status === 'DRAFT') return 'draft';
  if (m.status !== 'ENABLED') return 'off';
  const a = state.install[m.guid];
  if (!a) return 'unknown';
  if ((a.traffic?.opens || 0) > 0) return 'live';
  if ((a.unrecognized || []).length) return 'seen';
  return 'quiet';
}

// Status column content: a colored word a person can read at a glance, with the
// detail underneath. The dot rides inside the word so color and text always agree.
function statusCellInnerHTML(m) {
  const st = installStateFor(m);
  const a = state.install[m.guid];
  let word = '', sub = '';
  if (st === 'draft') { word = 'Draft'; sub = 'Not live yet'; }
  else if (st === 'off') { word = 'Disabled'; sub = 'Hidden from visitors'; }
  else if (st === 'live') {
    const t = a.traffic?.last_open_at ? new Date(a.traffic.last_open_at) : null;
    const sameDay = t && t.toDateString() === new Date().toDateString();
    const when = t ? (sameDay ? t.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : t.toLocaleDateString()) : '';
    word = 'Live'; sub = when ? `Last visitor ${esc(when)}` : 'Serving visitors';
  }
  else if (st === 'seen') { word = 'Needs review'; sub = 'Unrecognised domain'; }
  else if (st === 'quiet') { word = 'Not seen'; sub = `No visitors in ${Number(a.window_days || 30)} days`; }
  if (!word) return '';
  return `<div class="mag-status" data-live="${st}"><span class="mag-dot" data-live="${st}" aria-hidden="true"></span>${word}</div>
          ${sub ? `<div class="mag-cellsub">${sub}</div>` : ''}`;
}

/**
 * One row per magnet (2026-08-20 redesign). The rest state answers four things
 * only — which magnet, is it on, is it healthy, is it producing — and every
 * piece of install-time material (snippet, paste guide, CSP note, inline note,
 * install health detail, connected sites) lives in the collapsed detail panel,
 * toggled by the Install button or the health line. A magnet that has never
 * been seen auto-opens its detail once install activity loads, because that is
 * the only moment the setup material matters.
 */
function magnetCardHTML(m) {
  const showOwner = state.owners.length > 1 || AuthController.getMemberships().length > 1;
  const domains = (m.domains || []).filter(Boolean);
  const canEdit = m.can_manage === true;
  const open = state.detailOpen[m.guid] === true;
  const sub = [
    MODE_LABEL[m.display_mode] || 'Chat bubble',
    domains.length ? domains.join(', ') : 'No domains set. Add yours to go live.',
    showOwner && m.owner_name ? m.owner_name : '',
  ].filter(Boolean).join(' · ');
  return `
    <div class="mag-card${open ? ' is-open' : ''}" data-guid="${esc(m.guid)}">
      <div class="mag-line">
        <div class="mag-cell mag-cell--id">
          <div class="mag-id__line">
            <span class="mag-name" title="${esc(m.name)}">${esc(m.name)}</span>
            ${m.ab?.enabled ? '<span class="mag-ab-chip"><span class="material-symbols-outlined" aria-hidden="true">science</span>A/B</span>' : ''}
            ${(m.notify_emails || []).length ? `<a class="mag-alert-chip" href="#/settings/notifications" title="Lead alerts to ${(m.notify_emails || []).length} email${(m.notify_emails || []).length === 1 ? '' : 's'}. Manage in Settings."><span class="material-symbols-outlined" aria-hidden="true">notifications_active</span>${(m.notify_emails || []).length}</a>` : ''}
            ${m.status !== 'ENABLED' ? `<span class="mag-badge" data-s="${esc(m.status)}">${esc(m.status)}</span>` : ''}
          </div>
          <div class="mag-cellsub mag-sub" title="${esc(sub)}">${esc(sub)}</div>
        </div>
        <div class="mag-cell mag-cell--status">${statusCellInnerHTML(m)}</div>
        <div class="mag-cell mag-cell--stats">${magnetStatsHTML(m.guid)}</div>
        <div class="mag-cell mag-cell--act">
          ${canEdit ? `<button class="mag-btn mag-btn--primary" data-action="edit" data-guid="${esc(m.guid)}"><span class="material-symbols-outlined" aria-hidden="true">tune</span>Edit</button>` : ''}
          <button class="mag-btn mag-btn--icon${open ? ' is-open' : ''}" data-action="install-toggle" data-guid="${esc(m.guid)}" aria-expanded="${open}" aria-controls="mag-detail-${esc(m.guid)}" title="${open ? 'Close' : 'Open'} install and health"><span class="material-symbols-outlined" aria-hidden="true">${open ? 'expand_less' : 'expand_more'}</span></button>
          <div class="mag-more-wrap">
              <button class="mag-btn" data-action="menu" data-guid="${esc(m.guid)}" aria-haspopup="true" aria-expanded="false" title="More actions"><span class="material-symbols-outlined" aria-hidden="true">more_horiz</span></button>
              <div class="mag-menu" hidden role="menu">
                <button class="mag-menu__item" role="menuitem" data-action="preview" data-guid="${esc(m.guid)}"><span class="material-symbols-outlined" aria-hidden="true">visibility</span>Preview</button>
                ${canEdit ? `<button class="mag-menu__item" role="menuitem" data-action="experience" data-guid="${esc(m.guid)}"><span class="material-symbols-outlined" aria-hidden="true">compare</span>Experience</button>` : ''}
                <button class="mag-menu__item" role="menuitem" data-action="funnel" data-guid="${esc(m.guid)}" title="Which question loses the most visitors"><span class="material-symbols-outlined" aria-hidden="true">filter_alt</span>Funnel</button>
                ${canEdit ? `
                <button class="mag-menu__item" role="menuitem" data-action="flow" data-guid="${esc(m.guid)}" title="Edit the conversation flow"><span class="material-symbols-outlined" aria-hidden="true">account_tree</span>Flow</button>
                ${m.status === 'DRAFT' ? '' : `
                <button class="mag-menu__item" role="menuitem" data-action="toggle" data-guid="${esc(m.guid)}">${m.status === 'ENABLED'
                  ? '<span class="material-symbols-outlined" aria-hidden="true">pause</span>Disable'
                  : '<span class="material-symbols-outlined" aria-hidden="true">play_arrow</span>Enable'}</button>`}
                <button class="mag-menu__item danger" role="menuitem" data-action="delete" data-guid="${esc(m.guid)}"><span class="material-symbols-outlined" aria-hidden="true">delete</span>Delete</button>` : ''}
            </div>
          </div>
        </div>
      </div>
      <div class="mag-detail" id="mag-detail-${esc(m.guid)}" ${open ? '' : 'hidden'}>
        <div class="mag-detail__hd">
          <span class="mag-detail__k">Install</span>
          <span class="mag-detail__n" title="${esc(m.name)}">${esc(m.name)}</span>
          <button class="mag-detail__x" type="button" data-action="install-toggle" data-guid="${esc(m.guid)}" title="Close"><span class="material-symbols-outlined" aria-hidden="true">close</span></button>
        </div>
        <div class="mag-embed">
          <input type="text" readonly value="${esc(embedFor(m.guid))}" aria-label="Embed snippet">
          <button class="mag-btn" data-action="copy" data-guid="${esc(m.guid)}" title="Copy embed snippet"><span class="material-symbols-outlined" aria-hidden="true">content_copy</span></button>
        </div>
        ${installGuideHTML(m.guid)}
        ${cspNoteHTML()}
        ${m.display_mode === 'inline' ? `<div class="mag-inline-note"><span class="material-symbols-outlined" aria-hidden="true">info</span><span>Inline mode: also place <code>&lt;div data-m5m-inline&gt;&lt;/div&gt;</code> where you want it to appear. The card fills that spot up to its max width.</span></div>` : ''}
        <div class="mag-install-slot">${installBlockHTML(m)}</div>
        <div class="mag-conn-slot">${connectionsBlockHTML(m)}</div>
      </div>
    </div>`;
}

function toggleDetail(guid, force) {
  const next = typeof force === 'boolean' ? force : state.detailOpen[guid] !== true;
  state.detailOpen[guid] = next;
  const card = document.querySelector(`.mag-card[data-guid="${CSS.escape(guid)}"]`);
  if (!card) return;
  card.classList.toggle('is-open', next);
  const detail = card.querySelector('.mag-detail');
  if (detail) detail.hidden = !next;
  const btn = card.querySelector(`[data-action="install-toggle"][aria-controls]`);
  if (btn) {
    btn.setAttribute('aria-expanded', String(next));
    btn.classList.toggle('is-open', next);
    const ic = btn.querySelector('.material-symbols-outlined');
    if (ic) ic.textContent = next ? 'expand_less' : 'expand_more';
  }
}

// Bound once for the app's lifetime (routes remount, document persists): clicks
// outside the list and Escape both close any open row menu. Cheap no-ops when
// the magnets page isn't on screen.
let menuClosersBound = false;
function bindMenuClosers() {
  if (menuClosersBound) return;
  menuClosersBound = true;
  document.addEventListener('click', (e) => {
    if (!e.target.closest?.('.mag-more-wrap')) closeMenus();
  });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeMenus(); });
}

function closeMenus(except) {
  document.querySelectorAll('.mag-menu').forEach((menu) => {
    if (menu === except) return;
    menu.hidden = true;
    const b = menu.parentElement?.querySelector('[data-action="menu"]');
    if (b) b.setAttribute('aria-expanded', 'false');
  });
}

// Patch one row in place after install activity arrives (or refreshes): the
// detail slot, the dot, the health line — and the one smart default: a magnet
// nobody has ever seen auto-opens its install detail, once, unless the person
// already toggled it themselves this visit.
function applyInstallToRow(m) {
  const card = document.querySelector(`.mag-card[data-guid="${CSS.escape(m.guid)}"]`);
  if (!card) return;
  const slot = card.querySelector('.mag-install-slot');
  if (slot) slot.innerHTML = installBlockHTML(m);
  const st = installStateFor(m);
  const statusCell = card.querySelector('.mag-cell--status');
  if (statusCell) statusCell.innerHTML = statusCellInnerHTML(m);
  if ((st === 'quiet' || st === 'seen') && state.detailOpen[m.guid] === undefined) toggleDetail(m.guid, true);
}

export function render() {
  if (inPortfolio()) {
    return pickerPageHTML({
      section: 'magnets', title: 'Your Magnets',
      description: 'Pick a client to view and manage their magnets.',
    });
  }
  ensureStyles();
  return `
    <div class="page-shell" data-section="magnets">
      <div class="page-header">
        <div class="page-header-text">
          <h1 class="page-title">Your Magnets</h1>
          <p class="page-description">Conversational lead-capture widgets you embed on your site.</p>
        </div>
        <div class="mag-header-actions" style="display:flex;gap:10px;">
          <button class="btn btn-secondary" id="mag-template-btn" type="button" style="display:none">
            <span class="material-symbols-outlined" aria-hidden="true">auto_awesome</span>
            Template
          </button>
          <button class="btn btn-primary" id="mag-new-btn" type="button" style="display:none">
            <span class="material-symbols-outlined" aria-hidden="true">add</span>
            New magnet
          </button>
        </div>
      </div>
      <div class="dashboard-card">
        <div class="card-body" id="mag-body">
          ${loadingState('list', 'Loading magnets')}
        </div>
      </div>
    </div>`;
}

export async function init() {
  if (inPortfolio()) {
    // A template picked on /templates rides along into the client picker: say
    // so (peek, not take - the claim happens after a client is chosen and this
    // route re-runs in that client's context).
    const parked = peekPendingTemplate();
    if (parked) {
      const shell = document.querySelector('.page-shell .page-header-text .page-description');
      if (shell) shell.textContent = `Template picked: ${parked.name}. Choose the client to build it for.`;
    }
    const fmtN = (n) => Number(n || 0).toLocaleString();
    return initPicker(apiBase(), [
      { h: 'Magnets', td: (r) => `${fmtN(r.magnets.active)}<span style="color:var(--color-text-subtle,#6C7C9A)">/${fmtN(r.magnets.total)}</span>` },
      { h: 'Leads · wk', td: (r) => fmtN(r.leads.this_week) },
      { h: 'Leads · total', td: (r) => fmtN(r.leads.total) },
    ], { emptyMsg: 'No client orgs yet. Create one under Clients, then provision its magnets here.' });
  }
  calEventTypes = null; // re-list on each visit so a fresh Calendly connect shows up
  ccEventTypes = null;  // same for Cal.com
  mbPages = null;       // same for Microsoft Bookings
  crTrackers = null;    // same for CallRail tracking numbers
  document.getElementById('mag-new-btn')?.addEventListener('click', startNewMagnet);
  document.getElementById('mag-template-btn')?.addEventListener('click', startFromTemplate);
  const body = document.getElementById('mag-body');
  body?.addEventListener('click', onGridClick);
  bindMenuClosers();
  await load();

  // Arrived here from the /templates browse page. Claimed after load() because
  // useTemplate() reads state.owners, which load() is what populates. Silently
  // ignored without provisioning rights — the same guard that hides both buttons.
  const pending = takePendingTemplate();
  if (pending && state.canProvision) useTemplate(pending);
}

/**
 * Paint the magnet grid into `host`, capped like every other list.
 *
 * This markup was written out identically in three places (initial load, the post-save
 * repaint, and the modal-close repaint), so any change to how the grid renders had to be
 * made three times or it drifted. One function now, which is also the only way the cap
 * stays consistent across all three.
 */
function paintGrid(host) {
  if (!host) return;
  host.innerHTML = `<div class="mag-grid">
    <div class="mag-head" aria-hidden="true"><span>Magnet</span><span>Status</span><span>Last 30 days</span><span></span></div>
    ${state.magnets.map(magnetCardHTML).join('')}</div>`;
  capList(host.querySelector('.mag-grid'), { rowSelector: '.mag-card', noun: 'magnets' });
}

async function load() {
  const body = document.getElementById('mag-body');
  if (!body) return;
  try {
    const [res, sres] = await Promise.all([
      guardedFetch(`${apiBase()}/m5t/v5/magnets`),
      guardedFetch(`${apiBase()}/m5t/v5/magnets/stats`).catch(() => null),
    ]);
    const data = await res.json().catch(() => ({}));
    // res owns this slot; sres is trailing-30-day stats decoration and is already
    // read defensively below, so a denial on stats must not blank the magnet grid.
    failIfNotOk(res, data);
    if (!data.ok) throw new Error(data.message || 'Failed to load');
    state.magnets = data.magnets || [];
    state.owners = data.owners || [];
    state.canProvision = !!data.canProvision;
    state.canManage = !!data.canManage;
    state.primaryOrgGuid = data.primaryOrgGuid || null;
    const sdata = sres ? await sres.json().catch(() => ({})) : {};
    state.stats = sdata?.stats || {};
  } catch (err) {
    body.innerHTML = isDenial(err) ? denialState() : emptyState('error', 'Could not load your magnets. Please try again.');
    return;
  }

  const newBtn = document.getElementById('mag-new-btn');
  if (newBtn) newBtn.style.display = state.canProvision ? '' : 'none';
  const tplBtn = document.getElementById('mag-template-btn');
  if (tplBtn) tplBtn.style.display = state.canProvision ? '' : 'none';

  if (!state.magnets.length) {
    body.innerHTML = emptyState('widgets', state.canProvision
      ? 'Design your first magnet. Start from a template, or click “New magnet” and build from scratch.'
      : 'No magnets yet.');
    return;
  }
  paintGrid(body);
  // Not awaited: the grid is already usable, and each card patches itself in.
  loadInstallActivity();
  loadConnections();
}

// Logo upload and the Flow tab both need a real guid to attach to — they used to just
// tell a brand-new magnet "save this first, then reopen it here." Now "New magnet"
// provisions a real (DRAFT-status, not yet live/embeddable) row immediately, so both
// tabs work right away; the modal opens in the same "editing" mode as any other magnet.
// DRAFT never serves publicly (getEnabledMagnet requires status='ENABLED'), and an
// abandoned draft is left in place rather than cleaned up — it's a legitimate,
// resumable state per the schema's own DRAFT convention, reachable again via Edit.
async function provisionDraft(ownerGuid) {
  const headers = { 'Content-Type': 'application/json', 'Idempotency-Key': crypto.randomUUID() };
  const body = { status: 'DRAFT' };
  if (ownerGuid) body.owner_guid = ownerGuid;
  const res = await guardedFetch(`${apiBase()}/m5t/v5/magnets`, { method: 'POST', headers, body: JSON.stringify(body) });
  let data = {};
  try { data = await res.json(); } catch { /* fall through to res.ok */ }
  if (!res.ok || !data.ok) throw new Error(data.message || `Could not start a new magnet (${res.status})`);
  return data.magnet;
}

// Apply the selected template's appearance and flows in one definition save.
// New API drafts supply a revision; older API responses retain the legacy PATCH
// contract. The draft remains unpublished and is reloaded before opening Edit.
async function applyTemplate(magnet, template) {
  const guid = magnet.guid;
  const res = await guardedFetch(`${apiBase()}/m5t/v5/magnets/${encodeURIComponent(guid)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...templateChanges(template),
      ...(magnet.revision ? { save_version: 1, expected_revision: magnet.revision } : {}) }),
  });
  let data = {};
  try { data = await res.json(); } catch { /* fall through to res.ok */ }
  if (!res.ok || !data.ok) throw new Error(data.message || `Could not apply the ${template.name} template (${res.status})`);
}

async function provisionAndOpen(ownerGuid, template) {
  const newBtn = document.getElementById('mag-new-btn');
  const tplBtn = document.getElementById('mag-template-btn');
  if (newBtn) newBtn.disabled = true;
  if (tplBtn) tplBtn.disabled = true;
  // Set the instant provisionDraft() resolves — from that point on, the DRAFT
  // exists server-side, and it's openModal()'s own freshDraft/doClose() that
  // becomes responsible for cleaning it up on Cancel. If anything between here
  // and a successful openModal() call throws (applyTemplate's PATCH, the
  // post-template load() refetch), that hand-off never happens, so the catch
  // below deletes it directly — otherwise it's an orphaned DRAFT row with no
  // UI trace at all, not even a cancelable card in the grid.
  let provisionedGuid = null;
  try {
    let magnet = await provisionDraft(ownerGuid);
    provisionedGuid = magnet.guid;
    if (template) {
      await applyTemplate(magnet, template);
      await load(); // refetch so the modal opens against the template's real, persisted steps
      magnet = state.magnets.find((m) => m.guid === magnet.guid) || magnet;
    } else {
      state.magnets = [magnet, ...state.magnets];
      // Reflect it in the grid right away. A normal Cancel/close cleans this row up
      // (openModal's doClose(), via freshDraft below) before the user would ever see it
      // here — this mainly covers the tab-closed/crashed-before-closing edge case, where
      // no JS ever runs to do that cleanup and the draft is genuinely still there.
      const body = document.getElementById('mag-body');
      paintGrid(body);
    }
    openMagnetEditor(magnet, undefined, { freshDraft: true });
  } catch (err) {
    if (provisionedGuid) {
      // guardedFetch, not fetch — for the INTERCEPTOR, not for staleness. Nothing reads
      // this response, which is why it was left unguarded during the navigation-guard
      // adoption, and that reasoning answered the wrong question. A magnets DELETE is
      // capability-gated and CAN 403, and a 403 through the patched window.fetch signs
      // the user out. Bypassing the interceptor is about which fetch you call, not
      // about whether you read the answer. (Writes are never aborted, so the rollback
      // still completes if the user navigates away.)
      guardedFetch(`${apiBase()}/m5t/v5/magnets/${encodeURIComponent(provisionedGuid)}`, { method: 'DELETE' }).catch(() => {});
    }
    toast(err.message || 'Could not start a new magnet', 'danger');
  } finally {
    if (newBtn) newBtn.disabled = false;
    if (tplBtn) tplBtn.disabled = false;
  }
}

// owner_guid can't be changed after creation (PATCH /:guid doesn't touch it), so a
// reseller managing multiple client orgs still has to pick the owner up front —
// same requirement the old in-modal picker enforced, just asked one step earlier
// so the draft lands under the right org from the start.
function openOwnerPickerModal(template) {
  const scrim = document.createElement('div');
  scrim.className = 'mag-modal-scrim';
  scrim.innerHTML = `
    <div class="mag-modal" role="dialog" aria-modal="true" aria-label="Choose client" style="max-width:420px">
      <div class="mag-modal__head">
        <span class="mag-modal__title">${template ? `New magnet from “${esc(template.name)}”` : 'New magnet'}</span>
        <button class="mag-modal__close" type="button" aria-label="Close">&times;</button>
      </div>
      <div class="mag-modal__body" style="padding:20px">
        <label class="mag-modal__label" for="mag-owner-pick">Client / workspace</label>
        <select class="mag-modal__select" id="mag-owner-pick">
          ${state.owners.map((o) => `<option value="${esc(o.guid)}"${o.guid === state.primaryOrgGuid ? ' selected' : ''}>${esc(o.name || o.guid)}</option>`).join('')}
        </select>
        <p class="mag-modal__hint">Which client is this magnet for? You can't change this later.</p>
      </div>
      <div class="mag-modal__foot">
        <div class="mag-modal__foot-btns">
          <button class="mag-modal__btn mag-modal__btn--ghost" type="button" data-x="cancel">Cancel</button>
          <button class="mag-modal__btn mag-modal__btn--primary" type="button" data-x="continue">Continue</button>
        </div>
      </div>
    </div>`;
  document.body.appendChild(scrim);
  requestAnimationFrame(() => { scrim.classList.add('open'); scrim.querySelector('.mag-modal').classList.add('open'); });
  const doClose = () => { scrim.classList.remove('open'); setTimeout(() => scrim.remove(), 180); document.removeEventListener('keydown', onKey); };
  function onKey(e) { if (e.key === 'Escape') { e.preventDefault(); doClose(); } }
  document.addEventListener('keydown', onKey);
  scrim.querySelector('.mag-modal__close').addEventListener('click', doClose);
  scrim.querySelector('[data-x="cancel"]').addEventListener('click', doClose);
  scrim.querySelector('[data-x="continue"]').addEventListener('click', () => {
    const guid = scrim.querySelector('#mag-owner-pick').value;
    doClose();
    provisionAndOpen(guid, template);
  });
}

/**
 * The single org this caller may provision for, when there is exactly one.
 *
 * Sent EXPLICITLY rather than letting the server fall back to primaryOrgGuid, because
 * the fallback is the ACTIVE org and the active org is not necessarily one you may
 * provision for. A reseller whose active org is a managed_client has exactly one
 * provisionable org — their own reseller org — and an active org that is not it; the
 * server default would aim the create at the read-only org and 403. `owners` is already
 * filtered by canForOwner server-side, so anything in it is safe to send.
 */
function soleProvisionableOwner() {
  return state.owners.length === 1 ? state.owners[0].guid : null;
}

function startNewMagnet() {
  if (editorWorkspaceEnabled()) { window.router.navigate('/magnets/new'); return; }
  if (state.owners.length > 1) { openOwnerPickerModal(); return; }
  provisionAndOpen(soleProvisionableOwner());
}

// The one place a chosen template becomes a real draft. Shared by the in-page picker
// and by the /templates browse page's hand-off, so both take the identical path
// (including the owner picker, which a multi-org reseller still needs either way).
function useTemplate(template) {
  if (editorWorkspaceEnabled()) { setPendingTemplate(template.id); window.router.navigate('/magnets/new'); return; }
  if (state.owners.length > 1) { openOwnerPickerModal(template); return; }
  provisionAndOpen(soleProvisionableOwner(), template);
}

function startFromTemplate() {
  openTemplateGallery(useTemplate);
}

function onGridClick(e) {
  const btn = e.target.closest('[data-action]');
  if (!btn) {
    closeMenus();
    // The row itself is the disclosure affordance: a click anywhere on the line
    // that is not a button or link toggles that magnet's install panel.
    if (!e.target.closest('a')) {
      const line = e.target.closest('.mag-line');
      const card = line?.closest('.mag-card');
      if (card?.dataset.guid) toggleDetail(card.dataset.guid);
    }
    return;
  }
  const guid = btn.dataset.guid;
  const m = state.magnets.find((x) => x.guid === guid);
  const action = btn.dataset.action;
  if (action === 'menu') {
    const menu = btn.parentElement?.querySelector('.mag-menu');
    if (!menu) return;
    const opening = menu.hidden;
    closeMenus(menu);
    menu.hidden = !opening;
    btn.setAttribute('aria-expanded', String(opening));
    return;
  }
  closeMenus();
  if (action === 'install-toggle') return toggleDetail(guid);
  if (action === 'copy') return copyEmbed(guid);
  if (!m) return;
  if (action === 'preview') return openPreviewModal(() => magnetToPackage(m), { installationGuid: m.guid });
  if (action === 'experience') return openPreviewModal(() => magnetToPackage(m), { installationGuid: m.guid, manageExperience: true, onChanged: () => load() });
  if (action === 'funnel') return openFunnelModal(m);
  if (action === 'edit') return openMagnetEditor(m);
  if (action === 'flow') return openMagnetEditor(m, 'flow');
  if (action === 'allow-domain') return allowDomain(m, btn.dataset.domain);
  if (action === 'dismiss-domain') return dismissDomain(m, btn.dataset.domain);
  if (action === 'restore-domain') return restoreDomain(m, btn.dataset.domain);
  if (action === 'pair-start') return startPairing(m);
  if (action === 'copy-code') return copyPairingCode(m);
  if (action === 'revoke-connection') return revokeConnection(m, btn.dataset.conn);
  if (action === 'clear-connection') return clearConnection(m, btn.dataset.conn, btn.dataset.label);
  if (action === 'toggle') return toggleStatus(m);
  if (action === 'delete') return removeMagnet(m);
}

async function copyEmbed(guid) {
  try { await navigator.clipboard.writeText(embedFor(guid)); toast('Embed snippet copied', 'success'); }
  catch { toast('Copy failed', 'danger'); }
}

// Which QUESTION loses the most visitors — distinct from the open/engage/complete
// funnel elsewhere: step position numbers only mean something within one magnet's
// own step order, so this is always scoped to a single magnet, fetched on demand
// rather than preloaded for every card in the grid.
export function stepFunnelHTML(data) {
  const total = Number(data?.total_steps) || 0;
  const byPos = {};
  (Array.isArray(data?.funnel) ? data.funnel : []).forEach((r) => { byPos[r.position] = Number(r.reached) || 0; });
  if (!total || !Object.keys(byPos).length) {
    return `<p class="mag-modal__hint" style="margin:0">No step activity yet in this window. As visitors answer questions, drop-off by step will appear here.</p>`;
  }
  const max = Math.max(1, byPos[1] || 0);
  const rows = [];
  for (let p = 1; p <= total; p++) {
    const n = byPos[p] || 0;
    const pct = Math.min(100, Math.round((n / max) * 100));
    rows.push(`
      <div class="mag-funnel__row">
        <div class="mag-funnel__meta">
          <span class="mag-funnel__label">Step ${p} of ${total}</span>
          <span class="mag-funnel__val">${n.toLocaleString()} <span class="mag-funnel__pct">${pct}%</span></span>
        </div>
        <div class="mag-funnel__track"><div class="mag-funnel__bar${p === 1 ? '' : ' mag-funnel__bar--drop'}" style="width:${pct}%"></div></div>
      </div>`);
  }
  return `<div class="mag-funnel">${rows.join('')}</div>`;
}

async function openFunnelModal(m) {
  const scrim = document.createElement('div');
  scrim.className = 'mag-modal-scrim';
  scrim.innerHTML = `
    <div class="mag-modal mag-funnel-modal" role="dialog" aria-modal="true" aria-label="Step funnel for ${esc(m.name)}">
      <div class="mag-modal__head">
        <span class="mag-modal__title">Step funnel — ${esc(m.name)}</span>
        <button class="mag-modal__close" type="button" aria-label="Close">&times;</button>
      </div>
      <div class="mag-modal__body" style="padding:20px">
        <p class="mag-modal__hint" style="margin-top:0">How many visitors reached each question, last 30 days. A big drop between two steps is where people bail.</p>
        <div id="mag-funnel-body">${emptyState('hourglass_empty', 'Loading…')}</div>
      </div>
    </div>`;
  document.body.appendChild(scrim);
  requestAnimationFrame(() => { scrim.classList.add('open'); scrim.querySelector('.mag-modal').classList.add('open'); });
  const close = () => { scrim.classList.remove('open'); setTimeout(() => scrim.remove(), 180); document.removeEventListener('keydown', onKey); };
  function onKey(e) { if (e.key === 'Escape') { e.preventDefault(); close(); } }
  document.addEventListener('keydown', onKey);
  scrim.querySelector('.mag-modal__close').addEventListener('click', close);
  scrim.addEventListener('click', (e) => { if (e.target === scrim) close(); });

  const bodyEl = scrim.querySelector('#mag-funnel-body');
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/magnets/${encodeURIComponent(m.guid)}/step-funnel`);
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data.ok) throw new Error(data.message || `Failed (${res.status})`);
    bodyEl.innerHTML = stepFunnelHTML(data);
  } catch (err) {
    bodyEl.innerHTML = `<p class="mag-modal__hint" style="margin:0">Could not load the funnel. Please try again.</p>`;
  }
}

// Turn an OBSERVATION into an allow-list entry — the one write in this feature, and
// it is the existing PATCH, made by a member with MANAGE_MAGNETS. Nothing about the
// suggestion grants anything on its own; a human confirming is the whole gate.
//
// Sends the current ACTIVE domains plus the new one, because PATCH replaces the list
// wholesale (and marks everything it receives active) — sending only the new domain
// would silently drop the customer's existing ones, and echoing back deactivated
// entries would silently revive them.
async function allowDomain(m, domain) {
  const add = String(domain || '').trim().toLowerCase();
  if (!add) return;
  if (!window.confirm(`Allow ${add} to load this magnet?\n\nOnly do this for a site you recognise — anyone can put this magnet's embed code on a page.`)) return;
  try {
    await approveMagnetDomain(m.guid, add);
    toast(`${add} allowed`, 'success');
  } catch (error) { toast(error.message || 'Could not allow that domain', 'danger'); return; }
  try { await load(); } catch (e) { console.warn('[magnets] refresh after allow-domain failed:', e); }
}

// Dismiss — the third outcome for a "seen requesting" entry, alongside Allow and
// leaving it alone. It changes what this list SHOWS and nothing else: the domain is
// still refused by the gate exactly as before.
//
// Dismissing is dot-anchored server-side, so offering the registrable parent is the
// difference between suppressing one preview URL and suppressing the pattern that
// keeps minting them.
async function dismissDomain(m, domain) {
  const host = String(domain || '').trim().toLowerCase();
  if (!host) return;
  const target = await chooseHiddenDomain(host);
  if (!target) return;

  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/magnets/${encodeURIComponent(m.guid)}/install-activity/dismiss`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ domain: target }),
    });
    if (!(await res.json().catch(() => ({}))).ok) throw new Error();
    toast(`${target} hidden from this list`, 'success');
  } catch { toast('Could not dismiss that domain', 'danger'); return; }
  await refreshInstallActivity(m);
}

async function restoreDomain(m, domain) {
  const host = String(domain || '').trim().toLowerCase();
  if (!host) return;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/magnets/${encodeURIComponent(m.guid)}/install-activity/restore`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ domain: host }),
    });
    if (!(await res.json().catch(() => ({}))).ok) throw new Error();
    toast(`${host} will show again`, 'success');
  } catch { toast('Could not restore that domain', 'danger'); return; }
  await refreshInstallActivity(m);
}

/** Re-read one card's install activity — cheaper and less jarring than reloading the grid. */
async function refreshInstallActivity(m) {
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/magnets/${encodeURIComponent(m.guid)}/install-activity`);
    const data = await res.json().catch(() => ({}));
    if (!data.ok) return;
    state.install[m.guid] = data;
    applyInstallToRow(m);
  } catch (err) { console.warn('[magnets] install refresh failed:', err); }
}

async function toggleStatus(m) {
  // Defense in depth: the toggle button itself is omitted for DRAFT cards (Edit → Save
  // is the only publish path, since that's the only place name/domains/flow get
  // validated), but guard here too in case this is ever reached some other way — an
  // unvalidated DRAFT going straight to ENABLED is a real "empty magnet goes live" risk,
  // not a cosmetic one.
  if (m.status === 'DRAFT') { toast('Open Edit and Save to publish a draft magnet.', 'info'); return; }
  const next = m.status === 'ENABLED' ? 'DISABLED' : 'ENABLED';
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/magnets/${encodeURIComponent(m.guid)}`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: next }),
    });
    if (!(await res.json().catch(() => ({}))).ok) throw new Error();
    toast(next === 'ENABLED' ? 'Magnet enabled' : 'Magnet disabled', 'success');
  } catch { toast('Update failed', 'danger'); return; }
  // Separate from the try above on purpose: a list-refresh failure here must not
  // also fire the 'danger' toast on top of the 'success' one that already fired
  // for the (successful) status toggle itself.
  try { await load(); } catch (e) { console.warn('[magnets] refresh after status toggle failed:', e); }
}

async function removeMagnet(m) {
  if (!window.confirm(`Delete “${m.name}”? Visitors will no longer see this magnet.`)) return;
  try {
    const res = await guardedFetch(`${apiBase()}/m5t/v5/magnets/${encodeURIComponent(m.guid)}`, { method: 'DELETE' });
    if (!(await res.json().catch(() => ({}))).ok) throw new Error();
    toast('Magnet deleted', 'success');
  } catch { toast('Delete failed', 'danger'); return; }
  // Separate from the try above on purpose: a list-refresh failure here must not
  // also fire the 'danger' toast on top of the 'success' one that already fired
  // for the (successful) delete itself.
  try { await load(); } catch (e) { console.warn('[magnets] refresh after delete failed:', e); }
}

// Read the current configurator form into bare appearance fields (shared by Save + Preview).
function collectAppearance(scrim) {
  const actions = [];
  scrim.querySelectorAll('.mag-cta').forEach((row) => {
    if (!row.querySelector('.mag-cta__on').checked) return;
    const type = row.dataset.type;
    const kind = row.dataset.kind;
    const valEl = row.querySelector('.mag-cta__val');
    const value = valEl ? valEl.value.trim() : '';
    if (kind === 'value') {
      if (type === 'call' && row.querySelector('.mag-cta__callsrc')?.value === 'callrail') {
        const sel = row.querySelector('.mag-cta__crtracker');
        const number = sel?.value || '';
        const opt = sel?.selectedOptions?.[0];
        if (number) {
          actions.push({ type, value: number, config: {
            source: 'callrail',
            ...(opt?.dataset?.trackerId ? { tracker_id: opt.dataset.trackerId } : {}),
            ...(opt?.dataset?.trackerName ? { tracker_name: opt.dataset.trackerName } : {}),
          } });
        }
      } else if (value) actions.push({ type, value });          // call/email need a target
    } else if (kind === 'book') {
      const booksrc = row.querySelector('.mag-cta__booksrc')?.value;
      if (booksrc === 'calendly') {
        const sel = row.querySelector('.mag-cta__calevent');
        const url = sel?.value || '';
        const name = (sel?.selectedOptions?.[0]?.textContent || '').trim();
        actions.push({ type, config: { source: 'calendly', ...(url ? { calendly_url: url } : {}), ...(url && name ? { event_name: name } : {}) } });
      } else if (booksrc === 'calcom' || booksrc === 'ms_bookings') {
        // The page URL rides `value` (the in-magnet page embed / fallback).
        // For Microsoft Bookings, a picked SERVICE adds business_id +
        // service_id and the SDK runs its own scheduler against the real
        // calendar instead (native booking via magnetPublic).
        const sel = row.querySelector(booksrc === 'calcom' ? '.mag-cta__ccevent' : '.mag-cta__mbevent');
        const url = sel?.value || '';
        const name = (sel?.selectedOptions?.[0]?.textContent || '').trim();
        const cfg = { source: booksrc, ...(url && name ? { event_name: name } : {}) };
        if (booksrc === 'ms_bookings') {
          const bizId = sel?.selectedOptions?.[0]?.dataset?.id || '';
          const svcSel = row.querySelector('.mag-cta__mbservice');
          const svcId = svcSel?.value || '';
          if (bizId && svcId) {
            cfg.business_id = bizId;
            cfg.service_id = svcId;
            const svcName = (svcSel.selectedOptions?.[0]?.textContent || '').replace(/ \(.*$/, '').trim();
            if (svcName) cfg.event_name = svcName;
          }
        }
        actions.push({ type, ...(url ? { value: url } : {}), config: cfg });
      } else {
        const a = { type };
        if (value) { if (/^https?:\/\//i.test(value)) a.value = value; else a.config = { meeting_label: value }; }
        actions.push(a);                                        // blank → built-in in-panel scheduler
      }
    } else if (kind === 'support') {
      const options = (row.querySelector('.mag-cta__opts')?.value || '')
        .split('\n').map((s) => s.trim()).filter(Boolean).map((label) => ({ label }));
      const a = { type };
      if (options.length) a.config = { options };
      actions.push(a);
    } else {
      actions.push({ type });                                   // chat → routes to the flow
    }
  });
  const payload = {
    display_mode: scrim.querySelector('input[name="mag-display"]:checked')?.value || 'chat',
    trigger: collectTrigger(scrim),
    theme_mode: scrim.querySelector('input[name="mag-theme"]:checked')?.value || 'auto',
    agent_tools: scrim.querySelector('#mag-f-agent-tools')?.value || 'none',
    title: scrim.querySelector('#mag-f-title').value.trim(),
    theme_color: scrim.querySelector('#mag-f-color').value,
    subtitle: scrim.querySelector('#mag-f-subtitle').value,          // '' legitimately hides the line
    launcher_label: scrim.querySelector('#mag-f-launcher').value.trim(),
    ...Object.fromEntries(['visual_style','motion_style','message_style'].map(key => [key, scrim.querySelector(`[data-visual-field="${key}"]`)?.value || ''])),
    presentation_style: scrim.querySelector('#mag-f-presentation')?.value || 'conversation',
    launcher_style: scrim.querySelector('#mag-f-launcher-style')?.value || 'label',
    launcher_hint: scrim.querySelector('#mag-f-launcher-hint')?.value.trim() || '',
    visitor_controls: scrim.querySelector('#mag-f-visitor-controls')?.checked === true,
    home_title: scrim.querySelector('#mag-f-home-title')?.value.trim() || '',
    logo_url: scrim.querySelector('#mag-f-logo').value.trim(),
    logo_fit: scrim.querySelector('#mag-f-logo-fit')?.value === 'cover' ? 'cover' : 'contain',
    logo_scale: Number(scrim.querySelector('#mag-f-logo-scale')?.value || 100),
    summary: scrim.querySelector('#mag-f-summary').checked,
    summary_heading: scrim.querySelector('#mag-f-summary-heading').value.trim(),
    home: scrim.querySelector('#mag-f-home').checked,
    welcome: scrim.querySelector('#mag-f-welcome').value,            // '' legitimately clears it
    ab: collectAB(scrim),
    actions,
    // Brand theming + inline sizing + post-submit + misc (all optional; empty clears server-side).
    font: scrim.querySelector('#mag-f-font').value.trim(),
    theme_vars: collectThemeVars(scrim),                            // {} when the toggle is off → cleared
    ...(() => {
      // Preset choice resolves to the same two stored values Custom edits directly.
      const preset = scrim.querySelector('input[name="mag-size-preset"]:checked')?.value || 'standard';
      const v = inlineSizeValues(preset);
      if (v) return { inline_width: v.w, inline_height: v.h };
      return {
        inline_width: scrim.querySelector('#mag-f-inline-w').value.trim(),
        inline_height: scrim.querySelector('#mag-f-inline-h').value.trim(),
      };
    })(),
    hide_launcher: scrim.querySelector('#mag-f-hide-launcher').checked,
    welcome_experience: collectWelcomeExperience(scrim),
    legal_notice: collectLegalNotice(scrim),
    consent: collectConsent(scrim),
    input_placeholder: scrim.querySelector('#mag-f-input-ph').value,
    summary_confirm_label: scrim.querySelector('#mag-f-summary-confirm').value.trim(),
    success_title: scrim.querySelector('#mag-f-success-title').value.trim(),
    success_text: scrim.querySelector('#mag-f-success-text').value,  // '' legitimately clears it
    success_cta: collectSuccessCta(scrim),                          // {} clears server-side
  };
  // collectTrigger returns undefined only when it cannot read the control at all; the
  // API writes trigger only when present, so omitting it preserves whatever is stored
  // rather than flattening it. That was the ONLY behaviour before this control existed,
  // and it stays as the fallback.
  if (payload.trigger === undefined) delete payload.trigger;
  return payload;
}

// ── Logo upload (drag-and-drop) ─────────────────────────────────────────────
// Downscales + re-encodes client-side before upload — visitors only ever see this
// at small avatar size, so there's no reason to ship a multi-MB phone photo as-is.
// Uploads to the magnet's sprite_imagery (server-side storage, not inline in the
// package — see magnets.js's PUT /:guid/logo comment for why) and, on success,
// points the existing "Logo URL" field at the URL that serves it back out; the
// rest of Save/collectAppearance is unchanged, it just reads that field's value.
const LOGO_MAX_DIM = 512;
const LOGO_ACCEPT = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp']);
const LOGO_MAX_SOURCE_BYTES = 8 * 1024 * 1024; // pre-downscale cap; the canvas re-encode does the real shrinking

function fileToDownscaledDataUri(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('Could not read that file.'));
    reader.onload = () => {
      img.onerror = () => reject(new Error('That file isn’t a readable image.'));
      img.onload = () => {
        const scale = Math.min(1, LOGO_MAX_DIM / Math.max(img.naturalWidth, img.naturalHeight));
        const w = Math.max(1, Math.round(img.naturalWidth * scale));
        const h = Math.max(1, Math.round(img.naturalHeight * scale));
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        // Re-encoded as PNG regardless of source: keeps transparency (most logos need
        // it), and GIF/WEBP animation can't survive a canvas round-trip anyway.
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

function wireLogoUpload(scrim, magnet, staging = null) {
  const drop = scrim.querySelector('#mag-logo-drop');
  if (!drop) return; // brand-new (unsaved) magnet — no guid to upload against yet
  const fileInput = scrim.querySelector('#mag-f-logo-file');
  const browseBtn = scrim.querySelector('#mag-logo-browse');
  const removeBtn = scrim.querySelector('#mag-logo-remove');
  const preview = scrim.querySelector('#mag-logo-preview');
  const statusEl = scrim.querySelector('#mag-logo-status');
  const urlInput = scrim.querySelector('#mag-f-logo');

  const setStatus = (msg, isError) => {
    statusEl.textContent = msg;
    statusEl.style.color = isError ? 'var(--color-danger, #DD4124)' : '';
  };
  const setPreview = (url) => {
    preview.innerHTML = url ? `<img src="${esc(url)}" alt="">` : `<span class="material-symbols-outlined" aria-hidden="true">image</span>`;
    removeBtn.hidden = !url;
  };
  // Fires real bubbling input/change events on the existing URL field rather than
  // reimplementing dirty-tracking / live-preview refresh — main's delegated
  // listeners (below, in openModal) already do both correctly for this field.
  const touchUrlField = (value) => {
    urlInput.value = value;
    urlInput.dispatchEvent(new Event('input', { bubbles: true }));
    urlInput.dispatchEvent(new Event('change', { bubbles: true }));
  };

  async function upload(file) {
    if (!LOGO_ACCEPT.has(file.type)) { setStatus('PNG, JPEG, GIF, or WEBP only.', true); return; }
    if (file.size > LOGO_MAX_SOURCE_BYTES) { setStatus('That file is too large (8MB max before resizing).', true); return; }
    staging?.busy(true);
    setStatus(staging ? 'Preparing logo…' : 'Uploading…', false);
    try {
      const dataUri = await fileToDownscaledDataUri(file);
      if (staging) {
        staging.stage(dataUri); touchUrlField(dataUri); setPreview(dataUri);
        setStatus('Logo ready. It will change when you publish or save.', false);
        return;
      }
      const res = await guardedFetch(`${apiBase()}/m5t/v5/magnets/${encodeURIComponent(magnet.guid)}/logo`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ image: dataUri }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || `Upload failed (${res.status})`);
      // Cache-bust: same magnet guid → same URL, so a re-upload needs a fresh query
      // param or the browser (and any CDN in front of the API) would keep serving
      // the old bytes at that URL.
      const url = `${apiBase()}/m5t/v5/magnet/${encodeURIComponent(magnet.guid)}/logo?v=${Date.now()}`;
      touchUrlField(url);
      setPreview(url);
      setStatus('Logo updated.', false);
    } catch (err) {
      setStatus(err.message || 'Upload failed.', true);
    } finally { staging?.busy(false); }
  }

  fileInput.addEventListener('change', () => { if (fileInput.files[0]) upload(fileInput.files[0]); fileInput.value = ''; });
  // browseBtn is inside drop, so its click also bubbles to drop's own listener below —
  // excluded here the same way removeBtn is, so opening the file picker isn't triggered twice.
  drop.addEventListener('click', (e) => {
    if (e.target === removeBtn || removeBtn.contains(e.target)) return;
    if (e.target === browseBtn) return;
    fileInput.click();
  });
  browseBtn.addEventListener('click', () => fileInput.click());
  ['dragenter', 'dragover'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.add('is-dragover'); }));
  ['dragleave', 'drop'].forEach((ev) => drop.addEventListener(ev, (e) => { e.preventDefault(); drop.classList.remove('is-dragover'); }));
  drop.addEventListener('drop', (e) => { const f = e.dataTransfer?.files?.[0]; if (f) upload(f); });

  removeBtn.addEventListener('click', async (e) => {
    e.stopPropagation();
    if (staging) {
      staging.stage(null); touchUrlField(''); setPreview('');
      setStatus('Logo removal is pending until you publish or save.', false); return;
    }
    setStatus('Removing…', false);
    try {
      const res = await guardedFetch(`${apiBase()}/m5t/v5/magnets/${encodeURIComponent(magnet.guid)}/logo`, { method: 'DELETE' });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || `Remove failed (${res.status})`);
      touchUrlField('');
      setPreview('');
      setStatus('Logo removed.', false);
    } catch (err) {
      setStatus(err.message || 'Remove failed.', true);
    }
  });
}

// Preserve authored tokens that a color picker cannot represent (CSS color
// functions, omitted defaults, and corner radii). Opening and saving another
// field must not silently turn a partial palette into a different full palette.
const themeVarDrafts = new WeakMap();
export function collectThemeVars(scrim) {
  return scrim.querySelector('#mag-f-surface-on')?.checked ? collectThemeVarDraft(scrim) : {};
}
function collectThemeVarDraft(scrim) {
  const vars = { ...themeVarDrafts.get(scrim) };
  for (const { key, id } of MAGNET_THEME_COLOR_FIELDS) {
    const input = scrim.querySelector(`#mag-f-sv-${id}`);
    if (input && (input.dataset.touched === 'true' || input.value !== input.dataset.initialValue)) vars[key] = input.value;
  }
  return vars;
}

function wireAppearancePresets(scrim, initialVars, onChange, onPreview, remember) {
  const toggle = scrim.querySelector('#mag-f-surface-on');
  const grid = scrim.querySelector('#mag-f-surface');
  const font = scrim.querySelector('#mag-f-font');
  const status = scrim.querySelector('#mag-style-status');
  const mode = () => scrim.querySelector('input[name="mag-theme"]:checked')?.value || 'auto';
  let paletteMode = mode() === 'dark' ? 'dark' : 'light';
  const setMode = (value) => { scrim.querySelector(`input[name="mag-theme"][value="${value}"]`).checked = true; };
  const setVars = (vars) => {
    themeVarDrafts.set(scrim, { ...vars });
    const defaults = magnetAppearancePreset('clean', paletteMode).theme_vars;
    for (const { key, id } of MAGNET_THEME_COLOR_FIELDS) {
      const input = scrim.querySelector(`#mag-f-sv-${id}`);
      input.value = /^#[0-9a-fA-F]{6}$/.test(vars[key] || '') ? vars[key] : defaults[key];
      input.dataset.initialValue = input.value;
      delete input.dataset.touched;
      input.title = vars[key] || '';
    }
  };
  const sync = () => {
    const selected = toggle.checked ? matchMagnetAppearancePreset(font.value, collectThemeVars(scrim), scrim.querySelector('[data-visual-field=visual_style]')?.value || '') : '';
    scrim.querySelectorAll('[data-appearance-preset]').forEach((button) => {
      button.setAttribute('aria-pressed', String(button.dataset.appearancePreset === selected));
      const preset = MAGNET_APPEARANCE_PRESETS.find((p) => p.id === button.dataset.appearancePreset);
      button.style.setProperty('--mag-style-surface', preset[paletteMode].surface_2);
      button.style.setProperty('--mag-style-text', preset[paletteMode].text);
    });
    grid.hidden = !toggle.checked;
    const name = MAGNET_APPEARANCE_PRESETS.find((p) => p.id === selected)?.name;
    status.textContent = name ? `${name} · ${paletteMode === 'dark' ? 'Dark' : 'Light'} palette`
      : toggle.checked ? 'Custom palette. These colors stay fixed until you change them.'
        : 'Adaptive surfaces. Auto and site sync follow the selected theme; your font and brand color stay in place.';
  };
  const applyPreset = (id) => {
    const preset = magnetAppearancePreset(id, paletteMode);
    if (!preset) return;
    font.value = preset.font;
    toggle.checked = true;
    setMode(preset.theme_mode);
    setVars(preset.theme_vars);
    sync();
  };
  setVars(initialVars);
  sync();
  scrim.querySelectorAll('input[name="mag-theme"]').forEach((radio) => radio.addEventListener('change', () => {
    const selected = toggle.checked ? matchMagnetAppearancePreset(font.value, collectThemeVars(scrim), scrim.querySelector('[data-visual-field=visual_style]')?.value || '') : '';
    if (mode() === 'auto' || mode() === 'sync') {
      // Fixed theme_vars would override both palettes and silently defeat Auto.
      toggle.checked = false;
    } else {
      paletteMode = mode();
      if (selected) applyPreset(selected);
    }
    sync();
  }));
  toggle.addEventListener('change', () => {
    if (toggle.checked) {
      const selected = matchMagnetAppearancePreset(font.value, collectThemeVars(scrim), scrim.querySelector('[data-visual-field=visual_style]')?.value || '');
      setMode(paletteMode);
      // Auto/site sync retain the draft for later customization. If the customer
      // changed Light/Dark meanwhile, restore the preset's matching palette.
      if (selected) setVars(magnetAppearancePreset(selected, paletteMode).theme_vars);
      else if (!Object.keys(themeVarDrafts.get(scrim) || {}).length) setVars(magnetAppearancePreset('clean', paletteMode).theme_vars);
    }
    sync();
  });
  font.addEventListener('change', sync);
  grid.querySelectorAll('input[type="color"]').forEach((input) => input.addEventListener('input', () => { input.dataset.touched = 'true'; sync(); onChange(); }));
  return mountStyleGallery(scrim, {
    read: () => ({ visual_style: scrim.querySelector('[data-visual-field=visual_style]')?.value || '', font: font.value, theme_mode: mode(), theme_vars: collectThemeVars(scrim), theme_color: scrim.querySelector('#mag-f-color').value,
      surface_enabled: toggle.checked, surface_draft: collectThemeVarDraft(scrim), palette_mode: paletteMode }),
    write: value => {
      const visual = scrim.querySelector('[data-visual-field=visual_style]'); if (visual) visual.value = value.visual_style || '';
      font.value = value.font; setMode(value.theme_mode); paletteMode = value.palette_mode || (value.theme_mode === 'dark' ? 'dark' : 'light');
      toggle.checked = value.surface_enabled ?? Object.keys(value.theme_vars || {}).length > 0;
      setVars(value.surface_draft || value.theme_vars || {});
      scrim.querySelector('#mag-f-color').value = value.theme_color;
      sync();
    },
    onApply: onChange, onPreview, remember,
  });
}

// Success-card deep link → { label, url } only when both are set, else {} (server clears).
function collectSuccessCta(scrim) {
  const label = scrim.querySelector('#mag-f-success-cta-label').value.trim();
  const url = scrim.querySelector('#mag-f-success-cta-url').value.trim();
  return (label && url) ? { label, url } : {};
}

// A/B section → { enabled, split, b:{ title?, welcome?, theme_color? } }. B's color only counts
// as an override when it differs from A's (the picker has to show something).
function collectAB(scrim) {
  const b = {};
  const title = scrim.querySelector('#mag-f-ab-title')?.value.trim();
  const welcome = scrim.querySelector('#mag-f-ab-welcome')?.value.trim();
  const colorPicker = scrim.querySelector('#mag-f-ab-color');
  const color = colorPicker?.value || '';
  const colorEdited = color.toLowerCase() !== (colorPicker?.defaultValue || '').toLowerCase();
  const aColor = scrim.querySelector('#mag-f-color')?.value || '';
  if (title) b.title = title;
  if (welcome) b.welcome = welcome;
  if (color && (colorPicker?.dataset.savedOverride === 'true' || (colorEdited && color.toLowerCase() !== aColor.toLowerCase()))) b.theme_color = color;
  return {
    enabled: !!scrim.querySelector('#mag-f-ab')?.checked,
    split: Number(scrim.querySelector('#mag-f-ab-split')?.value) || 50,
    b,
  };
}

// Font dropdown. Web-font values MUST match the SDK's GOOGLE_FONTS allowlist (first family,
// lowercased) so ensureWebFont() actually loads them; system fonts always render. The "Web
// fonts" group's key set is cross-checked against @sse/shared's WEB_FONT_ALLOWLIST by
// magnet-vocab-consistency.test.js — kept as an independent literal (not derived) since its
// CSS-stack quoting and "(serif)" label suffix are presentation details worth hand-authoring,
// not worth risking a subtle mismatch by reverse-deriving them.
export const FONT_GROUPS = [
  { label: 'Web fonts (loaded for you)', opts: [
    ['Inter, sans-serif', 'Inter'],
    ['Poppins, sans-serif', 'Poppins'],
    ['Montserrat, sans-serif', 'Montserrat'],
    ['Roboto, sans-serif', 'Roboto'],
    ['"Open Sans", sans-serif', 'Open Sans'],
    ['Lato, sans-serif', 'Lato'],
    ['Nunito, sans-serif', 'Nunito'],
    ['Raleway, sans-serif', 'Raleway'],
    ['"Work Sans", sans-serif', 'Work Sans'],
    ['Oswald, sans-serif', 'Oswald'],
    ['Merriweather, serif', 'Merriweather (serif)'],
  ] },
  { label: 'System / always available', opts: [
    ['system-ui, sans-serif', 'System UI'],
    ['Georgia, serif', 'Georgia (serif)'],
    ['"Times New Roman", Times, serif', 'Times (serif)'],
    ['Arial, Helvetica, sans-serif', 'Arial / Helvetica'],
    ['Verdana, Geneva, sans-serif', 'Verdana'],
    ['"Courier New", monospace', 'Courier (mono)'],
  ] },
];
function fontOptionsHTML(current) {
  const cur = String(current || '');
  const known = new Set(FONT_GROUPS.flatMap((g) => g.opts.map(([v]) => v)));
  const sel = (v) => (v === cur ? ' selected' : '');
  let html = `<option value=""${cur === '' ? ' selected' : ''}>System default</option>`;
  // Preserve a saved custom stack that isn't one of the presets.
  if (cur && !known.has(cur)) html += `<option value="${esc(cur)}" selected>Current: ${esc(cur)}</option>`;
  for (const g of FONT_GROUPS) {
    html += `<optgroup label="${esc(g.label)}">` +
      g.opts.map(([v, l]) => `<option value="${esc(v)}"${sel(v)}>${esc(l)}</option>`).join('') +
      '</optgroup>';
  }
  return html;
}

// ── create / edit modal ──────────────────────────────────────────────────────
function openMagnetEditor(magnet, tab, opts = {}) {
  if (!editorWorkspaceEnabled()) return openModal(magnet, tab, opts);
  if (opts.freshDraft) rememberEditorDraft(magnet.guid);
  window.router.navigate(editorPath(magnet.guid));
}

export function openModal(magnet, initialTab, opts = {}) {
  ensureStyles();
  collapseMagnetNavigation();
  const workspace = !!opts.host;
  // Direct editor routes do not run the list's init(). Never reuse another
  // workspace's provider choices when mounting a new working definition.
  if (workspace) { calEventTypes = null; ccEventTypes = null; mbPages = null; crTrackers = null; }
  let saving = false;
  let sessionPaused = false, saveEpoch = 0;
  let pendingLogo;
  let shopifyFinderPanel, pendingFinder, finderPreviewSteps, finderPreviewConfig;
  const finderPatch = () => pendingFinder ? { product_finder: pendingFinder } : {};
  let preparingLogo = false;
  // freshDraft: this exact magnet was just auto-provisioned by THIS "New Magnet"
  // click (see provisionAndOpen) — not an existing draft the user opened via Edit to
  // resume. Only a freshDraft gets deleted if the user leaves without saving; an
  // existing draft from a prior session is left alone on cancel, same as it always was.
  const { freshDraft = false } = opts;
  const editing = !!magnet;
  // One key per create attempt (this modal instance), unchanged across retries of
  // the same Save click — lets the server recognize "this exact attempt already
  // succeeded" instead of provisioning a second magnet on a false-failure retry,
  // rapid double-click, or back-button resubmit. Not sent on edits: PATCH updates
  // an existing row by guid, so a repeat there re-applies the same update, not a
  // duplicate resource.
  const createIdemKey = editing ? null : crypto.randomUUID();
  const showOwnerPicker = !editing && state.owners.length > 1;
  const dm = magnet?.display_mode || 'chat';
  const tm = magnet?.theme_mode || 'auto';
  const actionByType = {};
  (magnet?.actions || []).forEach((a) => { if (a && a.type) actionByType[a.type] = a; });
  // New magnets default to the welcome+CTA hub; edits respect the saved choice.
  const homeOn = editing ? !!magnet.home : true;
  // New magnets start from a friendly default subtitle; edits respect a saved blank (hidden).
  const subtitleVal = editing ? (magnet.subtitle != null ? magnet.subtitle : '') : 'Typically replies in a few minutes';
  // Existing custom palettes are preserved until the customer edits them.
  const tv = (magnet?.theme_vars && typeof magnet.theme_vars === 'object') ? magnet.theme_vars : {};
  const svOn = Object.keys(tv).length > 0;
  const linkedShop = magnet?.product_finder?.config?.source?.shop;
  const shopifyStore = /^[a-z0-9][a-z0-9-]*\.myshopify\.com$/.test(linkedShop || '') ? linkedShop : '';
  const scrim = document.createElement('div');
  scrim.className = 'mag-modal-scrim';
  if (magnet.owner_guid) scrim.dataset.calendarOwnerGuid = magnet.owner_guid;
  scrim.innerHTML = `
    <div class="mag-modal" role="dialog" aria-modal="true" aria-label="${editing ? 'Edit magnet' : 'New magnet'}">
      <div class="mag-modal__head">
        <span class="mag-modal__title">${editing ? 'Edit magnet' : 'New magnet'}</span>
        <button class="mag-modal__close" type="button" aria-label="Close">&times;</button>
      </div>
      <div class="mag-modal__main">
        <nav class="mag-modal__nav" role="tablist" aria-label="Magnet settings">
          <button type="button" class="mag-modal__navitem is-active" data-tab="basics"><span class="material-symbols-outlined" aria-hidden="true">tune</span>Basics</button>
          <button type="button" class="mag-modal__navitem" data-tab="appearance"><span class="material-symbols-outlined" aria-hidden="true">palette</span>Appearance</button>
          <button type="button" class="mag-modal__navitem" data-tab="welcome"><span class="material-symbols-outlined" aria-hidden="true">forum</span>Welcome &amp; CTAs</button>
          <button type="button" class="mag-modal__navitem" data-tab="flow"><span class="material-symbols-outlined" aria-hidden="true">account_tree</span>Flow</button>
          <button type="button" class="mag-modal__navitem" data-tab="finish"><span class="material-symbols-outlined" aria-hidden="true">task_alt</span>Finish</button>
          <button type="button" class="mag-modal__navitem" data-tab="ab"><span class="material-symbols-outlined" aria-hidden="true">science</span>A/B test</button>
        </nav>
        <div class="mag-modal__body">
          <section class="mag-tab" data-tab="basics">
        <label class="mag-modal__label" for="mag-f-name">Name</label>
        <input class="mag-modal__input" id="mag-f-name" type="text" placeholder="e.g. Homepage quote magnet" value="${esc(magnet?.name || '')}">

        ${showOwnerPicker ? `
        <label class="mag-modal__label" for="mag-f-owner">Client / workspace</label>
        <select class="mag-modal__select" id="mag-f-owner">
          ${state.owners.map((o) => `<option value="${esc(o.guid)}"${o.guid === state.primaryOrgGuid ? ' selected' : ''}>${esc(o.name || o.guid)}</option>`).join('')}
        </select>` : ''}

        <label class="mag-modal__label">Display mode</label>
        ${segHTML('mag-display', dm, DISPLAY_OPTS)}
        <p class="mag-modal__hint">${shopifyStore ? 'Inline appears where you add the Mach Five Magnet block in your Shopify theme.' : 'Inline appears inside your page. Bubble, drawer and popup float over it.'}</p>
        ${shopifyStore ? `<div class="mag-modal__sec"><label class="mag-modal__label">Connected Shopify store</label><p class="mag-modal__hint" style="overflow-wrap:anywhere">${esc(shopifyStore)}</p><a href="#/magnets/${encodeURIComponent(magnet.guid)}/website" data-close-modal>Set up in Shopify →</a></div>` : ''}

        <div data-auto-open-settings>
        <label class="mag-modal__label" for="mag-f-trigger">Auto-open</label>
        <select class="mag-modal__select" id="mag-f-trigger">
          ${TRIGGER_OPTS.map((o) => `<option value="${o.value}"${triggerSelection(magnet) === o.value ? ' selected' : ''}>${esc(o.label)}</option>`).join('')}
          <option value="__per_device"${triggerSelection(magnet) === '__per_device' ? ' selected' : ''}>Per device&hellip;</option>
        </select>
        <div class="mag-perdev" id="mag-f-perdev"${triggerSelection(magnet) === '__per_device' ? '' : ' hidden'}>
          <div class="mag-perdev__row">
            <label class="mag-modal__label" for="mag-f-trigger-desktop">Desktop</label>
            <select class="mag-modal__select" id="mag-f-trigger-desktop">
              ${TRIGGER_OPTS.map((o) => `<option value="${o.value}"${perDeviceSide(magnet, 'desktop') === o.value ? ' selected' : ''}>${esc(o.label)}</option>`).join('')}
            </select>
          </div>
          <div class="mag-perdev__row">
            <label class="mag-modal__label" for="mag-f-trigger-mobile">Mobile</label>
            <select class="mag-modal__select" id="mag-f-trigger-mobile">
              ${TRIGGER_OPTS.map((o) => `<option value="${o.value}"${perDeviceSide(magnet, 'mobile') === o.value ? ' selected' : ''}>${esc(o.label)}</option>`).join('')}
            </select>
          </div>
          <p class="mag-modal__hint">Mobile means a viewport under ${MOBILE_BREAKPOINT_PX}px — the width at which the panel goes near-fullscreen, which is why an auto-open that suits desktop can be too much on a phone.</p>
        </div>
        <p class="mag-modal__hint">Open automatically once per visit, unless the visitor has already opened the magnet.</p>
        </div>

        ${shopifyStore ? '<details class="mag-modal__sec"><summary>Storefront domains</summary>' : ''}
        <label class="mag-modal__label" for="mag-f-domains">Allowed domain(s)</label>
        <input class="mag-modal__input" id="mag-f-domains" type="text" placeholder="example.com, www.example.com" value="${esc((magnet?.domains || []).join(', '))}">
        <p class="mag-modal__hint">${shopifyStore ? 'Only approved storefront domains can load this guide.' : 'Comma-separated. The magnet only loads on these domains.'}</p>
        ${shopifyStore ? '</details>' : ''}

        <details class="mag-modal__sec"><summary>AI assistant access</summary>
          <label class="mag-modal__label" for="mag-f-agent-tools">Allow AI assistants to</label>
          <select class="mag-modal__select" id="mag-f-agent-tools" aria-describedby="mag-agent-tools-help">
            ${MAGNET_AGENT_TOOLS.map(option => `<option value="${option.value}"${option.value === readMagnetAgentTools(magnet?.agent_tools ?? magnet?.definition?.mag_agent_tools) ? ' selected' : ''}>${option.label}</option>`).join('')}
          </select>
          <p class="mag-modal__hint" id="mag-agent-tools-help">Read only lets an assistant describe this magnet; Open and prefill also lets it start and fill in answers, but the visitor always submits.</p>
        </details>

        ${magnet ? `
        <label class="mag-modal__label">Lead alerts</label>
        <p class="mag-modal__hint" style="margin-top:0">
          <span class="material-symbols-outlined" aria-hidden="true" style="font-size:15px;vertical-align:-3px;color:${(magnet.notify_emails || []).length ? 'var(--color-success, #009473)' : 'var(--color-text-subtle, #6C7C9A)'}">${(magnet.notify_emails || []).length ? 'notifications_active' : 'notifications_off'}</span>
          ${(magnet.notify_emails || []).length
            ? `On for ${(magnet.notify_emails || []).length} email${(magnet.notify_emails || []).length === 1 ? '' : 's'}.`
            : 'Off.'}
          <a href="#/settings/notifications" data-close-modal>Manage in Settings</a>
        </p>` : ''}
          </section>

          <section class="mag-tab" data-tab="appearance" hidden>
        <div class="mag-modal__sec">
          <p class="mag-modal__sec-t">Appearance</p>
          <div class="mag-modal__row">
            <div>
              <label class="mag-modal__label" for="mag-f-title">Header title</label>
              <input class="mag-modal__input" id="mag-f-title" type="text" placeholder="How can we help?" value="${esc(magnet?.title || '')}">
            </div>
            <div style="flex:0 0 auto">
              <label class="mag-modal__label" for="mag-f-color">Color</label>
              <input class="mag-modal__color" id="mag-f-color" type="color" value="${esc(/^#[0-9a-fA-F]{6}$/.test(magnet?.theme_color || '') ? magnet.theme_color : '#0072BA')}">
            </div>
          </div>

          ${welcomeExperienceEditorHTML(magnet?.welcome_experience || magnet?.definition?.mag_welcome_experience, magnet?.assistant_authoring?.welcome_experience_available === true)}
          <div id="mag-assistant-design"${magnet?.assistant_authoring?.available === true ? '' : ' hidden'}>
            <label class="mag-modal__label" for="mag-f-presentation">Conversation layout</label>
            <select class="mag-modal__select" id="mag-f-presentation">${PRESENTATION_STYLES.map(option => `<option value="${option.value}"${(magnet?.presentation_style || 'conversation') === option.value ? ' selected' : ''}>${option.value === 'assistant' ? 'Guided conversation' : 'Classic conversation'}</option>`).join('')}</select>
            <p class="mag-modal__hint">Choose the look here. Arrange questions together in Conversation.</p>
            <div id="mag-launcher-options">
              <label class="mag-modal__label" for="mag-f-launcher-style">Floating invitation</label>
              <select class="mag-modal__select" id="mag-f-launcher-style">${LAUNCHER_STYLES.map(option => `<option value="${option.value}"${(magnet?.launcher_style || 'label') === option.value ? ' selected' : ''}>${option.label}</option>`).join('')}</select>
              <div id="mag-launcher-hint-field"><label class="mag-modal__label" for="mag-f-launcher-hint">Supporting line</label><input class="mag-modal__input" id="mag-f-launcher-hint" maxlength="160" placeholder="Let’s find your starting point" value="${esc(magnet?.launcher_hint || '')}"></div>
              <div class="mag-tog-row" id="mag-visitor-controls-field"><input type="checkbox" id="mag-f-visitor-controls"${magnet?.visitor_controls ? ' checked' : ''}><label for="mag-f-visitor-controls">Let visitors resize and move the panel</label></div>
            </div>
          </div>

          <label class="mag-modal__label" for="mag-f-subtitle">Subtitle</label>
          <input class="mag-modal__input" id="mag-f-subtitle" type="text" placeholder="Typically replies in a few minutes" value="${esc(subtitleVal)}">
          <p class="mag-modal__hint">Small line under the title. Leave blank to hide it.</p>

          <label class="mag-modal__label" for="mag-f-launcher">Launcher label</label>
          <input class="mag-modal__input" id="mag-f-launcher" type="text" placeholder="Defaults to the title" value="${esc(magnet?.launcher_label || '')}">
          <p class="mag-modal__hint">Text on the floating button (Chat / Slide / Modal only).</p>

          <label class="mag-modal__label">Theme</label>
          ${segHTML('mag-theme', tm, THEME_OPTS)}
          <p class="mag-modal__hint">Light and Dark keep a fixed palette. Auto and site sync restore adaptive surfaces.</p>

          ${styleGalleryHTML({ visualDesignAvailable: magnet?.assistant_authoring?.visual_design_available === true })}
          <div id="mag-visual-design"${magnet?.assistant_authoring?.visual_design_available === true ? '' : ' hidden'}>
            ${[['visual_style','Frame & typography',MAGNET_VISUAL_STYLES],['message_style','Message bubbles',MAGNET_MESSAGE_STYLES],['motion_style','Motion',MAGNET_MOTION_STYLES]].map(([key,label,options]) => `<label class="mag-modal__label" for="mag-f-${key}">${label}</label><select class="mag-modal__select" id="mag-f-${key}" data-visual-field="${key}">${options.map(option => `<option value="${option.value}"${(magnet?.[key] || '') === option.value ? ' selected' : ''}>${option.label}</option>`).join('')}</select>`).join('')}
            <p class="mag-modal__hint">Motion follows the visitor’s reduced-motion preference.</p>
          </div>

          <label class="mag-modal__label">Logo</label>
          ${editing ? `
          <div class="mag-logo-drop" id="mag-logo-drop">
            <input type="file" id="mag-f-logo-file" accept="image/png,image/jpeg,image/gif,image/webp" hidden>
            <div class="mag-logo-drop__preview" id="mag-logo-preview">${magnet?.logo_url ? `<img src="${esc(magnet.logo_url)}" alt="">` : `<span class="material-symbols-outlined" aria-hidden="true">image</span>`}</div>
            <div class="mag-logo-drop__body">
              <p class="mag-logo-drop__t">Drag an image here, or <button type="button" class="mag-logo-drop__browse" id="mag-logo-browse">browse</button></p>
              <p class="mag-modal__hint" style="margin:2px 0 0">PNG, JPEG, GIF, or WEBP — resized automatically.</p>
            </div>
            <button type="button" class="mag-logo-drop__remove" id="mag-logo-remove"${magnet?.logo_url ? '' : ' hidden'} title="Remove logo"><span class="material-symbols-outlined" aria-hidden="true">delete</span></button>
          </div>
          <p class="mag-modal__hint" id="mag-logo-status" role="status" style="min-height:1em"></p>
          ` : `<p class="mag-modal__hint" style="margin-top:0">Save this magnet first, then reopen it here to upload a logo.</p>`}

          <label class="mag-modal__label" for="mag-f-logo">Or paste a logo URL</label>
          <input class="mag-modal__input" id="mag-f-logo" type="text" placeholder="https://…/logo.png" value="${esc(magnet?.logo_url || '')}">
          <p class="mag-modal__hint">Shown in the header and on bot avatars. Falls back to a color mark.</p>

          <label class="mag-modal__label" for="mag-f-logo-fit">Logo fit</label>
          <select class="mag-modal__select" id="mag-f-logo-fit">
            <option value="cover"${magnet?.logo_fit === 'cover' ? ' selected' : ''}>Cover: fills the logo area (may crop)</option>
            <option value="contain"${magnet?.logo_fit !== 'cover' ? ' selected' : ''}>Contain: shows the whole logo</option>
          </select>
          <p class="mag-modal__hint">Use “Contain” for a wide wordmark so it isn’t cropped.</p>

          <div id="mag-logo-sizing" class="mag-logo-sizing">
            <label class="mag-modal__label" for="mag-f-logo-scale">Header logo size</label>
            <div class="mag-logo-sizing__controls">
              <input id="mag-f-logo-scale" type="range" min="50" max="150" step="5" value="${Number.isInteger(magnet?.logo_scale) && magnet.logo_scale >= 50 && magnet.logo_scale <= 150 ? magnet.logo_scale : 100}" aria-describedby="mag-logo-sizing-hint">
              <output for="mag-f-logo-scale" id="mag-logo-scale-value"></output>
              <button type="button" class="mag-modal__btn" id="mag-logo-scale-reset">Reset</button>
            </div>
            <p class="mag-modal__hint" id="mag-logo-sizing-hint">Adjust the header logo. Conversation avatars stay compact.</p>
          </div>

          <details class="mag-style-customize"><summary>Customize typography &amp; palette</summary>
          <label class="mag-modal__label" for="mag-f-font">Font family</label>
          <select class="mag-modal__select" id="mag-f-font">${fontOptionsHTML(magnet?.font || '')}</select>
          <p class="mag-modal__hint">Choose the type used throughout your magnet. Web fonts load automatically; system fonts are available immediately.</p>

          <div class="mag-tog-row" style="margin-top:12px">
            <input type="checkbox" id="mag-f-surface-on"${svOn ? ' checked' : ''}>
            <label for="mag-f-surface-on">Customize this palette</label>
          </div>
          <div id="mag-f-surface" class="mag-surface-grid"${svOn ? '' : ' hidden'}>
            ${MAGNET_THEME_COLOR_FIELDS.map(({ id, label }) => `<div><label class="mag-modal__label" for="mag-f-sv-${id}">${label}</label><input class="mag-modal__color" id="mag-f-sv-${id}" type="color"></div>`).join('')}
          </div>
          <p class="mag-modal__hint">Customize the card, conversation, questions, and text together. Turn this off to use the built-in theme surfaces.</p>

          </details>

          <div id="mag-f-inline-size"${dm === 'inline' ? '' : ' hidden'}>
            <label class="mag-modal__label">Inline size</label>
            <div class="mag-size-grid" role="radiogroup" aria-label="Inline size">
              ${(() => {
                const cur = matchInlineSizePreset(magnet?.inline_width, magnet?.inline_height);
                const card = (id, name, hint, tw, th) => `
                  <label class="mag-size-card${cur === id ? ' is-on' : ''}">
                    <input type="radio" name="mag-size-preset" value="${id}"${cur === id ? ' checked' : ''}>
                    <span class="mag-size-thumb" aria-hidden="true"><i style="width:${tw}px;height:${th}px"></i></span>
                    <span class="mag-size-name">${name}</span>
                    <span class="mag-size-hint">${hint}</span>
                  </label>`;
                return INLINE_SIZE_PRESETS.map((pz) => card(pz.id, pz.name, pz.hint, PRESET_THUMBS[pz.id].w, PRESET_THUMBS[pz.id].h)).join('')
                  + card('custom', 'Custom', 'Your own CSS sizes', 44, 32);
              })()}
            </div>
            <p class="mag-modal__hint">The card grows with its content by default. A set height keeps its size steady and scrolls longer questions. Where you place <code>&lt;div data-m5m-inline&gt;&lt;/div&gt;</code> decides the space.</p>
            <div id="mag-f-inline-custom"${matchInlineSizePreset(magnet?.inline_width, magnet?.inline_height) === 'custom' ? '' : ' hidden'}>
              <div class="mag-modal__row">
                <div>
                  <label class="mag-modal__label" for="mag-f-inline-w">Max width</label>
                  <input class="mag-modal__input" id="mag-f-inline-w" type="text" placeholder="640px" value="${esc(magnet?.inline_width || '')}">
                </div>
                <div>
                  <label class="mag-modal__label" for="mag-f-inline-h">Height</label>
                  <input class="mag-modal__input" id="mag-f-inline-h" type="text" placeholder="Fit content (default)" value="${esc(magnet?.inline_height || '')}">
                </div>
              </div>
              <p class="mag-modal__hint">A CSS length (<code>760px</code>, <code>80vh</code>) or <code>min()/max()/clamp()</code>. Blank = default size. Height can't be a percentage.</p>
              <p class="mag-size-err" id="mag-f-inline-err"></p>
            </div>
          </div>

          <div class="mag-tog-row" style="margin-top:12px">
            <input type="checkbox" id="mag-f-hide-launcher"${magnet?.hide_launcher ? ' checked' : ''}>
            <label for="mag-f-hide-launcher">Hide the floating button (opens only from your own button or link)</label>
          </div>
          <p class="mag-modal__hint">For Chat / Slide / Modal: no bubble appears; you open it by calling <code>MachFiveMagnet.open()</code> from your page. Pair with Auto-open = “Manual”.</p>

          <label class="mag-modal__label" for="mag-f-input-ph">Input placeholder</label>
          <input class="mag-modal__input" id="mag-f-input-ph" type="text" placeholder="Uses a hint for each question type" value="${esc(magnet?.input_placeholder || '')}">
          ${legalNoticeEditorHTML(magnet?.legal_notice || magnet?.definition?.mag_legal_notice, !editing || magnet?.assistant_authoring?.legal_notice_available === true)}
          ${consentEditorHTML(magnet?.consent || magnet?.definition?.mag_consent, magnet?.assistant_authoring?.consent_available === true)}
        </div>
          </section>

          <section class="mag-tab" data-tab="welcome" hidden>
        <div class="mag-modal__sec">
          <p class="mag-modal__sec-t">Welcome screen</p>
          <div class="mag-tog-row">
            <input type="checkbox" id="mag-f-home"${homeOn ? ' checked' : ''}>
            <label for="mag-f-home">Open on a welcome message and task choices</label>
          </div>
          <label class="mag-modal__label" for="mag-f-home-title">Welcome heading</label>
          <input class="mag-modal__input" id="mag-f-home-title" maxlength="160" placeholder="How can we help?" value="${esc(magnet?.home_title || '')}">
          <label class="mag-modal__label" for="mag-f-welcome">Welcome message</label>
          <textarea class="mag-modal__input" id="mag-f-welcome" rows="2" placeholder="Welcome! Pick an option below and we’ll take it from there.">${esc(magnet?.welcome || '')}</textarea>
          <p class="mag-modal__hint">Shown first when the magnet opens. Visitors choose a task from the welcome screen.</p>
        </div>

        <div class="mag-modal__sec">
          <p class="mag-modal__sec-t">Call-to-action bar</p>
          <p class="mag-modal__hint" style="margin-top:0">The persistent buttons a visitor taps to pick a channel. Book opens an in-panel scheduler, or your connected scheduling tool (Calendly, Cal.com, Microsoft Bookings). Call can use a CallRail tracking number when connected, so calls attribute to this magnet. Chat runs your flow, Support is a multi-select intake. Leave unchecked to hide.</p>
          ${CTA_ORDER.map((t) => ctaRowHTML(t, actionByType[t])).join('')}
        </div>
          </section>

          <section class="mag-tab" data-tab="flow" hidden>
            <div id="mag-flow-panel"></div>
          </section>

          <section class="mag-tab" data-tab="ab" hidden>
        <div class="mag-modal__sec">
          <p class="mag-modal__sec-t">A/B experiment</p>
          <div class="mag-tog-row">
            <input type="checkbox" id="mag-f-ab"${magnet?.ab?.enabled ? ' checked' : ''}>
            <label for="mag-f-ab">Run an A/B test on this magnet</label>
          </div>
          <p class="mag-modal__hint">Variant A is what you configured above. Fill in what variant B changes. Visitors are split between them and results appear on <b>A/B Testing</b>.</p>
          <div class="mag-modal__row">
            <div>
              <label class="mag-modal__label" for="mag-f-ab-title">B: Header title</label>
              <input class="mag-modal__input" id="mag-f-ab-title" type="text" placeholder="(unchanged)" value="${esc(magnet?.ab?.b?.mag_title || '')}">
            </div>
            <div style="flex:0 0 auto">
              <label class="mag-modal__label" for="mag-f-ab-color">B: Color</label>
              <input class="mag-modal__color" id="mag-f-ab-color" type="color" data-saved-override="${!!magnet?.ab?.b?.mag_theme_color}" value="${esc(/^#[0-9a-fA-F]{6}$/.test(magnet?.ab?.b?.mag_theme_color || '') ? magnet.ab.b.mag_theme_color : (magnet?.theme_color || '#0072BA'))}">
            </div>
          </div>
          <label class="mag-modal__label" for="mag-f-ab-welcome">B: Welcome message</label>
          <textarea class="mag-modal__input" id="mag-f-ab-welcome" rows="2" placeholder="(unchanged)">${esc(magnet?.ab?.b?.mag_welcome || '')}</textarea>
          <label class="mag-modal__label" for="mag-f-ab-split">Traffic to variant B</label>
          <select class="mag-modal__select" id="mag-f-ab-split">
            ${[25, 50, 75].map((n) => `<option value="${n}"${(magnet?.ab?.split || 50) === n ? ' selected' : ''}>${n}% see B / ${100 - n}% see A</option>`).join('')}
          </select>
        </div>
          </section>

          <section class="mag-tab" data-tab="finish" hidden>
        <div class="mag-modal__sec">
          <p class="mag-modal__sec-t">Summary card</p>
          <div class="mag-tog-row">
            <input type="checkbox" id="mag-f-summary"${magnet?.summary ? ' checked' : ''}>
            <label for="mag-f-summary">Show a recap card when the visitor finishes</label>
          </div>
          <label class="mag-modal__label" for="mag-f-summary-heading">Recap heading</label>
          <input class="mag-modal__input" id="mag-f-summary-heading" type="text" placeholder="Here’s what we’ve got" value="${esc(magnet?.summary_heading || '')}">
          <label class="mag-modal__label" for="mag-f-summary-confirm">Confirm button label</label>
          <input class="mag-modal__input" id="mag-f-summary-confirm" type="text" placeholder="Send request" value="${esc(magnet?.summary_confirm_label || '')}">
        </div>

        <div class="mag-modal__sec">
          <p class="mag-modal__sec-t">After submit</p>
          <p class="mag-modal__hint" style="margin-top:0">The card shown once the visitor finishes. Blank = the default “You’re all set!” message.</p>
          <label class="mag-modal__label" for="mag-f-success-title">Success title</label>
          <input class="mag-modal__input" id="mag-f-success-title" type="text" placeholder="You’re all set!" value="${esc(magnet?.success_title || '')}">
          <label class="mag-modal__label" for="mag-f-success-text">Success message</label>
          <textarea class="mag-modal__input" id="mag-f-success-text" rows="2" placeholder="Your details have been sent to the team. They will follow up using the contact information you provided.">${esc(magnet?.success_text ?? 'Your details have been sent to the team. They will follow up using the contact information you provided.')}</textarea>
          <div class="mag-modal__row">
            <div>
              <label class="mag-modal__label" for="mag-f-success-cta-label">Button label (optional)</label>
              <input class="mag-modal__input" id="mag-f-success-cta-label" type="text" placeholder="e.g. Visit our site" value="${esc(magnet?.success_cta?.label || '')}">
            </div>
            <div>
              <label class="mag-modal__label" for="mag-f-success-cta-url">Button link</label>
              <input class="mag-modal__input" id="mag-f-success-cta-url" type="text" placeholder="https://…" value="${esc(magnet?.success_cta?.url || '')}">
            </div>
          </div>
          <p class="mag-modal__hint">A deep link shown after submit (e.g. “Play now”). Needs both a label and a URL.</p>
        </div>
          </section>
        </div>
        <aside class="mag-modal__preview" id="mag-preview-column" aria-label="Live preview"><div id="mag-preview-pane" style="height:100%"></div></aside>
      </div>
      <div class="mag-modal__foot">
        ${editing ? '<button class="mag-modal__btn mag-modal__btn--preview" type="button" data-x="preview" aria-haspopup="dialog"><span class="material-symbols-outlined" aria-hidden="true" style="font-size:18px;vertical-align:-4px;margin-right:4px">fullscreen</span>Full Screen Preview</button>' : ''}
        <span class="mag-modal__dirty" id="mag-f-dirty" hidden><span class="material-symbols-outlined" aria-hidden="true">fiber_manual_record</span>Unsaved changes</span>
        <p class="mag-modal__error" id="mag-f-error"></p>
        <div class="mag-modal__foot-btns">
          <button class="mag-modal__btn mag-modal__btn--ghost" type="button" data-x="cancel">Cancel</button>
          <button class="mag-modal__btn mag-modal__btn--primary" type="button" data-x="save">${editing ? (magnet.status === 'DRAFT' ? 'Publish magnet' : 'Save') : 'Create magnet'}</button>
        </div>
      </div>
    </div>`;
  (opts.host || document.body).appendChild(scrim);
  if (workspace) {
    const dialog = scrim.querySelector('.mag-modal');
    dialog.removeAttribute('aria-modal');
    dialog.setAttribute('role', 'region');
    dialog.setAttribute('aria-label', 'Magnet editor');
    scrim.querySelector('.mag-modal__head').innerHTML = `
      <div class="mag-editor-identity"><button type="button" class="mag-editor-back" data-x="back">← Back to magnets</button>
      <h1>${esc(magnet.name || 'Untitled magnet')}</h1>
      <p>${esc(magnet.owner_name || 'Your workspace')} · ${magnet.status === 'ENABLED' ? 'Published' : magnet.status === 'DRAFT' ? 'Draft' : 'Disabled'}</p></div>
      <span data-save-state role="status">No unsaved changes</span>`;
    const nav = scrim.querySelector('.mag-modal__nav');
    nav.setAttribute('role', 'navigation'); nav.setAttribute('aria-label', 'Editor sections');
    const labels = { flow: 'Conversation', appearance: 'Design', welcome: 'Welcome & options', finish: 'Visitor confirmation', basics: 'Website & setup', ab: 'A/B experiment' };
    for (const key of ['flow', 'appearance', 'welcome', 'finish', 'basics', 'ab']) {
      const button = nav.querySelector(`[data-tab="${key}"]`);
      button.lastChild.textContent = labels[key];
      nav.appendChild(button);
    }
    const note = document.createElement('span'); note.className = 'mag-editor-save-note';
    note.textContent = magnet.status === 'ENABLED' ? 'Publishing updates this magnet on connected websites.' : magnet.status === 'DRAFT' ? 'Publish when your magnet is ready for visitors.' : 'Saving keeps this magnet disabled.';
    scrim.querySelector('.mag-modal__foot').insertBefore(note, scrim.querySelector('.mag-modal__foot-btns'));
    if (magnet.status === 'ENABLED') scrim.querySelector('[data-x="save"]').textContent = 'Publish changes';
    if (magnet.status === 'DISABLED') scrim.querySelector('[data-x="save"]').textContent = 'Save changes';
    if (magnet.status === 'DRAFT') {
      const draftSave = document.createElement('button'); draftSave.type = 'button'; draftSave.className = 'mag-modal__btn mag-modal__btn--ghost';
      draftSave.dataset.x = 'save-draft'; draftSave.textContent = 'Save draft';
      scrim.querySelector('.mag-modal__foot-btns').prepend(draftSave);
      note.textContent = 'Save a private draft, or review it before publishing.';
      scrim.querySelector('[data-x="cancel"]').textContent = 'Close';
    }
    scrim.querySelector('#mag-f-error').setAttribute('role', 'alert');
    connectEditorLabels(scrim);
  }
  requestAnimationFrame(() => { scrim.classList.add('open'); scrim.querySelector('.mag-modal').classList.add('open'); });

  // Section tabs: show one panel at a time (no more one giant scroll of everything).
  const conversationWorkspace = workspace ? mountConversationWorkspace(scrim, { shopify: !!shopifyStore, onStage: name => { if (!saving && !preparingLogo) showTab(name); } }) : null;
  const marketingValue = () => scrim.querySelector('#mag-f-marketing-excluded')?.checked ?? (magnet.marketing_excluded === true);
  const marketingPatch = () => marketingValue() === (magnet.marketing_excluded === true) ? {} : { marketing_excluded: marketingValue() };
  const launchWorkspace = workspace ? mountLaunchWorkspace(scrim, magnet, () => ({
    ...appearanceChanges(magnet, initialBasics, { name: scrim.querySelector('#mag-f-name').value.trim(), domains: scrim.querySelector('#mag-f-domains').value.split(',').map(value => value.trim()).filter(Boolean) }),
    ...appearancePatch(), ...marketingPatch(), ...finderPatch(), flows: changedFlowRoutes(), ...(pendingLogo !== undefined ? { logo_image: true } : {}),
  }), { onDeliveryContext: refreshFlowDeliveryContext }) : null;
  let activeTab = initialTab;
  const studio = workspace ? mountMagnetStudio(opts.host, { editor: true }) : null;
  const designWorkspace = workspace ? mountStudioDesign(scrim) : null;
  const syncAssistantOptions = () => {
    const floating = scrim.querySelector('input[name="mag-display"]:checked')?.value || 'chat';
    scrim.querySelector('#mag-launcher-options').hidden = floating === 'inline';
    scrim.querySelector('#mag-launcher-hint-field').hidden = scrim.querySelector('#mag-f-launcher-style').value !== 'invitation';
    scrim.querySelector('#mag-visitor-controls-field').hidden = floating !== 'chat' || scrim.querySelector('#mag-f-presentation').value !== 'assistant';
  };
  scrim.addEventListener('change', syncAssistantOptions);
  syncAssistantOptions();
  mountWelcomeExperience(scrim);

  const navItems = [...scrim.querySelectorAll('.mag-modal__navitem')];
  const panels = [...scrim.querySelectorAll('.mag-tab')];
  const showTab = (name) => {
    if (sessionPaused) return;
    activeTab = name;
    launchWorkspace?.show(name);
    if (workspace) {
      scrim.querySelector('[data-x="save"]').textContent = name === 'review'
        ? magnet.status === 'DRAFT' ? 'Publish magnet' : magnet.status === 'DISABLED' ? 'Save changes' : 'Publish changes'
        : magnet.status === 'DRAFT' ? 'Review & publish' : 'Review changes';
    }
    const panelName = conversationWorkspace?.show(name) || name;
    navItems.forEach((b) => { b.classList.toggle('is-active', b.dataset.tab === panelName); if (b.dataset.tab === panelName) b.setAttribute('aria-current', 'page'); else b.removeAttribute('aria-current'); });
    panels.forEach((p) => { p.hidden = p.dataset.tab !== panelName; });
    scrim.querySelector('.mag-modal__body').scrollTop = 0;
    opts.onSection?.(name);
    canvasEditor?.close();
    if (workspace) scheduleRefresh();
  };
  navItems.forEach((b) => b.addEventListener('click', () => { if (!saving && !preparingLogo) showTab(b.dataset.tab); }));

  // The studio canvas stays mounted beside every editing section.
  let initialAppearance = collectAppearance(scrim);
  const initialBasics = { name: scrim.querySelector('#mag-f-name').value.trim(), domains: scrim.querySelector('#mag-f-domains').value.split(',').map(s => s.trim()).filter(Boolean) };
  const appearancePatch = () => appearanceChanges(magnet, initialAppearance, collectAppearance(scrim));
  let styleGallery = null;
  const previewPkg = () => { const pkg = magnetToPackage({ ...magnet, marketing_excluded: marketingValue(), ...collectAppearance(scrim), appearance_patch: appearancePatch(), ...(editing ? currentFlowDefinition() : {}), ...(finderPreviewSteps ? {steps:finderPreviewSteps} : {}) }); if(finderPreviewSteps && finderPreviewConfig?.help?.enabled)pkg.mag_summary=true; return styleGallery ? styleGallery.overlay(pkg) : pkg; };
  const paneEl = scrim.querySelector('#mag-preview-pane');
  const editingSelection = () => finderPreviewSteps || ((pendingFinder || magnet.product_finder) && currentFlowRoute()==='chat') ? null : ['flow', 'appearance'].includes(activeTab) ? currentFlowSelection() : null;
  const canvasEditor = workspace ? mountCanvasEditor(scrim.querySelector('.mag-modal__preview'), {
    getSelection: editingSelection,
    getControl: () => scrim.querySelector('.flow-node--open .flow-f-prompts'),
    isLocked: () => saving || preparingLogo || sessionPaused,
  }) : null;
  const previewVisible = () => workspace || window.matchMedia('(min-width: 1200px)').matches;
  let previewApi = null, fullScreenPreview = null;
  const expandPreview = initialInspection => {
    if (sessionPaused) return;
    fullScreenPreview?.close();
    fullScreenPreview = openPreviewModal(previewPkg, { ...previewOptions, fullScreen: true, initialInspection });
  };
  let followingPreview = false;
  const previewOptions = { installationGuid: editing ? magnet.guid : undefined, preserveEditorPosition: workspace, studio: workspace,
    answerAuthoring: magnet?.answer_authoring?.available === true,
    getEditingStep: workspace ? editingSelection : undefined,
    getFinishState: workspace ? () => activeTab === 'finish' ? conversationWorkspace.finishState() : null : undefined,
    onTestConversation: workspace ? () => {
      followingPreview = true;
      try { showTab('flow'); } finally { followingPreview = false; }
    } : undefined,
    getFlowNavigation: workspace ? () => !finderPreviewSteps && !((pendingFinder || magnet.product_finder) && currentFlowRoute()==='chat') && activeTab === 'flow' ? currentFlowNavigation() : null : undefined,
    onFlowNavigate: workspace ? ({ route, uid }) => {
      if (saving || preparingLogo || sessionPaused) return;
      if (route === 'entry') { showTab('welcome'); return; }
      if (selectFlowQuestion(route, uid)) { if (activeTab !== 'flow') showTab('flow'); scheduleRefresh(); }
    } : undefined,
    onRuntimeNavigate: workspace ? ({ route, index, total, stage }) => {
      if (saving || preparingLogo || sessionPaused || finderPreviewSteps) return;
      followingPreview = true;
      try {
        if (['review', 'success'].includes(stage)) { conversationWorkspace.showFinish(stage); showTab('finish'); return; }
        if (['call', 'email'].includes(route)) { showTab('welcome'); return; }
        if (selectFlowQuestion(route, null, { index, total, silent: true }) && activeTab !== 'flow') showTab('flow');
      } finally { followingPreview = false; }
    } : undefined,
    onEditModeChange: active => { if (!active) canvasEditor?.close(); },
    onEditWording: () => {
      if (activeTab !== 'flow') showTab('flow');
      scrim.querySelector('.flow-node--open [data-flow-task-tab="wording"]')?.click();
      const field = scrim.querySelector('.flow-node--open .flow-f-prompts');
      field?.focus({ preventScroll: true }); field?.scrollIntoView?.({ block: 'nearest' });
    },
    onExpand: expandPreview, progressive: workspace, followRoute: workspace ? () => conversationWorkspace.stage() === 'welcome' ? 'entry' : currentFlowRoute() : undefined };
  const ensurePane = () => { if (!sessionPaused && !previewApi && previewVisible()) { try { previewApi = mountPreviewPane(paneEl, previewPkg, previewOptions); } catch (e) { /* ignore */ } } };
  let refreshTimer = null;
  const scheduleRefresh = () => {
    if (followingPreview) return;
    if (sessionPaused) return;
    if (!previewVisible()) return;
    ensurePane();
    if (!previewApi) return;
    canvasEditor?.sync();
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(() => { try { previewApi.refresh(); } catch (e) { /* getPkg can throw mid-edit */ } }, 300);
  };
  const mql = window.matchMedia('(min-width: 1200px)');
  const onWidthChange = () => { if (previewVisible()) { ensurePane(); scheduleRefresh(); } };
  if (!workspace) mql.addEventListener('change', onWidthChange);

  // Dirty-guard. The backdrop NO LONGER closes the modal — a stray click used to discard
  // everything. X / Cancel / Escape confirm only when there are actually unsaved edits.
  // Explicit-save model (not autosave): nothing in this modal writes to the server except
  // the Save button's own click handler below — the flow editor's own state sync
  // (readModel, add/remove/reorder) only ever mutates in-memory client state, same as every
  // other tab's form fields, right up until Save is clicked. `dirty` exists to make that
  // boundary visible and to stop someone from losing work by leaving without knowing —
  // not to trigger any background write.
  let dirty = false;
  let saved = false; // set true right before the Save handler's own doClose() call —
                      // see doClose() below for why this matters for freshDraft cleanup.
  let saveAttempted = false; // a lost response may still mean the draft was published
  const dirtyEl = scrim.querySelector('#mag-f-dirty');
  const recomputeDirty = () => Object.keys(marketingPatch()).length > 0 || Object.keys(appearancePatch()).length > 0
    || Object.keys(appearanceChanges(magnet, initialBasics, { name: scrim.querySelector('#mag-f-name').value.trim(), domains: scrim.querySelector('#mag-f-domains').value.split(',').map(s => s.trim()).filter(Boolean) })).length > 0
    || (editing && Object.keys(changedFlowRoutes()).length > 0) || pendingLogo !== undefined || !!pendingFinder || !!shopifyFinderPanel?.needsApply();
  const setDirty = (v) => {
    dirty = workspace ? recomputeDirty() : v;
    if (dirtyEl) dirtyEl.hidden = !dirty;
    const label = scrim.querySelector('[data-save-state]');
    if (label) label.textContent = preparingLogo ? 'Preparing logo…' : saving ? 'Saving…' : dirty ? 'Unsaved changes' : 'No unsaved changes';
  };
  const main = scrim.querySelector('.mag-modal__main');
  mountConsentSubscriptions(scrim, { load: async () => {
    if (!magnet?.owner_guid) throw new Error('Save this magnet before choosing a subscription.');
    const response = await guardedFetch(`${apiBase()}/m5t/v5/integrations/hubspot/subscriptions?owner_guid=${encodeURIComponent(magnet.owner_guid)}`);
    const body = await response.json();
    if (!response.ok || !body.ok) throw new Error(body.error || 'HubSpot subscriptions could not be checked.');
    if (body.owner_guid !== magnet.owner_guid) throw new Error('Workspace changed. Reopen this magnet before choosing a subscription.');
    return body.data;
  } });
  const logoScale = scrim.querySelector('#mag-f-logo-scale');
  const syncLogoScale = () => {
    const value = `${logoScale.value}%`;
    scrim.querySelector('#mag-logo-scale-value').textContent = value;
    logoScale.setAttribute('aria-valuetext', value);
  };
  logoScale.addEventListener('input', syncLogoScale);
  scrim.querySelector('#mag-logo-scale-reset').addEventListener('click', () => {
    logoScale.value = '100';
    logoScale.dispatchEvent(new Event('input', { bubbles: true }));
    logoScale.dispatchEvent(new Event('change', { bubbles: true }));
  });
  syncLogoScale();
  // Studio refreshes are debounced; the preview preserves supported visitor progress.
  main.addEventListener('input', event => {
    if (event.target.closest('[data-style-controls]')) return;
    setDirty(true);
    if (workspace) scheduleRefresh();
  });
  main.addEventListener('change', event => { if (event.target.closest('[data-style-controls]')) return; setDirty(true); syncPerDeviceVisibility(scrim); if (workspace) syncFlowPaths(collectAppearance(scrim)); scheduleRefresh(); });
  styleGallery = wireAppearancePresets(scrim, tv, () => { setDirty(recomputeDirty()); scheduleRefresh(); }, scheduleRefresh, AuthController.getEntity()?.guid && magnet?.owner_guid ? value => saveStylePreference(AuthController.getEntity().guid, magnet.owner_guid, value) : null);
  initialAppearance = collectAppearance(scrim); // Capture only after custom palette controls are hydrated.
  syncPerDeviceVisibility(scrim);   // and once on open, for an existing per-device magnet
  // Legacy modals also update color while the native picker stays open.
  scrim.querySelector('#mag-f-color')?.addEventListener('input', () => { setDirty(true); scheduleRefresh(); });

  // Leaving with unsaved edits: two distinct ways to "leave" this modal, both guarded.
  // 1. Actually leaving the page/tab (close, refresh, typed URL) — the browser's own
  //    native prompt; modern browsers show their own generic text regardless of what's
  //    set on returnValue, but the mechanism still requires an explicit non-empty value.
  const onBeforeUnload = (e) => { if (dirty || saving || preparingLogo || sessionPaused) { e.preventDefault(); e.returnValue = ''; } };
  window.addEventListener('beforeunload', onBeforeUnload);
  // 2. In-app navigation elsewhere in the SPA while this modal is still open (e.g. a
  //    sidebar link) — the modal is appended straight to <body>, outside #app, so a route
  //    change alone wouldn't close it or warn about it. ROUTE_NAV_BEFORE fires after Navigo
  //    has already committed the hash change, so a decline here means navigating back, not
  //    preventing it outright.
  let modalOpen = true;
  const routeGuardToken = PubSub.subscribe(AppBroadcast.ROUTE_NAV_BEFORE, () => {
    if (!modalOpen || workspace) return;
    if (dirty && !confirm('Discard your changes to this magnet?')) {
      window.router?.navigate('/magnets');
      return;
    }
    doClose();
  });

  const doClose = () => {
    if (!modalOpen) return;
    // A freshDraft that's closed without ever being saved shouldn't linger — "New
    // magnet" -> "Cancel" must mean nothing happened, not "an empty magnet now exists,
    // just not published." Only applies to a draft THIS modal session provisioned; an
    // existing draft opened via Edit to resume it is left alone, same as always.
    // Fire-and-forget: nothing in this modal blocks on it, and a DRAFT never serves
    // publicly regardless, so a failed cleanup here is a low-stakes orphaned row, not
    // a live-magnet risk.
    if (!workspace && freshDraft && !saved && !saveAttempted && magnet && magnet.status === 'DRAFT') {
      // guardedFetch for the interceptor, not for staleness — see provisionAndOpen's
      // rollback above for the full reasoning.
      guardedFetch(`${apiBase()}/m5t/v5/magnets/${encodeURIComponent(magnet.guid)}`, { method: 'DELETE' }).catch(() => {});
      // The server-side delete alone isn't enough — provisionAndOpen() eagerly inserted
      // this card into state.magnets and the rendered grid the instant it was
      // provisioned (before the user ever saw the modal), and nothing else was going to
      // remove it. Without this, the card just sits there through the whole Cancel flow
      // — indistinguishable from "Cancel didn't work" even when the delete succeeds.
      // Synchronous and optimistic, not waiting on the fetch above: the point is that
      // Cancel feels instant, not gated on a network round-trip.
      state.magnets = state.magnets.filter((m) => m.guid !== magnet.guid);
      const gridBody = document.getElementById('mag-body');
      paintGrid(gridBody);
    }
    shopifyFinderPanel?.destroy();
    studio?.destroy();
    designWorkspace?.destroy?.();
    canvasEditor?.destroy();
    launchWorkspace?.destroy();
    modalOpen = false;
    window.removeEventListener('beforeunload', onBeforeUnload);
    PubSub.unsubscribe(routeGuardToken);
    fullScreenPreview?.close();
    clearTimeout(refreshTimer); mql.removeEventListener('change', onWidthChange); if (previewApi && previewApi.destroy) previewApi.destroy(); scrim.classList.remove('open'); setTimeout(() => scrim.remove(), 180); document.removeEventListener('keydown', onKey);
  };
  const canLeave = () => {
    if (saving || preparingLogo) { toast('Please wait for the current operation to finish.', 'warning'); return false; }
    return !dirty || confirm('Discard your changes to this magnet?');
  };
  const close = () => { if (!canLeave()) return; doClose(); opts.onClose?.(); };
  function onKey(e) { if (!workspace && e.key === 'Escape') { e.preventDefault(); close(); } }
  document.addEventListener('keydown', onKey);
  scrim.querySelector('.mag-modal__close')?.addEventListener('click', close);
  scrim.querySelector('[data-x="back"]')?.addEventListener('click', close);
  scrim.querySelector('[data-x="cancel"]').addEventListener('click', close);
  // "Manage in Settings" (lead alerts) navigates away — close via the dirty-guard first.
  if (!workspace) scrim.querySelectorAll('[data-close-modal]').forEach(link => link.addEventListener('click', close));
  // Intentionally no backdrop-click-to-close: prevents accidental data loss (user feedback).
  const firstFocus = workspace ? scrim.querySelector('h1') : scrim.querySelector('#mag-f-name');
  if (workspace) firstFocus.tabIndex = -1;
  firstFocus.focus({ preventScroll: workspace });

  // Flow now lives in a tab (not a separate popup). Mount the visual editor when editing;
  // a brand-new magnet has no guid yet, so it shows a short "create first" note.
  const flowPanel = scrim.querySelector('#mag-flow-panel');
  const finderReadonly=document.createElement('div');finderReadonly.className='finder-readonly';finderReadonly.hidden=true;
  finderReadonly.innerHTML='<p>These questions follow your product guide settings.</p><details><summary>Inspect generated conversation</summary><div data-generated-inspection></div></details>';
  const syncFinderFlow=()=>{
    const bound=!!(pendingFinder||magnet.product_finder)&&currentFlowRoute()==='chat';
    const preparing=!!shopifyFinderPanel?.needsApply()&&currentFlowRoute()==='chat';
    flowPanel.classList.toggle('flow-shopify-bound',bound||preparing);finderReadonly.hidden=!bound||preparing;
    const catalog=flowPanel.querySelector('.mag-shopify-finder');if(catalog)catalog.hidden=currentFlowRoute()!=='chat';
    if(bound)renderFinderInspection(finderReadonly.querySelector('[data-generated-inspection]'),currentFlowDefinition().steps||[]);
  };
  if (editing) mountFlowTab(flowPanel, magnet, () => { setDirty(true); scheduleRefresh(); }, {
    progressive: workspace, retainIncomplete: workspace && magnet.status === 'DRAFT', onRouteChange: () => {syncFinderFlow();scheduleRefresh();},
    onSelectionChange: workspace ? () => {syncFinderFlow();scheduleRefresh();} : undefined,
    ...(workspace ? { getDeliveryContext: () => launchWorkspace?.getContext(),
      onRequestDelivery: options => launchWorkspace?.refresh(options),
      onOpenDelivery: () => showTab('delivery') } : {}),
  });
  else flowPanel.innerHTML = '<p class="mag-modal__hint" style="margin-top:0">Your new magnet starts with a simple starter conversation. Create it first, then reopen it here to build the flow.</p>';
  flowPanel.append(finderReadonly);syncFinderFlow();
  if(workspace && magnet.shopify_finder_authoring && (magnet.status === 'DRAFT' || (magnet.status === 'ENABLED' && magnet.shopify_finder_publication))) {
    const catalogHost=document.createElement('div');const routePicker=flowPanel.querySelector('.flow-routes');if(routePicker)routePicker.after(catalogHost);else flowPanel.prepend(catalogHost);
    shopifyFinderPanel=mountShopifyFinder(catalogHost,{
      binding:magnet.product_finder,published:magnet.status==='ENABLED',supportsV2:magnet.shopify_guided_finder_authoring===true,supportsV3:magnet.shopify_multi_question_finder_authoring===true,isCurrent:()=>modalOpen && !sessionPaused && (!opts.isCurrentScope || opts.isCurrentScope()),
      isBusy:()=>saving || preparingLogo,
      request:async(path,body)=>{
        const res=await guardedFetch(`${apiBase()}/m5t/v5/shopify${path}`,{method:body===undefined?'GET':'POST',headers:{'Content-Type':'application/json'},...(body===undefined?{}:{body:JSON.stringify(body)})});
        const data=await res.json();if(!res.ok)throw Object.assign(new Error(data.error),{code:data.error});return data;
      },
      onDirty:()=>{syncFinderFlow();setDirty(true);},
      onPreview:(steps,config)=>{finderPreviewSteps=steps;finderPreviewConfig=config;canvasEditor?.close();scheduleRefresh();},
      onApply:(recipe,steps,definition)=>{finderPreviewSteps=null;finderPreviewConfig=null;
        const priorAction=(pendingFinder||magnet.product_finder)?.config?.resultAction||'view_product';
        const nextAction=recipe.config?.resultAction||'view_product';
        const finishFields={mag_summary_heading:'mag-f-summary-heading',mag_summary_confirm_label:'mag-f-summary-confirm',mag_success_title:'mag-f-success-title',mag_success_text:'mag-f-success-text'};
        if(nextAction==='request_quote'){
          scrim.querySelector('#mag-f-summary').checked=true;
          if(priorAction!==nextAction)for(const [key,id] of Object.entries(finishFields))scrim.querySelector(`#${id}`).value=PRODUCT_FINDER_QUOTE_COPY[key]||'';
        }else if(priorAction==='request_quote'){
          for(const [key,id] of Object.entries(finishFields)){const input=scrim.querySelector(`#${id}`);if(input.value===PRODUCT_FINDER_QUOTE_COPY[key])input.value='';}
          scrim.querySelector('#mag-f-summary').checked=recipe.config?.help?.enabled===true;
        }else if(recipe.config?.help?.enabled)scrim.querySelector('#mag-f-summary').checked=true;
        pendingFinder=recipe;replaceChatSteps(steps);syncFinderFlow();setDirty(true);scheduleRefresh();},
    });
  }

  if (workspace) scrim.querySelector('#mag-f-logo').addEventListener('input', () => {
    const value = scrim.querySelector('#mag-f-logo').value;
    if (pendingLogo !== undefined && value !== (pendingLogo || '')) pendingLogo = undefined;
    setDirty(dirty);
  });
  wireLogoUpload(scrim, magnet, workspace ? {
    stage: image => { pendingLogo = image; setDirty(true); },
    busy: value => { preparingLogo = value; setDirty(dirty); },
  } : null); // no-op when !editing — see its own guard
  if (initialTab) showTab(initialTab);
  ensurePane(); // initial live preview render

  // CTA rows: the checkbox enables the row's controls; typing a value auto-checks it.
  scrim.querySelectorAll('.mag-cta').forEach((row) => {
    const cb = row.querySelector('.mag-cta__on');
    const val = row.querySelector('.mag-cta__val');
    const opts = row.querySelector('.mag-cta__opts');
    const extras = row.querySelectorAll('.mag-cta__booksrc, .mag-cta__calevent, .mag-cta__callsrc, .mag-cta__crtracker');
    cb.addEventListener('change', () => {
      if (val) val.disabled = !cb.checked;
      if (opts) opts.disabled = !cb.checked;
      extras.forEach((el) => { el.disabled = !cb.checked; });
      if (cb.checked) (val || opts)?.focus();
    });
    if (val) val.addEventListener('input', () => { if (val.value.trim() && !cb.checked) { cb.checked = true; if (opts) opts.disabled = false; } });
  });

  // Book source: swap the label/link input for the provider event picker.
  scrim.querySelectorAll('.mag-cta[data-kind="book"]').forEach((row) => {
    const src = row.querySelector('.mag-cta__booksrc');
    const val = row.querySelector('.mag-cta__val');
    const sel = row.querySelector('.mag-cta__calevent');
    const ccSel = row.querySelector('.mag-cta__ccevent');
    const mbSel = row.querySelector('.mag-cta__mbevent');
    if (!src || !sel || !ccSel || !mbSel) return;
    const apply = () => {
      const isCal = src.value === 'calendly';
      const isCc = src.value === 'calcom';
      const isMb = src.value === 'ms_bookings';
      val.hidden = isCal || isCc || isMb;
      sel.hidden = !isCal;
      ccSel.hidden = !isCc;
      mbSel.hidden = !isMb;
      const mbSvc = row.querySelector('.mag-cta__mbservice');
      if (mbSvc) mbSvc.hidden = !isMb;
      if (isCal && !sel.dataset.listed) { sel.dataset.listed = '1'; populateCalEventSelect(sel); }
      if (isCc && !ccSel.dataset.listed) { ccSel.dataset.listed = '1'; populateCcEventSelect(ccSel); }
      if (isMb && !mbSel.dataset.listed) { mbSel.dataset.listed = '1'; populateMbPageSelect(mbSel); }
    };
    src.addEventListener('change', () => {
      const cb = row.querySelector('.mag-cta__on');
      if (!cb.checked) { cb.checked = true; cb.dispatchEvent(new Event('change')); }
      apply();
    });
    apply();
  });

  // Call source: swap the phone input for the CallRail tracking-number picker.
  scrim.querySelectorAll('.mag-cta[data-type="call"]').forEach((row) => {
    const src = row.querySelector('.mag-cta__callsrc');
    const val = row.querySelector('.mag-cta__val');
    const sel = row.querySelector('.mag-cta__crtracker');
    if (!src || !sel) return;
    const apply = () => {
      const isCr = src.value === 'callrail';
      val.hidden = isCr;
      sel.hidden = !isCr;
      if (isCr && !sel.dataset.listed) { sel.dataset.listed = '1'; populateCrTrackerSelect(sel); }
    };
    src.addEventListener('change', () => {
      const cb = row.querySelector('.mag-cta__on');
      if (!cb.checked) { cb.checked = true; cb.dispatchEvent(new Event('change')); }
      apply();
    });
    apply();
  });

  // Inline width/height apply to Inline mode only — show the row only when it's selected.
  const inlineSizeWrap = scrim.querySelector('#mag-f-inline-size');
  // Preset cards: highlight follows the radio; Custom reveals the raw fields.
  scrim.querySelectorAll('input[name="mag-size-preset"]').forEach((r) =>
    r.addEventListener('change', () => {
      scrim.querySelectorAll('.mag-size-card').forEach((c) => c.classList.toggle('is-on', c.querySelector('input').checked));
      scrim.querySelector('#mag-f-inline-custom').hidden = r.value !== 'custom';
    }));
  scrim.querySelectorAll('input[name="mag-display"]').forEach((r) =>
    r.addEventListener('change', () => { inlineSizeWrap.hidden = scrim.querySelector('input[name="mag-display"]:checked')?.value !== 'inline'; }));

  // Preview reflects the live form state (unsaved) via the shared preview engine.
  scrim.querySelector('[data-x="preview"]')?.addEventListener('click', () => expandPreview());

  const save = async (ev) => {
    if (sessionPaused) return;
    const saveBtn = ev.currentTarget;
    if (saveBtn.disabled) return;
    const errEl = scrim.querySelector('#mag-f-error');
    if (saving || preparingLogo || shopifyFinderPanel?.busy()) return;
    if (shopifyFinderPanel?.needsApply()) { const prepared = await shopifyFinderPanel.prepare(); if(!prepared){errEl.textContent='Check your product guide settings before saving.';showTab('flow');return;} }
    if (styleGallery?.isPending()) {
      showTab('appearance');
      errEl.textContent = 'Apply the style you are trying, or choose Keep my design, before saving.';
      designWorkspace?.reveal(scrim.querySelector('[data-style-apply]'));
      scrim.querySelector('[data-style-apply]').focus(); return;
    }
    if (workspace && opts.isCurrentScope && !opts.isCurrentScope()) {
      errEl.textContent = 'Your workspace changed in another window. Your edits are still here. Return to this magnet’s workspace before saving.';
      return;
    }
    const welcomeProblem = welcomeExperienceProblem(scrim);
    if (welcomeProblem) { showTab('appearance'); designWorkspace?.show('experience', true); errEl.textContent = welcomeProblem; return; }
    const savingDraft = workspace && saveBtn.dataset.x === 'save-draft' && magnet.status === 'DRAFT';
    if (!savingDraft && ((pendingFinder && !(magnet.status === 'ENABLED' && magnet.shopify_finder_publication)) || (magnet.product_finder && !magnet.shopify_finder_publication))) { errEl.textContent = magnet.status === 'DRAFT' ? 'Choose Save draft. Shopify storefront publishing is not enabled in this pilot.' : 'Publishing Shopify guide changes is not enabled for this magnet.'; return; }
    if (savingDraft && magnet.draft_save_version !== 1) { errEl.textContent = 'This server needs the protected draft-save update. Your edits are still here.'; return; }
    const name = scrim.querySelector('#mag-f-name').value.trim();
    if (!name) { showTab('basics'); errEl.textContent = 'Please give your magnet a name.'; return; }
    const domains = scrim.querySelector('#mag-f-domains').value.split(',').map((s) => s.trim()).filter(Boolean);

    // Custom inline sizes: refuse values the widget would silently ignore.
    if ((scrim.querySelector('input[name="mag-size-preset"]:checked')?.value || 'standard') === 'custom') {
      const sizeErr = inlineSizeError(scrim.querySelector('#mag-f-inline-w').value, 'width')
        || inlineSizeError(scrim.querySelector('#mag-f-inline-h').value, 'height');
      const errOut = scrim.querySelector('#mag-f-inline-err');
      if (errOut) errOut.textContent = sizeErr;
      if (sizeErr) { showTab('appearance'); designWorkspace?.reveal(scrim.querySelector('#mag-f-inline-w')); errEl.textContent = 'Check the inline size fields.'; return; }
    }

    // Validate the opt-in CTA bar: only call/email need a target — a checked-but-empty one is
    // a user error, not silent loss. Book (blank ⇒ scheduler), Chat and Support need no value.
    let ctaErr = '';
    scrim.querySelectorAll('.mag-cta').forEach((row) => {
      const on = row.querySelector('.mag-cta__on').checked;
      if (row.dataset.kind === 'book') {
        // Provider mode needs a picked event; the built-in scheduler needs nothing.
        const booksrc = row.querySelector('.mag-cta__booksrc')?.value;
        if (on && booksrc === 'calendly' && !(row.querySelector('.mag-cta__calevent')?.value || '')) {
          ctaErr = ctaErr || 'Pick a Calendly event for the Book button, or switch it back to the built-in scheduler.';
        }
        if (on && booksrc === 'calcom' && !(row.querySelector('.mag-cta__ccevent')?.value || '')) {
          ctaErr = ctaErr || 'Pick a Cal.com event for the Book button, or switch it back to the built-in scheduler.';
        }
        if (on && booksrc === 'ms_bookings' && !(row.querySelector('.mag-cta__mbevent')?.value || '')) {
          ctaErr = ctaErr || 'Pick a Bookings page for the Book button, or switch it back to the built-in scheduler.';
        }
        return;
      }
      if (row.dataset.kind !== 'value') return;
      if (row.dataset.type === 'call' && row.querySelector('.mag-cta__callsrc')?.value === 'callrail') {
        if (on && !(row.querySelector('.mag-cta__crtracker')?.value || '')) {
          ctaErr = ctaErr || 'Pick a CallRail tracking number for the Call button, or switch it back to a phone number.';
        }
        return;
      }
      const value = row.querySelector('.mag-cta__val').value.trim();
      if (on && !value) ctaErr = ctaErr || `Add a target for the ${CTA_META[row.dataset.type].label} button, or uncheck it.`;
    });
    if (ctaErr && (!editing || Object.hasOwn(appearancePatch(), 'actions'))) { showTab('welcome'); errEl.textContent = ctaErr; return; }

    // A/B: an enabled test with nothing changed in B can't measure anything.
    const abWanted = collectAB(scrim);
    if (abWanted.enabled && !Object.keys(abWanted.b).length && (!editing || Object.hasOwn(appearancePatch(), 'ab'))) {
      showTab('ab');
      errEl.textContent = 'Give variant B at least one change (title, welcome, or color), or untick the A/B test.';
      return;
    }

    const noticeProblem = legalNoticeProblem(scrim) || consentProblem(scrim);
    if (noticeProblem) {
      showTab('appearance'); designWorkspace?.show('legal', true);
      errEl.textContent = noticeProblem;
      return;
    }

    // Flow lives in this modal now — validate it too, and jump to the Flow tab on a problem.
    if (editing && !savingDraft) {
      const flowProblem = validateFlows();
      if (flowProblem) {
        showTab('flow');
        // Also mirror it into the modal's own footer error. The footer is
        // `flex: 0 0 auto` in a flex-column modal, so it is pinned and visible no
        // matter how far the flow panel has scrolled — the flow tab's own message
        // is inside the scrolling body and was reachable only by scrolling, which
        // is how a working validation read as a silent save failure in production.
        errEl.textContent = flowProblem.message;
        showFlowRoute(flowProblem.route, flowProblem.message, { uid: flowProblem.uid, field: flowProblem.field });
        return;
      }
    }

    if (workspace && !savingDraft && activeTab !== 'review') { showTab('review'); return; }
    const payload = editing
      ? { ...appearanceChanges(magnet, initialBasics, { name, domains }), ...appearancePatch(), ...marketingPatch(), ...finderPatch(),
          flows: changedFlowRoutes(), save_version: 1, expected_revision: magnet.revision }
      : { name, domains, ...collectAppearance(scrim) };
    if (workspace && pendingLogo !== undefined) {
      payload.logo_image = pendingLogo;
      payload.expected_logo_revision = magnet.logo_revision;
      // Preview uses the staged data URL; the API stores only the public URL.
      payload.logo_url = pendingLogo === null ? '' : `${apiBase()}/m5t/v5/magnet/${encodeURIComponent(magnet.guid)}/logo?v=${Date.now()}`;
    }
    if (showOwnerPicker) payload.owner_guid = scrim.querySelector('#mag-f-owner').value;
    // First real Save on an auto-provisioned draft is the actual "publish" moment —
    // flips it from DRAFT (not live/embeddable) to ENABLED.
    const publishingDraft = editing && magnet.status === 'DRAFT' && !savingDraft;
    if (savingDraft) { payload.status = 'DRAFT'; payload.draft_version = 1; }
    if (publishingDraft) payload.status = 'ENABLED';
    saveBtn.disabled = true;
    const attempt = ++saveEpoch;
    saving = true; setDirty(dirty);
    errEl.textContent = '';
    let requestSent = false, responseStatus;
    try {
      if (workspace && pendingLogo !== undefined && !/^[a-f0-9]{32}$/.test(magnet.logo_revision || '')) throw new Error('This server does not yet support protected logo saves. Your edits are still here.');
      if (editing && !magnet.revision) throw new Error('Reload this magnet before saving. The server must support protected saves.');
      const url = editing ? `${apiBase()}/m5t/v5/magnets/${encodeURIComponent(magnet.guid)}` : `${apiBase()}/m5t/v5/magnets`;
      const headers = { 'Content-Type': 'application/json' };
      if (createIdemKey) headers['Idempotency-Key'] = createIdemKey;
      saveAttempted = true;
      main.inert = true;
      requestSent = true;
      const res = await guardedFetch(url, {
        method: editing ? 'PATCH' : 'POST',
        headers,
        body: JSON.stringify(payload),
      });
      if (attempt !== saveEpoch) return;
      responseStatus = res.status;
      // Success must be read from the HTTP status, not just the parsed body: a
      // dropped/truncated response after the server already committed the write
      // (the exact "UI says failed, magnet actually got created" case that made
      // retries produce duplicates) lands here with `data` still `{}`, which used
      // to read as `!data.ok` — an honest failure signal for a request that had
      // already succeeded. `res.ok` doesn't have that failure mode, and a retry is
      // now safe either way since createIdemKey is unchanged across retries.
      let data = {};
      try { data = await res.json(); } catch { /* body missing/unparseable — fall through to res.ok */ }
      if (attempt !== saveEpoch) return;
      if (!res.ok || !data.ok || (editing && !data.revision)) throw new Error(data.message || data.error || `Save could not be confirmed (${res.status}). Your changes are still here.`);
      // Appearance, flows and activation were committed together by this request.
      toast(savingDraft ? 'Draft saved — not published' : publishingDraft || !editing ? 'Magnet published' : 'Magnet updated', 'success');
      saved = true; saving = false;
      doClose();
      if (workspace && opts.onSaved) opts.onSaved({ draft: savingDraft, published: publishingDraft, name, guid: magnet.guid });
      else if (workspace) opts.onClose?.();
      else await load();
    } catch (err) {
      if (attempt !== saveEpoch) return;
      saving = false; setDirty(dirty);
      saveBtn.disabled = false;
      main.inert = false;
      errEl.textContent = err.message || 'Something went wrong.';
      if (workspace && requestSent && (!responseStatus || responseStatus === 409 || responseStatus >= 500 || responseStatus < 300)) {
        const explanation = document.createElement('span');
        explanation.textContent = ' Your edits are still in this tab. Check the saved version before retrying; the earlier save may have completed. ';
        const compare = document.createElement('a'); compare.href = `#${editorPath(magnet.guid, 'design')}`;
        compare.target = '_blank'; compare.rel = 'noopener'; compare.textContent = 'Open saved version in a new tab';
        errEl.append(explanation, compare);
      }
    }
  };
  scrim.querySelector('[data-x="save"]').addEventListener('click', save);
  scrim.querySelector('[data-x="save-draft"]')?.addEventListener('click', save);
  return { showSection: showTab, canLeave, isSaving: () => saving || preparingLogo, destroy: doClose,
    suspend() {
      if (!workspace) return false;
      studio?.suspend();
      canvasEditor?.close();
      sessionPaused = true; ++saveEpoch; saving = false;
      clearTimeout(refreshTimer);
      // Its runtime read may have been abandoned with the expired session.
      // Rebuild the preview on resume; authoring controls remain untouched.
      fullScreenPreview?.close(); fullScreenPreview = null;
      previewApi?.destroy(); previewApi = null; paneEl.replaceChildren();
      return true;
    },
    resume({ changed = false } = {}) {
      studio?.resume();
      sessionPaused = false; main.inert = false;
      scrim.querySelectorAll('[data-x="save"], [data-x="save-draft"]').forEach(button => { button.disabled = false; });
      setDirty(dirty);
      const error = scrim.querySelector('#mag-f-error');
      error.textContent = changed
        ? 'Your edits are back, but the saved magnet changed while you were signed out. Compare it with your work before saving.'
        : 'Your edits are back. Review them before saving. No save was retried automatically.';
      if (changed || saveAttempted) {
        const compare = document.createElement('a'); compare.href = `#${editorPath(magnet.guid, 'design')}`;
        compare.target = '_blank'; compare.rel = 'noopener'; compare.textContent = 'Open saved version in a new tab';
        error.append(document.createTextNode(' '), compare);
      }
      // A read abandoned at expiry never settles. Restart only unfinished
      // listings; completed dropdowns keep their exact unsaved selections.
      for (const [selector, populate] of [['.mag-cta__calevent', populateCalEventSelect], ['.mag-cta__ccevent', populateCcEventSelect], ['.mag-cta__mbevent', populateMbPageSelect], ['.mag-cta__crtracker', populateCrTrackerSelect]]) {
        const select = scrim.querySelector(selector);
        if (select?.dataset.loading) populate(select);
      }
      const service = scrim.querySelector('.mag-cta__mbservice');
      const page = scrim.querySelector('.mag-cta__mbevent');
      if (service?.dataset.loading && page && !page.dataset.loading) populateMbServiceSelect(page);
      firstFocus.focus({ preventScroll: true }); scheduleRefresh();
    },
  };
}
